import { useState, useEffect, useRef, useMemo, useCallback, memo, Component } from "react";
import { logError, getErrorLog, clearErrorLog } from "./errorLog";
import pkg from "../package.json";
const APP_VERSION = pkg.version;

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    logError(error.message, error.stack, "react-error-boundary" + (info?.componentStack ? ": " + info.componentStack : ""));
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "60px 40px", fontFamily: "Georgia, serif", maxWidth: "600px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: "20px", color: "#b05050", marginBottom: "12px" }}>Something broke</div>
          <div style={{ fontSize: "13px", color: "#888", marginBottom: "24px" }}>{this.state.error.message}</div>
          <button onClick={() => window.location.reload()} style={{
            fontSize: "13px", padding: "10px 24px", borderRadius: "3px", border: "none",
            background: "#111", color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif"
          }}>Reload the app</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── PROMPTS ────────────────────────────────────────────────────────────────

const PERSONA_PROMPTS = {
  academic: "Write with an academic voice: precise, evidence-oriented phrasing, but still fluid and readable, not stiffly formal. Reasoning flows naturally rather than declaring conclusions abruptly.",
  casual_blog: "Write like a casual blogger talking directly to a reader: first person where it fits, contractions throughout, an aside or opinion dropped in naturally now and then.",
  reddit: "Write like a genuine Reddit comment: direct, a little blunt, gets to the point without throat-clearing, occasional informal shorthand, reads like someone typing quickly, not polished copy.",
  esl: "Write as a fluent but non-native English speaker naturally would: slightly simpler sentence construction in places, an occasional unconventional (but still correct) phrasing choice, a rhythm that isn't textbook-perfect native cadence.",
};

const SCRIPT_PAUSE_RULE = `
BREATH-PAUSE STYLE: ON
Insert natural spoken pauses: use "..." where a speaker would trail off or pause for effect, and prefer a period over a comma at points where a speaker would take a visible breath, even mid-sentence.`;

// Soft Flesch-Kincaid grade-level targets per Audience option, so that
// control actually shapes output complexity instead of leaving it to
// whatever the model produces unguided. min/max drive the AnalysisPanel's
// "was this target actually hit" comparison; label is the prompt-facing text.
const AUDIENCE_READING_LEVELS = {
  "Children": { min: 3, max: 5, label: "grade 3-5" },
  "General Public": { min: 7, max: 9, label: "grade 7-9" },
  "Professional": { min: 10, max: 13, label: "grade 10-13" },
  "Expert": { min: 13, max: Infinity, label: "grade 13+" },
};

// Optional tag on a saved Voice Profile (see VoiceProfiles) - names the
// register the SAMPLE happened to be written in, not a claim about the
// person generally. Direct generalization of the contraction/formality fix
// above: a sample only shows one register, and traits that come FROM that
// register (not from the person specifically) shouldn't get forced onto a
// piece written in a different one. Used only in buildSystemPrompt's prose
// below - deliberately not fed into any deterministic detector, since
// "which traits are personal vs. register" is a judgment call the prompt
// has to make, not something with a clean mechanical signal the way
// contractions specifically had.
const VOICE_STYLE_OPTIONS = ["Descriptive", "Narrative", "Expository", "Persuasive", "Technical", "Creative", "Conversational"];
// The two Formality dropdown values that call for avoiding contractions.
// Matches the real option values in MainApp's Formality SelectField.
const FORMAL_REGISTER_VALUES = new Set(["Formal", "Professional"]);
const VOICE_STYLE_REGISTER_HINTS = {
  Descriptive: "vivid, detail-heavy",
  Narrative: "story-driven, personal",
  Expository: "plain, factual, and clear",
  Persuasive: "argumentative and direct",
  Technical: "precise, formal, and jargon-heavy",
  Creative: "experimental and unconventional",
  Conversational: "casual, loose, and informal",
};

// Standalone "Writing Style" control - independent of Voice Profiles
// entirely, usable with no saved sample at all. Reuses the same 7
// categories as the Voice Profile style tag (VOICE_STYLE_OPTIONS) since
// they're the same underlying concept, but this shapes the whole PIECE's
// rhetorical mode directly, not a sample's register. Deliberately richer
// instructions than VOICE_STYLE_REGISTER_HINTS above, which only needed a
// short adjective phrase to describe a sample - this needs to actually
// tell the model how to write from scratch in that mode.
const WRITING_STYLE_INSTRUCTIONS = {
  Descriptive: "Lean into vivid, sensory-rich detail - paint the scene rather than just stating facts. Modifiers and figurative language are welcome; let detail accumulate rather than rushing to the point.",
  Narrative: "Structure this as a story with real sequence and causation - a beginning, a development, an arc. Use concrete scenes and moments, not just a list of points.",
  Expository: "Prioritize clarity over voice. Short, declarative sentences, minimal figurative language, explicit logical transitions. The information is the point, not the writer's personality.",
  Persuasive: "Build a case, not just an explanation - give the reader a real stake, a credible reason to believe it, and a clear line of reasoning. Address the reader directly and end with a genuine call to action.",
  Technical: "Precision over readability. Assume the reader already has the relevant background. Consistency matters more than variety - use the same structure for parallel steps or specs rather than varying it for style.",
  Creative: "Voice-driven and willing to break convention - unconventional structure, sentence fragments used deliberately, personality over neutrality.",
  Conversational: "Direct, informal address to the reader - contractions, plain everyday words, occasional rhetorical questions answered immediately. Looseness (a comma splice, a trailing thought) is fine here, not an error.",
};

// Communication Style questionnaire (2026-09-19) - an OPTIONAL, isolated
// addition to Voice Profiles. Deliberately separate from the writing-sample
// fingerprint: a sample captures HOW someone writes (sentence length,
// vocabulary), this captures rhetorical HABITS a short sample may not
// reliably surface (hedge vs. assert, question frequency, how they close an
// idea). Trimmed from an original 15-question draft to these 7 - the
// highest-signal, least-redundant-with-existing-controls, lowest-
// fabrication-risk subset (dropped: personality-adjacent dimensions that
// overlapped Tone/Formality, which already exist as controls, and a
// standalone "self-reference frequency" question, whose fabrication risk
// couldn't be safely scoped the way Q2 below was).
// Built as a fully isolated, removable unit on purpose (explicit user
// requirement): this array, BEHAVIORAL_QUESTIONS, plus the single
// generateBehavioralPrompt() function below, plus the one injection point
// in buildSystemPrompt's styleRules, are the entire footprint. Deleting all
// three removes the feature completely with nothing else to untangle - the
// same pattern already used for Quick mode.
const BEHAVIORAL_QUESTIONS = [
  {
    id: "structure",
    section: "How you structure an idea",
    question: "When you make a point in writing, which feels most natural?",
    options: [
      { value: "A", label: "I state my conclusion first, then explain why" },
      { value: "B", label: "I build up the reasoning and arrive at the conclusion at the end" },
      { value: "C", label: "It depends on the situation - I do both" },
    ],
  },
  {
    id: "evidence",
    section: "How you support a point",
    question: "When you back up a point, what do you usually reach for?",
    options: [
      { value: "A", label: "Facts, data, or specific examples" },
      { value: "B", label: "Logic and reasoning - explaining why it makes sense" },
      { value: "C", label: "Personal experience or stories, when I have one that fits" },
    ],
  },
  {
    id: "address",
    section: "How you address the reader",
    question: "When you write, how do you think about the person reading it?",
    options: [
      { value: "A", label: "I focus on what I want to say and trust the reader to follow" },
      { value: "B", label: "I constantly consider whether the reader will understand and relate" },
      { value: "C", label: "I write as if I'm having a conversation directly with them" },
    ],
  },
  {
    id: "questions",
    section: "How often you ask questions",
    question: "How often do you ask the reader a question in your writing?",
    options: [
      { value: "A", label: "Rarely - I make statements, not questions" },
      { value: "B", label: "Occasionally - when I want the reader to think" },
      { value: "C", label: "Frequently - questions feel natural to me" },
    ],
  },
  {
    id: "assertion",
    section: "How you express conviction",
    question: "When you state an opinion or conclusion, how do you usually frame it?",
    options: [
      { value: "A", label: "Directly - \"this is how it works\"" },
      { value: "B", label: "With light qualification - \"I think this is probably right\"" },
      { value: "C", label: "With clear acknowledgment of uncertainty - \"in my experience...\"" },
    ],
  },
  {
    id: "humor",
    section: "Whether humor shows up",
    question: "Does humor appear in your writing?",
    options: [
      { value: "A", label: "Rarely or never - I keep it focused and straightforward" },
      { value: "B", label: "Occasionally - a dry observation when it fits naturally" },
      { value: "C", label: "Often - it's part of how I communicate" },
    ],
  },
  {
    id: "ending",
    section: "How you close an idea",
    question: "How do you tend to end a piece of writing or a main argument?",
    options: [
      { value: "A", label: "With a clear, tidy conclusion - I wrap it up" },
      { value: "B", label: "With a call to action or a next step" },
      { value: "C", label: "With something open - a question, a reflection, a thought left hanging" },
    ],
  },
];

// The instruction text injected per answer. Note Q2's "C" option: the
// original 15-question draft mapped this straight to "supports points with
// personal experience and stories, narrative and lived-in" - live-testing
// this project has done repeatedly shows an unconditional instruction like
// that risks the model inventing an anecdote that was never in the source
// text, directly against the HARD RULE: NEVER INVENT DETAILS elsewhere in
// this same prompt (a real, previously-shipped bug - see
// findSampleContentBleed's history). Explicitly gated here instead: the
// preference is real and worth capturing, but it only fires when the
// source already supports it.
const BEHAVIORAL_ANSWER_TEXT = {
  structure: {
    A: "States the conclusion first, then explains the reasoning - leads with the point, justifies after",
    B: "Builds to the conclusion - reasoning comes first, the point lands at the end",
    C: "Flexible - sometimes front-loaded, sometimes building, depending on what fits",
  },
  evidence: {
    A: "Backs up points with facts, data, or specific concrete examples",
    B: "Backs up points with logical reasoning - explains the why, not just the what",
    C: "When the source material already contains a personal detail, anecdote, or lived example, lean into presenting it vividly - but NEVER invent a story, detail, or experience that isn't already there. If there's nothing personal to draw on, fall back to reasoning instead.",
  },
  address: {
    A: "Writer-centric - focuses on making the point clearly, trusts the reader to follow along",
    B: "Reader-centric - actively considers whether the reader will understand and relate at each step",
    C: "Conversational - writes directly to the reader, as if in dialogue with them",
  },
  questions: {
    A: "Rarely asks questions - stays declarative and statement-based throughout",
    B: "Occasionally uses a rhetorical question - when it genuinely makes the reader pause and think",
    C: "Frequently asks questions - they should feel woven naturally throughout, not just at the open",
  },
  assertion: {
    A: "High conviction - states opinions and conclusions directly, avoid hedges like \"I think\", \"perhaps\", \"might\"",
    B: "Lightly hedged - \"I think\" and \"probably\" show up naturally, not as a crutch on every sentence",
    C: "Openly acknowledges uncertainty - phrases like \"in my experience\" or \"this might not hold for everyone\" appear genuinely, not performatively",
  },
  humor: {
    A: "No humor - keep the tone focused and straightforward throughout",
    B: "Occasional dry humor - a light, wry observation when it genuinely fits, not forced in",
    C: "Humor is part of the voice - wit and lightness can appear more than once, woven in naturally",
  },
  ending: {
    A: "Ends with a clear, tidy conclusion - wrap up with a real takeaway",
    B: "Ends with a call to action or a concrete next step for the reader",
    C: "Ends on something open - a question, a reflection, or a thought left deliberately unresolved, not a neat bow",
  },
};

// Pure function, no side effects, easy to delete on its own. Returns "" (a
// safe no-op) whenever there's nothing to inject - an existing profile with
// no behavioral answers, or one where the feature was removed entirely,
// behaves exactly as if this function never existed.
const generateBehavioralPrompt = (behavioral) => {
  if (!behavioral || typeof behavioral !== "object") return "";
  const lines = BEHAVIORAL_QUESTIONS
    .map(q => behavioral[q.id] && BEHAVIORAL_ANSWER_TEXT[q.id]?.[behavioral[q.id]])
    .filter(Boolean);
  if (!lines.length) return "";
  return `

COMMUNICATION STYLE (from this person's own answers, not inferred from the sample):
${lines.map(l => `- ${l}`).join("\n")}
Apply these throughout the piece, not just in one or two moments - but every HARD RULE elsewhere in this prompt still applies in full, especially NEVER INVENT DETAILS. These describe HOW to say what's already there, never license to add something that isn't.`;
};

const buildSystemPrompt = (intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, voiceStyle, isClientVoice, writingStyle, behavioral) => {
  const intensityRules = {
    light: `REWRITE INTENSITY: LIGHT
- Fix banned words and obvious AI phrases only
- Smooth out the worst tells without restructuring
- Keep headings and overall shape intact`,
    medium: `REWRITE INTENSITY: MEDIUM
- Replace all banned words and phrases
- Rewrite predictable sentence openers
- Break up some parallel structures
- Vary sentence length noticeably
- Remove or rename overly tidy section headings`,
    aggressive: `REWRITE INTENSITY: AGGRESSIVE — this is the most important mode.

STRUCTURAL DESTRUCTION (do this first):
- The biggest AI tell is not the words — it is the SHAPE. Neat three-part structures, tidy section headings, clean logical flow, symmetrical paragraphs. Destroy all of it.
- If the article has three tips, make it feel like four ideas where one trails off, or two big ones and a couple of smaller thoughts. Break the symmetry.
- Remove or completely rewrite section headings. "Reading: A Powerful Tool" is an AI heading. A human might write "What actually worked for me" or just remove the heading entirely.
- Collapse or merge sections that feel too neat and separate
- Add one short paragraph that feels like a slight digression
- End the piece on something open or reflective, not a clean summary bow

PARAGRAPH SHAPE:
- Mix drastically: one sentence paragraph. Then a long one. Then two medium ones. Then another short one.
- At least two paragraphs should be a single sentence on their own
- Never let three consecutive paragraphs be the same length
- Confirmed against a real detector scan: opening EVERY paragraph with a general "topic sentence" claim (a broad statement of what the paragraph is about, often followed by "because") is itself a detectable AI habit, even when the wording is casual. Real writing doesn't always announce its point before making it.
  STILL AI-SOUNDING (states the claim, then supports it): "Paint can be transformative because it completely changes the mood of a room. I went with a dark blue in my bedroom after reading that it promotes better sleep."
  ACTUALLY HUMAN (drops straight into the specific, no preamble claim): "I went with a dark blue in my bedroom after reading somewhere it promotes better sleep. Best call I've made - paint alone changed the whole mood of the room."
  For at least half your paragraphs, open with a concrete detail, a specific moment, a question, or an aside instead of a general claim - save any broader observation for partway through the paragraph, or cut it entirely and let the specifics carry it.

VOICE:
- Add one moment of genuine opinion: "honestly", "in my experience", "I think most people get this wrong"
- One rhetorical question that doesn't get answered immediately
- Flat, abstract claims read as AI. The same claim stated concretely, with real weight behind it, reads as human - without inventing any new fact, just phrasing what's already there with more conviction.
  WRONG (flat, generic): "Consistency is more effective than intensity when learning a new skill."
  RIGHT (same claim, concrete): "Twenty minutes a day beats one four-hour weekend binge, every single time."
- CONCRETE DOES NOT MEAN STRIPPED DOWN. Confirmed by a real third-party detector report on this pipeline's own output: text with too FEW common connective words (articles, pronouns, "and"/"so"/"but", contractions) relative to content words reads as AI, even when every individual word is a fine choice - tight, headline-style phrasing with the connective tissue trimmed out is its own tell, separate from word rarity. Even the worked "RIGHT" example just above is an example of this - punchy, but almost no connective tissue in it at all. Real casual writing stays loose even when it's punchy.
  TOO TIGHT (concrete, but almost no connective tissue): "Twenty minutes a day beats one four-hour weekend binge, every single time."
  LOOSER, STILL CONCRETE: "Honestly, twenty minutes a day is going to beat one of those four-hour weekend binges every time - that's just how it works."
  Keep the specific claim and numbers, but don't strip out the pronouns, filler words, and connecting words a real person would naturally leave in while saying it.
- SENTENCE LENGTH IS THE #1 TELL IN THIS MODE, more important than any word choice. Never let more than 2 consecutive sentences run under 10 words each. Real casual writing runs on - it doesn't chop itself into a row of short, grammatically perfect sentences, even when the individual facts are simple ones.
  STILL AI-SOUNDING (short, clean, stacked back to back): "Resources were basically nonexistent back then. No websites to google. Books cost a fortune. But I stuck with it."
  ACTUALLY HUMAN (same facts, run together, uneven): "Resources were basically nonexistent back then, no websites to google and books that cost a fortune, but I stuck with it anyway."
  Join related short thoughts with "and", "but", or a comma instead of a period. A sentence that runs a little long, or splices a comma where a period "should" go, reads as more human than three or four tidy short sentences in a row ever does.
  VARY HOW you avoid choppiness, not just that you avoid it. Do NOT join every sentence in a row with "and" or "but" as a mid-sentence twist - a whole paragraph where every single sentence follows the same "[action], but/and [result]" shape is its own uniform, detectable pattern, just as much a tell as short choppy sentences were. Mix your methods: some sentences run long through a subordinate clause ("because", "when", "since"), some through a simple comma-joined list, some are just naturally longer without any joining word at all, and some genuinely can stay short if they don't sit in a row with others like them.
- Step-by-step or instructional content is especially prone to a version of this that ISN'T caught by "3 in a row": one short imperative per instruction ("Dig a hole. Water it well. Pull weeds.") spread one-per-step across separate paragraphs still drags the piece's overall reading level down, since that's a whole-piece average, not just local runs of consecutive short sentences. When writing instructions, actively combine 2-3 related sequential actions into one flowing sentence ("Dig a hole deeper than the pot, then water it well before mulching around the base") instead of giving each its own short standalone sentence, even when they're not consecutive on the page.
- Let one thought feel slightly open-ended rather than wrapped up neatly

TRANSITIONS:
- Kill all clean transitional summaries like "These three things work together. X does this. Y does that."
- Between paragraphs, you can shift topic abruptly with no bridging sentence - that's fine and encouraged. Inside a paragraph, keep sentences connected and running-on as described above under VOICE; don't recreate the same short-sentence choppiness there that you're avoiding between paragraphs.`
  };

  const contextRules = [
    writingGoal ? `WRITING GOAL: ${writingGoal} — shape the tone, structure, and vocabulary specifically for this type of content.` : '',
    audienceLevel ? `AUDIENCE LEVEL: ${audienceLevel} — calibrate vocabulary and complexity accordingly${AUDIENCE_READING_LEVELS[audienceLevel] ? `. READING LEVEL CEILING: keep this at or below a ${AUDIENCE_READING_LEVELS[audienceLevel].label} reading level. This caps complexity even if Persona, Formality, or Writing Goal would otherwise push it higher — a professional or formal register does not require difficult-to-read prose. Use shorter sentences and more common words if that's what it takes to stay under the ceiling` : ''}.` : '',
    formality ? `FORMALITY: ${formality} — maintain this register throughout.` : '',
    emotionalTone ? `EMOTIONAL TONE: ${emotionalTone} — the writing should feel ${emotionalTone.toLowerCase()} throughout.` : '',
    writingStyle && WRITING_STYLE_INSTRUCTIONS[writingStyle] ? `WRITING STYLE: ${writingStyle} — ${WRITING_STYLE_INSTRUCTIONS[writingStyle]}` : '',
    CASUAL_VOCAB_WRITING_GOALS.includes(writingGoal)
      ? `VOCABULARY PREFERENCE for ${writingGoal}: prefer the casual alternative wherever it fits naturally - ${CASUAL_VOCAB_PREFERENCES.map(([formal, casual]) => `"${formal}" -> "${casual}"`).join(", ")}. Not an absolute ban, just the default choice for this content type.`
      : '',
  ].filter(Boolean).join('\n');

  const scriptRules = (scriptMode ? `
SCRIPT/VIDEO MODE: ON
- Write for the ear, not the eye
- Short lines. Natural pauses built in with commas and full stops.
- Break lines where a speaker would breathe
- Rhythm example:
  "Most people get this wrong.
  Not because it's hard.
  Because nobody told them the simple version."` : '') + (scriptMode && breathPause ? SCRIPT_PAUSE_RULE : '');

  const personaRule = persona && PERSONA_PROMPTS[persona] ? `\nPERSONA VOICE:\n${PERSONA_PROMPTS[persona]}\n` : '';

  // Reframed from "analyse this sample and extract its characteristics" to
  // literal few-shot framing - "write as if you ARE this voice", not "apply
  // a description of this voice". Grounded in real research (Self-Disguise
  // Attack, arXiv 2508.15848): using real human writing as a direct
  // in-context anchor measurably helps evade AI detectors, distinct from and
  // stronger than an abstract style description the model reasons about
  // rather than directly conditions its generation on.
  // Strengthened after a real live test showed a genuine gap: the output
  // came back generically casual ("honestly", varied sentence length) but
  // missed every SPECIFIC, recognizable habit from the actual sample (a
  // recurring trailing "though", direct rhetorical questions to the reader,
  // blunt one-line punchlines, comma-spliced run-on rhythm). Root cause: this
  // section only asserted "this overrides default tone" as one abstract
  // line, positioned AFTER the generic intensity VOICE section's own
  // detailed worked examples - per this project's own repeated finding,
  // specific worked examples beat a later abstract override claim. Fixed
  // two ways: (1) moved earlier in the prompt, ahead of the generic
  // intensity section, so it's encountered first: see where it's inserted
  // below. (2) reworded to name concrete categories of habit to find and
  // reuse, not just "match rhythm and tone" in the abstract.
  const styleRules = styleText ? `
PERSONAL STYLE MATCHING — READ THIS BEFORE ANYTHING ELSE BELOW, INCLUDING THE VOICE/INTENSITY SECTION:
Below is a real, unedited writing sample from the actual person this output should sound like. This takes priority over any generic voice examples elsewhere in this prompt - if a generic instruction below would push toward a different phrasing than this person would actually use, follow this section instead.

Do not just match general rhythm or tone. Find this specific person's actual recurring habits and reuse them:
- A word or phrase they lean on repeatedly (a trailing qualifier, a filler word, a specific way of hedging)
- Whether they ever address the reader directly or ask a question mid-piece
- Whether they use short, blunt, standalone sentences for emphasis, or favor long comma-joined run-ons, or both
- Any recurring punctuation habit (heavy commas, exclamation points, trailing off)
- How rough or polished the writing actually is - don't smooth it into something more "correct" than the sample itself

HARD RULE — STYLE ONLY, NEVER CONTENT: everything between the triple-quotes below is a reference for VOICE ONLY - word habits, rhythm, punctuation. It is NOT the topic of what you are writing, and none of its specific facts, stories, or details belong anywhere in your output, even if part of it feels vivid or worth reusing. Your output must be entirely about the ACTUAL ARTICLE given in the user message below, written in this sample's voice - never about anything the sample itself describes.
Concrete example of the exact mistake to avoid: if the sample happens to open with "I've been an engineer most of my life, and I often struggle with creativity..." describing the sample author's own career, do NOT open your output with anything about being an engineer, creativity, or any other specific detail from that sample - that has nothing to do with the actual article's topic and must never appear in the output.

REAL WRITING SAMPLE, IN THIS PERSON'S OWN VOICE:
"""
${styleText}
"""

If this sample and your output were placed side by side with no names attached, they should read as the same person's writing - not just "a casual human," but recognizably THIS person, including their specific habits listed above. That similarity must come entirely from VOICE, never from reusing the sample's own content.${isClientVoice ? `

STRICT IMPERSONATION - THIS IS A CLIENT'S VOICE, NOT YOUR OWN: the goal here is indistinguishable ghostwriting, not adapting a personal voice to context. Unlike a normal Voice Profile, do NOT soften or adjust this sample's register to fit this piece's own Formality/Writing Goal setting - match it exactly as written, including contraction usage, sentence complexity, and how blunt vs. hedged it reads.${voiceStyle && VOICE_STYLE_REGISTER_HINTS[voiceStyle] ? ` This sample is tagged "${voiceStyle}" (${VOICE_STYLE_REGISTER_HINTS[voiceStyle]} writing) - that register applies regardless of what this piece's other settings call for, because this specific client writes this way, full stop.` : ''} Every setting elsewhere in this prompt that would normally push toward a different register is secondary to sounding exactly like this person.` : voiceStyle && VOICE_STYLE_REGISTER_HINTS[voiceStyle] ? `

REGISTER vs. PERSONAL HABITS: this sample is tagged "${voiceStyle}" - ${VOICE_STYLE_REGISTER_HINTS[voiceStyle]} writing. Some of what makes it sound this way comes from THAT REGISTER, not from the person specifically: sentence length/complexity, how blunt vs. hedged it is, how much jargon shows up. Apply those only if this piece's own Formality/Writing Goal call for a similarly ${VOICE_STYLE_REGISTER_HINTS[voiceStyle]} register - don't force a ${voiceStyle.toLowerCase()} register onto a piece that's meant to read differently. The habits listed above (recurring phrases, punctuation quirks, direct reader address, paragraph rhythm) are genuinely personal, not an artifact of this one sample's register - those apply regardless.` : ''}${generateBehavioralPrompt(behavioral)}` : '';

  const wordCountRule = targetWords
    ? `WORD COUNT — HARD REQUIREMENT: ${targetWords} words. This is not a suggestion.
- Count your words as you write. If you finish the rewrite and are short, go back and expand.
- Ways to reach the target naturally: rephrase and unpack ideas already in the text more fully, split a dense paragraph into two, add a sentence of context before a key point using only information already implied by the original.
- Do NOT pad with filler or repeat ideas already made.
- Do NOT invent new facts, names, brands, technologies, or specifics not present in the original - see NEVER INVENT DETAILS above.
- Do NOT summarise, compress, or drop any section from the original to save space.
- Every idea in the original must appear in the output, fully developed.
- If your draft is under ${targetWords} words, keep writing until you hit it.`
    : `WORD COUNT — HARD REQUIREMENT: stay within 10% of the original word count, above or below. This is not a suggestion. Do not treat "every idea must appear fully developed" as license to pad the piece with new invented content - rephrasing and restructuring what's already there is the entire job here, not adding substantially new material.`;

  const lockRule = factLockEnabled
    ? `\nHARD RULE: LOCKED CONTENT\nThe text contains tokens like [LOCK_0], [LOCK_1], etc. These represent names, numbers, quotes, or links that must not be altered. Copy each token through to the output exactly as written, in the same position. Never edit, translate, or remove the token text itself.\n`
    : '';

  // REVERSED 2026-09-15: this used to instruct "favor less common word
  // choices... avoid the single most statistically likely next word" -
  // directly backwards, confirmed by a real third-party detector report on
  // actual output from this pipeline. That report flagged LOW function-word
  // ratio (0.33 vs a 0.402 human baseline) and LONG average word length
  // (5.86 vs 5.24 chars) as AI tells - independently reproduced with a
  // from-scratch calculation on the same text (0.405 vs 0.492 human
  // reference, 4.72 vs 4.31 chars). Rarer/longer words are not what makes
  // human text pass; MORE common short words (articles, pronouns, "and",
  // contractions) do, because that's the connective tissue casual writing
  // actually runs on. The old instruction was pushing generation in exactly
  // the wrong direction on this axis. See findLowFunctionWordSentences
  // below for the matching backstop.
  const bypassRule = bypassTarget === "boosted"
    ? `\nDETECTION RESISTANCE: BOOSTED\nMake sentence-length variance a top priority: mix very short (under 8 words) and long (over 25 words) sentences aggressively throughout, more than you would by default.\n`
    : '';

  // Sharpened with concrete worked examples per technique, replacing the
  // abstract "minor grammatical looseness" description - matches the same
  // "worked example beats abstract instruction" lesson that fixed the
  // tricolon backstop's fix pass earlier this project. The 5-technique cap
  // (3-5 total, not per-technique) is preserved from the original version -
  // "more looks manufactured" is worth keeping explicit, not left implicit.
  const imperfectionRule = imperfectionMode
    ? `\nCASUAL GRAMMAR: ALLOWED\nMake exactly 3-5 of the following changes somewhere in the piece - more than that starts to look manufactured, not natural:
- Sentence fragment: break one long sentence into a main clause and a fragment. "The chain cost me a fortune and I had to walk the bike home." -> "The chain cost me a fortune. And I had to walk the bike home."
- Run-on: join two short sentences with a comma instead of a full stop. "Check the pressure. Spin the wheels." -> "Check the pressure, spin the wheels."
- Trailing thought: end one paragraph on an incomplete or open thought rather than a conclusion - "Worth doing. Probably." or "At least that's the idea."
- Mid-sentence pivot: start a sentence one direction, then change course. "The saddle height is - look, just get it right and your knees will thank you."
- Casual aside: a short parenthetical that feels like a genuine aside. "(Don't ask me how I know this.)"
Spread these across the piece, not clustered together, and only where they fit naturally - don't force one into a sentence where it reads oddly.\n`
    : '';

  return `You are a ruthless human editor. Transform text so completely that it no longer reads like AI — in structure, voice, rhythm, and shape.

HARD RULE: ZERO EM DASH POLICY
Never use an em dash (—). Replace with commas, periods, or conjunctions. No exceptions.

HARD RULE: NEVER INVENT DETAILS
Never invent facts, names, brands, technologies, dates, statistics, or other specifics that are not in the original text. A plausible-sounding but made-up detail ("YouTube wasn't even a glint in anyone's eye", a specific product name that was never mentioned, an invented statistic) is not allowed even if it makes the writing feel more vivid - it is both inaccurate and, on its own, a detectable AI tell, since fabricated filler reads differently from anything genuinely adapted from the source. If you want to add color or voice, apply it to what is already there. Do not add new claims.

HARD RULE: REORDER, DON'T JUST REPHRASE
The tell that survives every other fix in this prompt is SEQUENCE - even after every sentence gets reworded, restructured, and re-punctuated, AI text almost always presents ideas in the exact same order the source did. Real human writing constantly reorders: leads with the payoff before the setup, moves a qualifying detail from the end of a sentence to the front, or reshuffles which point comes first in a paragraph.
BEFORE (original order): "The ability to speak multiple languages is more than just a party trick; it is a bridge to new cultures, perspectives, and career opportunities."
STILL THE SAME ORDER (just reworded - this does NOT count): "Speaking multiple languages isn't just a fun trick, it's a gateway to new cultures, views, and job opportunities."
ACTUALLY REORDERED: "Cultures you'd never access otherwise. Jobs you didn't know existed. That's what's waiting on the other side of learning a second language, way more than some party trick."
For at least half the sentences in each paragraph, change WHICH PIECE of information comes first, not just how it's worded - move a trailing clause to the front, lead with the concrete example before the general claim it supports, or flip which half of the sentence the reader hits first. A paragraph where every sentence still opens with the same idea it opened with in the original, even in fresh words, has not actually been rewritten.

HARD RULE: STOP OPTIMIZING FOR POLISH
Real human writing is uneven in quality - not uniformly fluent, sophisticated, or grammatically flawless. Two independent third-party humanizer tools, verified against real GPTZero and ZeroGPT scans on this exact kind of content, both scored 93-97% human specifically BECAUSE they stopped short of full polish, not despite it - one used mildly non-native, slightly-off phrasing throughout ("tastes incomparably better," "unmatched in their juicy and rich taste"), the other used short, plain, blunt sentences and outright fragments ("Can be ready in as little as 30 days." with no subject, "A trowel makes holes for plants or seeds."). If a sentence you've written sounds impressively smooth, elegant, or precisely worded, that is a warning sign, not a compliment - rewrite it plainer or rougher.
- Let some sentences be short, plain, and unremarkable instead of always reaching for a more interesting or sophisticated way to say the same thing.
- An occasional sentence fragment or incomplete thought is fine, even outside CASUAL GRAMMAR mode - not every sentence needs a clean subject and verb.
- Don't always pick the most precise or elegant word - an ordinary, slightly-imprecise word choice reads as more human than a perfectly-chosen one.
- This is not permission to be sloppy with facts, meaning, or structure - it is specifically about not over-polishing the prose itself.

HARD RULE: SENTENCE LENGTH TARGET
Confirmed against a real GPTZero report: AI-typical average sentence length is 29.2 words, versus a 23.2-word human baseline. Aim for an average close to 23 words per sentence across the whole piece, and never let the overall average exceed 25 - that ceiling matters more than any individual sentence's length. This is a target to aim for while writing, not a floor to force text up to - a naturally terse piece that lands well under 23 is fine and should not be padded out to hit the number; the ceiling of 25 is the only hard limit.
${lockRule}${bypassRule}${imperfectionRule}${personaRule}
${styleRules}
${intensityRules[intensity]}

${contextRules}
${scriptRules}

BANNED WORDS — never use:
${BANNED_WORDS.join(", ")}

BANNED PHRASES:
${FORMAL_PHRASES.map(p => `"${p}"`).join(" / ")}

HEDGING / VAGUE ATTRIBUTION — never use unsupported vague authorities:
${HEDGING_PHRASES.map(p => `"${p}"`).join(" / ")}

OVERUSED INTENSIFIERS — cut or replace with something more specific:
${INTENSIFIER_WORDS.join(", ")}

BANNED STRUCTURES:
- No clean three-part summaries
- No tidy parallel lists where every item is the same grammatical shape - and splitting one across separate sentences does NOT fix it if each sentence repeats the same opening/clause pattern. That's the identical tell in a different container.
  Source example: "not the humans, the corporate greed, or the lack of regulation"
  STILL BROKEN (just re-punctuated, same repeated shape 3x): "Not the humans who designed it. Not the corporations chasing profits. Not the regulators who've been asleep at the wheel."
  ACTUALLY FIXED (items merged unevenly, different weight and length each, no repeated opening): "Not the humans who built it, and definitely not the regulators who let corporations cash in while looking the other way."
  The fix has to change which items get grouped together and how much space each gets - not just where the periods land.
- No more than one paragraph opening with "This", "These", "The", or "It"
- No textbook-style section headings

${FORMAL_REGISTER_VALUES.has(formality) && !isClientVoice
  ? `CONTRACTIONS: avoid them - write out full forms ("do not" instead of "don't") throughout, to match the ${formality} register requested for this piece. Live-tested and confirmed necessary: a model asked to do this while a Voice Profile was active still added contractions anyway, reasoning the sample "felt conversational" even though the sample itself had none and this formality setting says otherwise - this instruction overrides that impulse. If a writing sample is active below, match every OTHER habit in it (word choice, sentence rhythm, recurring phrases, punctuation quirks) - just not its contraction usage, which this piece's own explicit formality setting governs instead.`
  : styleText
  ? `CONTRACTIONS: match the writing sample above, not a fixed rule - if that person writes without contractions ("do not" instead of "don't"), do the same here even though it reads more formally.${isClientVoice ? " This is a client's voice - match it exactly regardless of this piece's Formality setting, that's the whole point of a Client Voice profile." : " Different people genuinely write differently; a real style match respects that instead of forcing everyone toward the same casual default."}`
  : `CONTRACTIONS — always use: don't, it's, you'll, we're, that's, there's, hasn't, won't, can't, I've, they're`}

${wordCountRule}

SELF-CHECK before outputting:${styleText ? '\n- Does your output actually reuse a SPECIFIC habit from the writing sample above (a recurring word/phrase, a direct question to the reader, a blunt short sentence, a particular punctuation quirk) - not just "sound casual" in general? If you can\'t point to one, go back and add one.' : ''}
- Does this still have the same skeleton as the input? If yes, break it.
- Pick any 3 sentences at random. In each, does the SAME piece of information still come first as in the original (just reworded)? If most of them do, you rephrased without reordering - go back and flip the order of at least half your sentences per paragraph.
- Is there a list or clause of 3+ same-shaped items anywhere - including 3 sentences in a row that open the same way or repeat the same clause pattern? If yes, it's still symmetric even if you already split it into separate sentences. Merge items unevenly, vary the length per item, or reorder - don't just move the punctuation.
- Count your paragraphs. How many open with a general claim/topic sentence rather than a concrete detail? If it's more than half, rewrite some openers to drop straight into a specific moment, detail, or question instead.
- Do section headings still sound like a content article? If yes, rewrite or remove them.
- Does it end with a neat summary? If yes, cut or rewrite it.
- Count consecutive sentences under 10 words. Found 3 or more in a row anywhere? That's polished choppiness, a detectable AI pattern on its own - go back and merge at least two of them into one longer sentence with "and", "but", or a comma before outputting.
- Is there any name, brand, technology, date, or statistic in your output that isn't in the original? If yes, remove it - you invented it, and it's not allowed.
- Is your word count within 10% of the original (or of the target, if one was given)? If it's noticeably longer, you likely padded with invented content - cut it back.
- Could this have been written by the same AI that wrote the input? If yes, go harder.

OUTPUT FORMAT — output exactly two sections, nothing else, no placeholder text:
1. The line "HUMANIZED TEXT:" followed by your complete rewritten version of the input, written out in full. Never leave this section blank or replace it with a description of what should go there.
2. The line "CHANGE LOG:" followed by a bullet list of the key changes you made.

Write like a real person. Unpredictable. Slightly uneven. Human.`;
};

const ANALYSIS_PROMPT = `You are an AI writing pattern detector. Analyse the text and return ONLY a JSON object. No preamble, no explanation, no markdown fences.

IMPORTANT CALIBRATION NOTE: text organized into multiple clearly-labeled points or sections (a how-to guide, a listicle, a numbered set of tips) is a normal, legitimate writing convention used constantly by skilled human writers - it is NOT itself an AI tell. A real published, pre-AI book excerpt organized this exact way (five labeled points, each explained, each closing with a consistent "How I use it:" personal example) scored high on a naive version of this rubric - that was a rubric bug, not a correct finding. Only score these signals for genuine, unnatural RIGIDITY: the same low-level pattern repeating with mechanical uniformity and no organic variation - not for the mere presence of structure, labeled points, or a consistent organizational device used deliberately across sections.

Score each signal 0-100 (0 = not present, 100 = strongly present):
- uniformSentences: sentences are all similar length with little variation. Judge actual sentence-to-sentence rhythm within paragraphs, not whether the piece has a consistent overall format.
- predictableOpeners: the SAME opener word/phrase or grammatical shape recurs 3+ times across nearby sentences or paragraphs with no variation. A deliberate, consistent structural device (e.g. every section closing with the same short lead-in phrase before a personal example) is normal authorial craft, not a tell, as long as the surrounding sentences vary. Do not flag ordinary openers ("This", "The", "It is", "There are") just for existing once or twice - only flag genuine unvaried repetition.
- perfectSymmetry: overly balanced lists, three-part structures, or parallel constructions that recur with suspicious uniformity - e.g. multiple three-item lists with near-identical item lengths, or several sections that are all nearly the same word count with the same internal shape. A multi-point piece whose sections genuinely vary in length, depth, and internal structure (some short, some long, different numbers of supporting paragraphs) is NOT symmetric just because it covers several points in sequence - only flag real, measurable uniformity, not the presence of a list/guide format itself.
- formalPhrasing: literal instances of stock AI/corporate phrases like "It is important to", "In conclusion", "Furthermore", "This article explains" - or close variants. Do not flag ordinary clear, professional, or instructive prose that simply lacks casual contractions - that is a register choice, not an AI tell.
- overallAI: overall likelihood this was AI-generated (0=human, 100=AI)

Even genuinely well-written, human-sounding text almost never scores an exact 0 on every single signal at once - real analysis finds at least some texture (a 2, a 5, an 8) even in strong writing. If you're about to return all four signals as exactly 0, look again: that's a shortcut, not an analysis. Give the actual number your reading of the text finds, not a round default. Equally, do not inflate a score just because the piece has a clear structure or covers multiple points - structure alone is not evidence.

Also provide:
- topIssues: array of exactly 3 short strings naming the biggest AI tells found. If the text is genuinely strong, name the 3 most notable stylistic observations instead, even minor ones - never leave these blank.
- verdict: one sentence under 12 words summarising the result, specific to this text, not a generic stock phrase.

Return your analysis as JSON in this shape (the values below are placeholders showing the structure only, not real answers - substitute your actual findings):
{"uniformSentences":N,"predictableOpeners":N,"perfectSymmetry":N,"formalPhrasing":N,"overallAI":N,"topIssues":["specific observation 1","specific observation 2","specific observation 3"],"verdict":"specific one-sentence verdict for this text"}`;

// ─── SEO OPTIMIZER ───────────────────────────────────────────────────────────

const SEO_CHECKLIST_ITEMS = [
  "Target keyword in H1 title",
  "Target keyword in first 100 words",
  "Target keyword in at least one H2",
  "Target keyword appears 2-4 times total",
  "Title under 60 characters",
  "Meta description under 160 characters",
  "At least 2 H2 subheadings present",
  "Paragraphs under 4 sentences",
  "Reading level grade 7-9",
  "Internal link placeholders included",
  "External link placeholders included",
  "Article meets target word count",
];

// Field labels and checklist item text are pinned exactly (not left to the
// model to phrase however) so the response can be parsed reliably - a vague
// "labeled lines" instruction was tested elsewhere in this app and found to
// produce inconsistent formatting; being maximally explicit here from the
// start avoids that.
const buildSeoOptimizerPrompt = (primaryKeyword, secondaryKeywords, contentType, targetLength, audience, tone) => `You are an SEO content optimizer. Take the input text and transform it into a fully optimized SEO article without changing the core meaning, voice, or tone.

TARGET KEYWORD: ${primaryKeyword}
SECONDARY KEYWORDS: ${secondaryKeywords || "(none provided)"}
CONTENT TYPE: ${contentType}
TARGET LENGTH: ${targetLength} words - hard requirement, the article body must land close to this word count
AUDIENCE: ${audience}
TONE: ${tone}

SEO STRUCTURE RULES:

TITLE (H1):
- Must contain the target keyword
- Under 60 characters
- Compelling enough to get clicks
- Accurate to the content - never clickbait

META DESCRIPTION:
- Under 160 characters
- Contains the target keyword naturally
- Summarizes the article and encourages clicks
- Written as a complete, readable sentence

HEADINGS:
- Use H2 for main sections
- Use H3 for subsections where needed
- At least one H2 must contain the target keyword
- Headings must be descriptive and scannable

KEYWORD PLACEMENT:
- Target keyword in the first 100 words
- Target keyword in at least one H2
- Target keyword 2-4 times total in the body - natural, never forced
- Secondary keywords woven in naturally where they fit
- Never stuff keywords - if it reads unnaturally, remove it

CONTENT RULES:
- First paragraph hooks the reader AND establishes the topic clearly
- Each section answers one clear question or covers one clear point
- Paragraphs maximum 3-4 sentences
- Use bullet points or numbered lists for steps, comparisons, or grouped information
- Include at least one specific example per main section
- End with a clear next step or call to action

INTERNAL LINK PLACEHOLDERS:
- Where relevant, add [INTERNAL LINK: suggested anchor text]
- Maximum 3 internal link suggestions

EXTERNAL LINK PLACEHOLDERS:
- Where a stat or claim would benefit from a source, add [EXTERNAL LINK: suggested source type]
- Maximum 2 external link suggestions

READABILITY:
- Grade 7-9 reading level
- Short sentences mixed with longer ones
- No paragraph longer than 4 sentences
- A subheading at least every 300 words

URL SLUG:
- Generate a clean, hyphenated URL slug from the title
- Lowercase only
- No stop words (the, a, an, of, in, etc.) unless essential
- Maximum 6 words

OUTPUT FORMAT - output exactly four sections, labeled exactly as shown below. Inside SEO METADATA, use exactly these field labels. Inside SEO CHECKLIST, use exactly these item names, in this order, each followed by PASS or FAIL - do not rename, reorder, or skip any of them:

SEO METADATA:
Title: [generated title]
Meta Description: [generated meta description]
Target Keyword: ${primaryKeyword}
Secondary Keywords: ${secondaryKeywords || "None"}
URL Slug: [generated slug]

OPTIMIZED ARTICLE:
[Full article. Start each heading line with "H1:", "H2:", or "H3:" followed by the heading text. Body paragraphs are plain lines with no prefix.]

SEO CHECKLIST:
${SEO_CHECKLIST_ITEMS.map(item => `- ${item}: PASS or FAIL`).join("\n")}

CHANGE LOG:
[Bullet list of key structural and keyword changes made to the original text]`;

const generateSeoOptimized = async (sourceText, primaryKeyword, secondaryKeywords, contentType, targetLength, audience, tone) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "seo_optimize",
      messages: [
        { role: "system", content: buildSeoOptimizerPrompt(primaryKeyword, secondaryKeywords, contentType, targetLength, audience, tone) },
        { role: "user", content: sourceText },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content || "";
};

const parseSeoOptimizerResponse = (raw, primaryKeyword) => {
  const sectionRe = /SEO METADATA:|OPTIMIZED ARTICLE:|SEO CHECKLIST:|CHANGE LOG:/g;
  const markers = [];
  let m;
  while ((m = sectionRe.exec(raw)) !== null) markers.push({ label: m[0].replace(":", ""), index: m.index, end: m.index + m[0].length });
  if (markers.length === 0) return null;
  const sections = {};
  for (let i = 0; i < markers.length; i++) {
    const start = markers[i].end;
    const stop = i + 1 < markers.length ? markers[i + 1].index : raw.length;
    sections[markers[i].label] = raw.slice(start, stop).trim();
  }

  const metaText = sections["SEO METADATA"] || "";
  const grabLine = (label) => {
    const found = metaText.match(new RegExp(label + "\\s*:\\s*(.+)", "i"));
    return found ? found[1].trim() : "";
  };
  const metadata = {
    title: grabLine("Title"),
    metaDescription: grabLine("Meta Description"),
    keyword: grabLine("Target Keyword") || primaryKeyword,
    secondaryKeywords: grabLine("Secondary Keywords"),
    urlSlug: grabLine("URL Slug"),
  };

  const articleText = sections["OPTIMIZED ARTICLE"] || "";
  const article = articleText.split("\n").map(l => l.trim()).filter(Boolean).map(line => {
    const hMatch = line.match(/^H([123]):\s*(.+)/i);
    return hMatch ? { type: "h" + hMatch[1], text: hMatch[2].trim() } : { type: "p", text: line };
  });

  const checklistText = sections["SEO CHECKLIST"] || "";
  const checklist = SEO_CHECKLIST_ITEMS.map(item => {
    const line = checklistText.match(new RegExp(item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "[^\\n]*", "i"));
    const pass = !!line && /PASS/i.test(line[0]) && !/FAIL/i.test(line[0]);
    return { item, pass };
  });

  return { metadata, article, checklist, changeLog: sections["CHANGE LOG"] || "" };
};

// ─── HARD DETECTORS ──────────────────────────────────────────────────────────

const countEmDashes = (text) => (text.match(/\u2014/g) || []).length;
const stripEmDashes = (text) => text.replace(/\s*\u2014\s*/g, ", ").replace(/,\s*,/g, ",");

// Invisible/exotic Unicode characters that survive copy-paste from various sources.
// These are real, detectable artifacts, not a claim about any specific AI provider.
const INVISIBLE_CHARS = {
  "\u200b": "Zero-Width Space", "\u200c": "Zero-Width Non-Joiner",
  "\u200d": "Zero-Width Joiner", "\u2060": "Word Joiner",
  "\u00ad": "Soft Hyphen", "\ufeff": "BOM / Zero-Width No-Break Space",
  "\u200e": "Left-to-Right Mark", "\u200f": "Right-to-Left Mark",
  "\u202a": "L-R Embedding", "\u202b": "R-L Embedding",
  "\u202c": "Pop Directional Format", "\u202d": "L-R Override", "\u202e": "R-L Override",
};
// Visible space characters with unusual codepoints (e.g. non-breaking space) - these
// still function as word separators, so they get normalized to a regular space, not deleted.
const EXOTIC_SPACES = ["\u00a0","\u1680","\u2000","\u2001","\u2002","\u2003",
  "\u2004","\u2005","\u2006","\u2007","\u2008","\u2009","\u200a","\u202f","\u205f","\u3000"];
const INVISIBLE_CHAR_CLASS = "[" + Object.keys(INVISIBLE_CHARS).join("") + "]";
const EXOTIC_SPACE_CLASS = "[" + EXOTIC_SPACES.join("") + "]";
const UNICODE_TAG_SOURCE = "[\\u{E0001}\\u{E0020}-\\u{E007F}]";

const detectInvisibleChars = (text) => {
  const detections = [];
  const re = new RegExp("(?:" + INVISIBLE_CHAR_CLASS + "|" + EXOTIC_SPACE_CLASS + ")", "g");
  let m;
  while ((m = re.exec(text)) !== null) {
    detections.push({
      name: INVISIBLE_CHARS[m[0]] || "Exotic space character",
      hex: "U+" + m[0].codePointAt(0).toString(16).toUpperCase().padStart(4, "0"),
    });
  }
  const tagRe = new RegExp(UNICODE_TAG_SOURCE, "gu");
  while ((m = tagRe.exec(text)) !== null) {
    // Presence of Unicode Tag-block characters is unusual and worth flagging,
    // but detecting the character doesn't identify who inserted it or why.
    detections.push({ name: "Hidden Unicode tag character", hex: "U+" + m[0].codePointAt(0).toString(16).toUpperCase().padStart(4, "0") });
  }
  return detections;
};

const stripInvisibleChars = (text) => text
  .replace(new RegExp(INVISIBLE_CHAR_CLASS, "g"), "")
  .replace(new RegExp(EXOTIC_SPACE_CLASS, "g"), " ")
  .replace(new RegExp(UNICODE_TAG_SOURCE, "gu"), "");

// Hard, deterministic safety net for the OUTPUT (the model already has
// instructions not to produce any of this, but a rare slip shouldn't survive
// to the user) - same "prompt instructions alone aren't reliable enough,
// back it with a real deterministic pass" principle as every backstop in
// this file, applied to formatting artifacts rather than phrasing patterns.
// Deliberately a single pure-string pass, not an LLM call - these are rare
// slips, not something that needs judgment to fix.
const CHATBOT_OPENER_RE = /^\s*(?:Sure|Absolutely|Of course|Certainly|Great|Happy to help|Glad to|No problem)[!,]?\s+[Hh]ere'?s\b[^.!?\n]*[.!?:]?\s*/;
const stripOutputArtifacts = (text) => {
  let cleaned = text;
  // Markdown that leaked into what should be plain prose - strip the
  // markup, keep the text it was wrapping.
  cleaned = cleaned.replace(/\*\*([^*\n]+)\*\*/g, "$1");
  cleaned = cleaned.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1");
  cleaned = cleaned.replace(/`([^`\n]+)`/g, "$1");
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, "");
  // Bullet/numbered lists that survived into the output - reuse the same
  // list-to-prose conversion already applied to input.
  cleaned = unrollLists(cleaned).text;
  // Chatbot-assistant opener ("Sure! Here's...") - strip the opener, keep
  // whatever content follows it.
  cleaned = cleaned.replace(CHATBOT_OPENER_RE, "");
  // Visible Unicode that reads as machine-typed rather than human-typed -
  // distinct from stripInvisibleChars above, which only handles characters
  // that render as nothing at all.
  cleaned = cleaned.replace(/[‘’]/g, "'");
  cleaned = cleaned.replace(/[“”]/g, '"');
  cleaned = cleaned.replace(/ /g, " ");
  cleaned = cleaned.replace(/[•◦▪]\s*/g, "");
  cleaned = cleaned.replace(/[→←↓↑]/g, "");
  cleaned = cleaned.replace(/[✓✗☐]/g, "");
  cleaned = cleaned.replace(/–/g, "-");
  return cleaned;
};

// lowBurstiness now tracks coefficient of variation (stdDev/avg), the SAME
// signal calcBurstinessScore below actually measures and displays - the
// previous trigger (raw stdDev < 4.5 AND avg > 15) used a different, absolute
// formula that silently disagreed with the displayed score. Confirmed live:
// a real revoice's displayed burstiness AI-tell score got steadily worse
// across the pipeline (26 -> 34 -> 39, i.e. cv dropping from 0.56 -> 0.50 ->
// 0.46 as average sentence length rose from merging short/choppy runs) while
// this trigger never once fired to correct it, because absolute stdDev was
// actually RISING (4.2 -> 5.8 -> 6.4) even as relative variance fell -
// exactly the case the old absolute-threshold formula couldn't see.
// Threshold (cv < 0.48) calibrated against the confirmed-human "My Freelance
// Journey" reference text (cv 0.54, must NOT fire) and the real regression
// case above (cv 0.46 at the point the score was already visibly bad, MUST
// fire) - sits between the two with a real margin on both sides.
const calcBurstiness = (text) => {
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(Boolean);
  const lengths = sentences.map(s => (s.match(/[a-z']+/gi) || []).length).filter(n => n > 0);
  if (lengths.length <= 3) return null;
  const avg = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const variance = lengths.reduce((a, b) => a + (b - avg) ** 2, 0) / lengths.length;
  const stdDev = Math.sqrt(variance);
  const cv = avg > 0 ? stdDev / avg : 0;
  return { stdDev: Math.round(stdDev * 10) / 10, avgLen: Math.round(avg * 10) / 10, lowBurstiness: cv < 0.48 };
};

const countSyllables = (word) => {
  word = word.toLowerCase().replace(/[^a-z]/g, "");
  if (word.length === 0) return 0;
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Flesch-Kincaid Grade Level: estimates the US school grade needed to read the text.
const calcReadability = (text) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(Boolean);
  if (words.length === 0 || sentences.length === 0) return null;
  const syllables = words.reduce((sum, w) => sum + countSyllables(w), 0);
  const grade = 0.39 * (words.length / sentences.length) + 11.8 * (syllables / words.length) - 15.59;
  return { grade: Math.round(grade * 10) / 10, words: words.length, sentences: sentences.length };
};

// Top ~200 English words by frequency rank, used as a Zipfian unigram model
// for the perplexity proxy below. Rank position drives the probability
// estimate (rank 1 = "the", the most common word), not the words' order here.
const COMMON_WORD_RANKS = [
  "the","be","to","of","and","a","in","that","have","i","it","for","not","on","with","he",
  "as","you","do","at","this","but","his","by","from","they","we","say","her","she","or",
  "an","will","my","one","all","would","there","their","what","so","up","out","if","about",
  "who","get","which","go","me","when","make","can","like","time","no","just","him","know",
  "take","people","into","year","your","good","some","could","them","see","other","than",
  "then","now","look","only","come","its","over","think","also","back","after","use","two",
  "how","our","work","first","well","way","even","new","want","because","any","these","give",
  "day","most","us","is","are","was","were","been","has","had","did","said","each","tell",
  "very","still","should","need","here","own","must","great","same","big","group","begin",
  "seem","country","help","talk","where","turn","problem","every","start","hand","might",
  "american","show","part","against","place","such","again","few","case","week",
  "company","system","program","question","during","government","number","night",
  "point","home","water","room","mother","area","money","story","fact","month","lot","right",
  "study","book","eye","job","word","business","issue","side","kind","head","house","service",
  "friend","father","power","hour","game","line","end","member","law","car","city","community",
  "name","president","team","minute","idea","body","information","parent","face",
  "others","level","office","door","health","person","art","war","history","party","result",
  "change","morning","reason","research","girl","guy","moment","air","teacher","force",
  "education","foot","boy","age","policy","process","music","market","sense","nation","plan",
  "college","interest","death","experience","effect","class","control","care","field",
  "development","role","effort","rate","heart","drug","leader","light","voice","wife",
  "whole","police","mind","finally","pull","return","free","military","price","report",
  "decision","rock","sound","difference","practice","form","future","society","court","industry",
];
const WORD_RANK_INDEX = new Map(COMMON_WORD_RANKS.map((w, i) => [w, i + 1]));

// Real perplexity formula (PP = 2^entropy) evaluated under a static unigram
// (word-frequency) model - NOT true LM perplexity, which needs an actual
// trained language model's next-token probabilities. This is a legitimate
// lexical-rarity signal (rarer word choice -> higher score) but has no notion
// of context or grammar. Returns 0-100, higher = rarer/less-predictable words.
const calcPerplexityScore = (text) => {
  const words = (text.toLowerCase().match(/[a-z']+/g) || []);
  if (words.length === 0) return 0;
  const N = COMMON_WORD_RANKS.length;
  let harmonicSum = 0;
  for (let r = 1; r <= N; r++) harmonicSum += 1 / r;
  const zipfProb = (rank) => 1 / (rank * harmonicSum);
  const outOfListProb = zipfProb(N) * 0.5; // Laplace-style smoothing for unlisted words
  let crossEntropyBits = 0;
  for (const w of words) {
    const rank = WORD_RANK_INDEX.get(w);
    crossEntropyBits += -Math.log2(rank ? zipfProb(rank) : outOfListProb);
  }
  const perplexity = Math.pow(2, crossEntropyBits / words.length);
  const MIN_PP = 20, MAX_PP = 2000;
  const clamped = Math.min(Math.max(perplexity, MIN_PP), MAX_PP);
  const score = ((Math.log(clamped) - Math.log(MIN_PP)) / (Math.log(MAX_PP) - Math.log(MIN_PP))) * 100;
  return Math.round(score);
};

// New signal, added 2026-09-15 - grounded in a real third-party detector
// report on this pipeline's own output (not reverse-engineered from theory
// like perplexity above): the report flagged LOW function-word ratio (0.33
// vs. a stated 0.402 human baseline) and LONG average word length as AI
// tells. Independently reproduced with this exact word list on the same
// text (0.405 vs. 0.492 on a confirmed pre-AI human reference) - the
// direction and rough magnitude both held up under a from-scratch
// recalculation, not just the third party's own number. A genuine,
// linguistically standard closed-class list (articles, pronouns,
// prepositions, conjunctions, auxiliary/linking verbs) - not a curated
// "AI buzzword" list like BANNED_WORDS, a real grammatical category.
const FUNCTION_WORDS = new Set(["a","an","the","and","but","or","nor","so","yet","for","of","in","on","at","by",
  "with","about","against","between","into","through","during","before","after","above","below","to","from",
  "up","down","over","under","again","further","out","off","i","you","he","she","it","we","they","me","him",
  "her","us","them","this","that","these","those","my","your","his","its","our","their","mine","yours","hers",
  "ours","theirs","who","whom","whose","which","what","am","is","are","was","were","be","been","being","have",
  "has","had","do","does","did","will","would","shall","should","may","might","must","can","could","not","no",
  "there","here","if","when","where","while","because","although","since","unless","whereas","than","then",
  "as","how","why","both","each","few","more","most","other","some","such","own","same","too","very","just",
  "only","also","even","still","yet"]);
// Returns 0-100, higher = stronger AI tell (inverted polarity to match
// perplexity/burstinessScore above - low function-word ratio = high score).
const calcFunctionWordScore = (text) => {
  const words = (text.toLowerCase().match(/[a-z']+/g) || []);
  if (words.length < 20) return 0;
  const fnCount = words.filter(w => FUNCTION_WORDS.has(w)).length;
  const ratio = fnCount / words.length;
  // Calibrated against the same human reference used above: ~0.49 is a
  // clean human baseline, ~0.40 is where real AI-flagged output landed.
  // Clamped 0.25-0.52 then scaled so 0.49+ -> 0, 0.25 or below -> 100.
  const clamped = Math.min(Math.max(ratio, 0.25), 0.52);
  return Math.round(((0.52 - clamped) / (0.52 - 0.25)) * 100);
};

// Real, directly-computed statistic (coefficient of variation of sentence
// lengths) - no proxy caveat needed, unlike perplexity above. Returns 0-100,
// higher = more varied sentence rhythm.
const calcBurstinessScore = (text) => {
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(Boolean);
  if (sentences.length < 3) return 0;
  const lengths = sentences.map(s => (s.match(/[a-z']+/gi) || []).length).filter(n => n > 0);
  if (lengths.length < 3) return 0;
  const mean = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  if (mean === 0) return 0;
  const variance = lengths.reduce((a, b) => a + (b - mean) ** 2, 0) / lengths.length;
  const coefficientOfVariation = Math.sqrt(variance) / mean;
  return Math.round(Math.min(coefficientOfVariation / 0.75, 1) * 100);
};

// ─── PRE-PROCESSING (list unrolling, fact locking) ───────────────────────────

// Detects consecutive bullet/numbered list lines and joins them into a flowing
// sentence, since list formatting is a strong, easily-detected AI tell.
const unrollLists = (text) => {
  const lines = text.split("\n");
  const result = [];
  let buffer = [];
  let listFound = false;

  const flush = () => {
    if (buffer.length === 0) return;
    if (buffer.length === 1) {
      result.push(buffer[0] + ".");
    } else {
      const last = buffer.pop();
      result.push(buffer.join(", ") + ", and " + last + ".");
    }
    buffer = [];
  };

  for (const line of lines) {
    const m = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+)/);
    if (m) {
      buffer.push(m[1].trim().replace(/[.:;]$/, ""));
      listFound = true;
    } else {
      flush();
      result.push(line);
    }
  }
  flush();
  return { text: result.join("\n"), listFound };
};

// Locks URLs, emails, quoted strings, and standalone numbers/percentages behind
// [LOCK_n] tokens so the LLM can't alter them, then restores the originals after.
const LOCK_PATTERNS = [
  /https?:\/\/[^\s)]+[^\s).,;:!?]/g,
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  /"[^"]{1,200}"/g,
  /\b\d[\d,.]*%?\b/g,
];

const lockProtectedContent = (text) => {
  const tokens = [];
  let locked = text;
  for (const re of LOCK_PATTERNS) {
    locked = locked.replace(new RegExp(re.source, re.flags), (match) => {
      const token = `[LOCK_${tokens.length}]`;
      tokens.push({ token, value: match });
      return token;
    });
  }
  return { locked, tokens };
};

const unlockContent = (text, tokens) => {
  let result = text;
  for (const { token, value } of tokens) {
    result = result.split(token).join(value);
  }
  return result;
};

// If a [LOCK_n] token itself goes missing from the output (the model drops
// it entirely rather than altering it - the failure mode Fact Lock can't
// otherwise catch, since unlockContent above just silently does nothing for
// a token that was never there), the locked value disappears with zero
// visibility. Checked as pure token-presence, not value-comparison - the
// token is opaque to the model by design, so "does the token still appear"
// is the only thing worth checking.
const findMissingLockedFacts = (text, tokens) => tokens.filter(({ token }) => !text.includes(token));

const restoreMissingLockedFacts = async (text, missing, hasLocks) => {
  const list = missing.map(({ token, value }) => `${token} (the real value, for your reference only - do not output "${value}" literally, output the token "${token}" itself): "${value}"`).join("\n");
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "restore_locked_facts",
      temperature: 0,
      messages: [
        {
          role: "system",
          content: `You are doing a targeted fix on an already-finished piece of writing. During editing, some protected content tokens were dropped entirely and need to be woven back in. For each one below, find the most natural, contextually appropriate place in the text to reinsert it - near where it originally would have fit given the surrounding sentence - and insert the TOKEN ITSELF (not the real value) at that point, adding only the minimal surrounding words needed for it to read naturally as part of a sentence:\n${list}\nDo not change anything else in the text. Do not remove any other token. Output ONLY the complete corrected text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// ─── REDLINE DIFF (word-level LCS) ────────────────────────────────────────────

// Splits text into alternating word/whitespace tokens so exact spacing survives the diff.
const tokenizeForDiff = (text) => text.match(/\S+|\s+/g) || [];

const computeWordDiff = (oldText, newText) => {
  const a = tokenizeForDiff(oldText);
  const b = tokenizeForDiff(newText);
  const n = a.length, m = b.length;
  // Standard LCS dynamic-programming table.
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const result = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { result.push({ type: "same", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { result.push({ type: "removed", text: a[i] }); i++; }
    else { result.push({ type: "added", text: b[j] }); j++; }
  }
  while (i < n) { result.push({ type: "removed", text: a[i] }); i++; }
  while (j < m) { result.push({ type: "added", text: b[j] }); j++; }
  return result;
};

// ─── FILE METADATA SCAN (Vector 3) ───────────────────────────────────────────
// Minimal dependency-free ZIP reader, used to read specific entries out of a
// .docx (which is a ZIP container) without pulling in a library like JSZip.

const AI_METADATA_KEYWORDS = ["claude", "chatgpt", "gpt-4", "gpt-3", "gemini",
  "openai", "anthropic", "copilot", "ai assistant", "llm"];

// Reads a single named entry from a ZIP file's bytes, decompressing if needed.
// Returns a Uint8Array of the entry's raw (decompressed) bytes, or null if not found.
const readZipEntry = async (bytes, entryName) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // Find End Of Central Directory record by scanning backwards for its signature.
  let eocdOffset = -1;
  for (let i = bytes.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocdOffset = i; break; }
  }
  if (eocdOffset === -1) return null;

  const entryCount = view.getUint16(eocdOffset + 10, true);
  let cdOffset = view.getUint32(eocdOffset + 16, true);

  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(cdOffset, true) !== 0x02014b50) break; // not a central directory entry
    const compressionMethod = view.getUint16(cdOffset + 10, true);
    const compressedSize = view.getUint32(cdOffset + 20, true);
    const nameLen = view.getUint16(cdOffset + 28, true);
    const extraLen = view.getUint16(cdOffset + 30, true);
    const commentLen = view.getUint16(cdOffset + 32, true);
    const localHeaderOffset = view.getUint32(cdOffset + 42, true);
    const name = new TextDecoder().decode(bytes.slice(cdOffset + 46, cdOffset + 46 + nameLen));

    if (name === entryName) {
      // Read the local file header to find where actual entry data starts.
      const lhNameLen = view.getUint16(localHeaderOffset + 26, true);
      const lhExtraLen = view.getUint16(localHeaderOffset + 28, true);
      const dataStart = localHeaderOffset + 30 + lhNameLen + lhExtraLen;
      const compressedData = bytes.slice(dataStart, dataStart + compressedSize);

      if (compressionMethod === 0) return compressedData; // stored, no compression
      if (compressionMethod === 8) { // deflate
        const ds = new DecompressionStream("deflate-raw");
        const stream = new Blob([compressedData]).stream().pipeThrough(ds);
        return new Uint8Array(await new Response(stream).arrayBuffer());
      }
      return null; // unsupported compression method
    }
    cdOffset += 46 + nameLen + extraLen + commentLen;
  }
  return null;
};

const scanDocxMetadata = async (file) => {
  const detections = [];
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    for (const propFile of ["docProps/core.xml", "docProps/app.xml"]) {
      const entry = await readZipEntry(bytes, propFile);
      if (!entry) continue;
      const content = new TextDecoder("utf-8").decode(entry).toLowerCase();
      for (const kw of AI_METADATA_KEYWORDS) {
        if (content.includes(kw)) detections.push({ flag: `DOCX ${propFile} contains "${kw}"` });
      }
    }
  } catch { /* malformed or non-DOCX zip - report no detections rather than erroring */ }
  return detections;
};

const scanPdfMetadata = async (file) => {
  const detections = [];
  try {
    const head = await file.slice(0, 65536).arrayBuffer();
    // latin-1 preserves a 1:1 byte-to-codepoint mapping, matching how PDF metadata is stored
    const text = new TextDecoder("latin1").decode(head).toLowerCase();
    for (const field of ["creator", "producer"]) {
      const m = text.match(new RegExp("/" + field + "\\s*\\(([^)]*)\\)"));
      if (!m) continue;
      for (const kw of AI_METADATA_KEYWORDS) {
        if (m[1].includes(kw)) detections.push({ flag: `PDF ${field[0].toUpperCase() + field.slice(1)} contains "${kw}"` });
      }
    }
  } catch { /* unreadable file - report no detections rather than erroring */ }
  return detections;
};

const scanFileMetadata = async (file) => {
  const ext = file.name.toLowerCase().split(".").pop();
  if (ext === "docx") return scanDocxMetadata(file);
  if (ext === "pdf") return scanPdfMetadata(file);
  return [];
};

// ─── METADATA STRIPPING ────────────────────────────────────────────────────
// Rewrites a file to remove detected AI-tool metadata. Runs entirely client-side,
// matching the "never uploaded" scanning above - no server round-trip.

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[n] = c >>> 0;
  }
  return table;
})();
const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
};
const deflateRaw = async (bytes) => {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};
const zU16 = (n) => [n & 0xff, (n >>> 8) & 0xff];
const zU32 = (n) => [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];

// Lists every entry in a zip with its still-compressed bytes, so entries we
// don't touch can be re-embedded byte-for-byte without decompressing them.
const parseZipEntries = (bytes) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocdOffset = -1;
  for (let i = bytes.length - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocdOffset = i; break; }
  }
  if (eocdOffset === -1) return [];
  const entryCount = view.getUint16(eocdOffset + 10, true);
  let cdOffset = view.getUint32(eocdOffset + 16, true);
  const entries = [];
  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(cdOffset, true) !== 0x02014b50) break;
    const method = view.getUint16(cdOffset + 10, true);
    const crc = view.getUint32(cdOffset + 16, true);
    const compressedSize = view.getUint32(cdOffset + 20, true);
    const uncompressedSize = view.getUint32(cdOffset + 24, true);
    const nameLen = view.getUint16(cdOffset + 28, true);
    const extraLen = view.getUint16(cdOffset + 30, true);
    const commentLen = view.getUint16(cdOffset + 32, true);
    const localHeaderOffset = view.getUint32(cdOffset + 42, true);
    const name = new TextDecoder().decode(bytes.slice(cdOffset + 46, cdOffset + 46 + nameLen));
    const lhNameLen = view.getUint16(localHeaderOffset + 26, true);
    const lhExtraLen = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + lhNameLen + lhExtraLen;
    const compressedData = bytes.slice(dataStart, dataStart + compressedSize);
    entries.push({ name, method, crc, uncompressedSize, compressedData });
    cdOffset += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
};

// Rebuilds a zip from a list of { name, method, crc, uncompressedSize, compressedData }
// entries - already-compressed bytes are reused as-is, keeping unmodified files identical.
const buildZip = (entries) => {
  const enc = new TextEncoder();
  const DOS_TIME = 0x0000, DOS_DATE = 0x5021; // cosmetic only, not read by any consumer here
  const localParts = [], centralParts = [];
  let offset = 0;
  for (const e of entries) {
    const nameBytes = enc.encode(e.name);
    const localHeader = new Uint8Array([
      0x50, 0x4b, 0x03, 0x04, ...zU16(20), ...zU16(0), ...zU16(e.method),
      ...zU16(DOS_TIME), ...zU16(DOS_DATE), ...zU32(e.crc),
      ...zU32(e.compressedData.length), ...zU32(e.uncompressedSize),
      ...zU16(nameBytes.length), ...zU16(0),
    ]);
    localParts.push(localHeader, nameBytes, e.compressedData);
    const centralHeader = new Uint8Array([
      0x50, 0x4b, 0x01, 0x02, ...zU16(20), ...zU16(20), ...zU16(0), ...zU16(e.method),
      ...zU16(DOS_TIME), ...zU16(DOS_DATE), ...zU32(e.crc),
      ...zU32(e.compressedData.length), ...zU32(e.uncompressedSize),
      ...zU16(nameBytes.length), ...zU16(0), ...zU16(0),
      ...zU16(0), ...zU16(0), ...zU32(0), ...zU32(offset),
    ]);
    centralParts.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + e.compressedData.length;
  }
  const centralStart = offset;
  const centralSize = centralParts.reduce((sum, p) => sum + p.length, 0);
  const eocd = new Uint8Array([
    0x50, 0x4b, 0x05, 0x06, ...zU16(0), ...zU16(0),
    ...zU16(entries.length), ...zU16(entries.length),
    ...zU32(centralSize), ...zU32(centralStart), ...zU16(0),
  ]);
  const out = new Uint8Array(centralStart + centralSize + eocd.length);
  let p = 0;
  for (const part of [...localParts, ...centralParts, eocd]) { out.set(part, p); p += part.length; }
  return out;
};

// Blanks the flagged property's text content, leaving the rest of the XML intact.
const blankXmlTagContaining = (xml, tag, keywords) => {
  const re = new RegExp(`(<${tag}[^>]*>)([^<]*)(</${tag}>)`, "i");
  const m = xml.match(re);
  if (!m) return { xml, changed: false };
  const lower = m[2].toLowerCase();
  if (!keywords.some(kw => lower.includes(kw))) return { xml, changed: false };
  return { xml: xml.slice(0, m.index) + m[1] + m[3] + xml.slice(m.index + m[0].length), changed: true, value: m[2] };
};

const stripDocxMetadata = async (file) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const entries = parseZipEntries(bytes);
  const changes = [];
  const rebuilt = [];
  for (const entry of entries) {
    if (entry.name !== "docProps/core.xml" && entry.name !== "docProps/app.xml") {
      rebuilt.push(entry);
      continue;
    }
    const compressed = entry.method === 0 ? entry.compressedData
      : new Uint8Array(await new Response(new Blob([entry.compressedData]).stream().pipeThrough(new DecompressionStream("deflate-raw"))).arrayBuffer());
    let xml = new TextDecoder("utf-8").decode(compressed);
    const tags = entry.name === "docProps/core.xml"
      ? ["dc:creator", "cp:lastModifiedBy", "dc:description"]
      : ["Application", "Company"];
    for (const tag of tags) {
      const result = blankXmlTagContaining(xml, tag, AI_METADATA_KEYWORDS);
      if (result.changed) {
        xml = result.xml;
        changes.push(`${entry.name} <${tag}>: "${result.value}"`);
      }
    }
    const newBytes = new TextEncoder().encode(xml);
    const newCompressed = await deflateRaw(newBytes);
    rebuilt.push({ name: entry.name, method: 8, crc: crc32(newBytes), uncompressedSize: newBytes.length, compressedData: newCompressed });
  }
  return { bytes: buildZip(rebuilt), changes };
};

const stripPdfMetadata = async (file) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const out = new Uint8Array(bytes);
  // latin-1 preserves a 1:1 byte-to-codepoint mapping, so string indices below are byte offsets
  const text = new TextDecoder("latin1").decode(out);
  const changes = [];
  for (const field of ["Creator", "Producer"]) {
    const re = new RegExp("/" + field + "\\s*\\(([^)]*)\\)", "g");
    let m;
    while ((m = re.exec(text)) !== null) {
      const value = m[1];
      if (!AI_METADATA_KEYWORDS.some(kw => value.toLowerCase().includes(kw))) continue;
      const valueStart = m.index + m[0].indexOf("(") + 1;
      for (let i = 0; i < value.length; i++) out[valueStart + i] = 0x20; // space, preserves byte offsets/xref
      changes.push(`${field}: "${value}"`);
    }
  }
  return { bytes: out, changes };
};

// Returns null when the format isn't supported or nothing flagged was found to remove.
const cleanFileMetadata = async (file) => {
  const ext = file.name.toLowerCase().split(".").pop();
  if (ext === "docx") return stripDocxMetadata(file);
  if (ext === "pdf") return stripPdfMetadata(file);
  return null;
};

const BANNED_WORDS = ["moreover","furthermore","however","therefore","additionally","specifically",
  "consequently","similarly","nonetheless","indeed","thus","alternatively","notably","despite",
  "essentially","elevate","ultimately","enhance","emphasise","ensure","delve",
  "revolutionize","foster","subsequently","vibrant","bustling","vital","underscores",
  "landscape","tapestry","crucible","metamorphosis","indelible","dynamic","frictionless","turnkey",
  "ecosystem","bandwidth","touchpoint","granular","alignment","ideation","siloed",
  "disruptive","gossamer","enigma","nestled","symphony","remnant","reverberate",
  "importantly","promptly","arguably","leverage","synergy","streamline","optimize","empower","innovative","robust",
  "seamless","holistic","agile","scalable","stakeholder","framework","deliverables",
  "cutting-edge","best-in-class","data-driven","mission-critical","game-changer",
  "thought leadership","actionable insights","core competency","deep dive",
  "low-hanging fruit","move the needle","circle back","pain point","bleeding edge"];

// Distinct from BANNED_WORDS above: these are ordinary, common words (not
// rare corporate jargon) that lean formal-register rather than casual - too
// common to safely single-occurrence-ban the way BANNED_WORDS does without
// real false-positive risk in normal writing, so this is a prompt-level
// preference nudge for casual-register Writing Goals only, not a backstop-
// enforced ban. Skipped entirely for Academic/Business goals (and when no
// goal is set) - a formal register calling for "individuals" or "obtain" is
// not itself an AI tell the way it is in a casual blog post or social post.
const CASUAL_VOCAB_PREFERENCES = [
  ["provides", "gives"], ["utilise", "use"], ["utilize", "use"], ["obtain", "get"],
  ["demonstrate", "show"], ["approximately", "around"], ["facilitate", "help with"],
  ["numerous", "a lot of"], ["individuals", "people"], ["regarding", "about"],
  ["in terms of", "when it comes to"],
];
const CASUAL_VOCAB_WRITING_GOALS = ["SEO Blog Post", "Social Media Post", "Creative Story", "Email"];

const FORMAL_PHRASES = ["as a result","in order to","that being said","it's important to note",
  "it is important to note","it's worth noting","in conclusion","to summarize",
  "in summary","to put it simply","a testament to","on the other hand",
  "as previously mentioned","in today's world","it is important to understand",
  "it goes without saying","here's why","here's the thing","here's how",
  // Copula-avoidance phrases (Wikipedia:Signs of AI writing) - "serves as",
  // "functions as" etc. used in place of plain "is/are". Kept as phrases
  // rather than bare verbs ("boasts", "features", "marks" alone have too
  // many unrelated legitimate meanings to safely flag) - the "X as Y"
  // construction is specifically the copula-substitute pattern, much lower
  // false-positive risk. Same 2-distinct-phrase-hit threshold as the rest
  // of this list handles the rest of the calibration.
  "serves as","functions as","acts as","stands as",
  "it is worth","this article","this piece","this post",
  // Added from a third-party AI-detector spec's stock-phrase list - kept in
  // this same FORMAL_PHRASES array (same 2+-hits-before-firing threshold as
  // everything else here) rather than BANNED_WORDS, since several of these
  // are single common words ("remarkably", "crucially", "fundamentally")
  // that need the recurs-not-one-off protection to avoid false-positiving on
  // ordinary, unrelated legitimate use.
  "let's dive in","let's explore","with that in mind","having said that",
  "as we've discussed","moving forward","going forward","at the end of the day",
  "when all is said and done","the bottom line is","long story short","in a nutshell",
  "to put this in perspective","the fact of the matter is",
  "it's fascinating to note","interestingly enough","remarkably","crucially",
  "fundamentally","foundational","undeniably","without a doubt","there's no denying",
  "first and foremost","last but not least","all things considered","on balance",
  "by and large","for all intents and purposes","more often than not"];

const HEDGING_PHRASES = ["some experts believe","studies suggest","research shows",
  "many argue","critics say","it is often claimed","some might argue",
  "it could be said that","it is generally accepted that","many would agree that",
  // Vague-attribution/weasel-wording phrases (Wikipedia:Signs of AI writing) -
  // citing an unnamed collective source instead of a specific one.
  "industry reports","observers note","observers say","sources indicate","sources suggest"];

const INTENSIFIER_WORDS = ["very","really","quite","extremely","incredibly","highly",
  "significantly","substantially","truly","utterly","definitely"];

// Grounded in real, peer-reviewed AI-detection research (GLTR, Binoculars,
// DetectGPT) rather than guesswork: every one of those methods measures the
// same underlying thing - how statistically PREDICTABLE the word choice is.
// GLTR flags text where tokens are consistently the single most likely next
// word; Binoculars/DetectGPT measure the same idea via perplexity. This app
// already computes a real proxy for that (calcPerplexityScore, Zipf-based
// lexical rarity) but - per this project's own history - it was measured and
// displayed, never fed into a fix pass. This is that fix, at the word level:
// repeated overuse of the same generic, low-information word (not a single
// use, which is normal - the REPETITION is the tell). Density-based, not a
// raw count - live-tested that a raw count of 3+ false-positived on a real,
// confirmed-human 1100-word reference text (5x "get", 4x "got", 4x "make" is
// completely normal reuse at that length); requiring 3+ occurrences AND at
// least 1.2% word density (roughly 1 per 80 words) correctly caught a
// deliberately flat/generic sample while staying clean on the same human
// reference text.
const VAGUE_WORDS = ["good","bad","great","nice","big","small","important","interesting",
  "amazing","awesome","get","got","make","made","thing","things","stuff","look","looks",
  "show","shows","help","helps","use","uses","way","ways","lot","lots"];
const findOverusedVagueWords = (text) => {
  const totalWords = text.trim().split(/\s+/).filter(Boolean).length;
  if (totalWords === 0) return null;
  const lower = text.toLowerCase();
  const hits = VAGUE_WORDS.map(w => {
    const count = (lower.match(new RegExp("\\b" + w + "\\b", "g")) || []).length;
    return { word: w, count, density: count / totalWords };
  }).filter(x => x.count >= 3 && x.density >= 0.012);
  return hits.length ? hits.sort((a, b) => b.density - a.density).slice(0, 3) : null;
};

const findBannedWords = (text) => {
  const lower = text.toLowerCase();
  return BANNED_WORDS.filter(w => new RegExp("\\b" + w + "\\b").test(lower));
};

// INTENSIFIER_WORDS had no fix pass and no calibration at all - the
// highlight UI flagged every single occurrence of "very"/"really" etc.,
// which false-positived on the confirmed-human reference text (4 hits)
// during this session's own audit. Same density-based fix as
// findOverusedVagueWords above, since these words share the same character
// (common, generic, fine in isolation, only a tell when repeated) - reuses
// the same thresholds, which were already calibrated against that same
// reference text for vague words.
const findOverusedIntensifiers = (text) => {
  const totalWords = text.trim().split(/\s+/).filter(Boolean).length;
  if (totalWords === 0) return null;
  const lower = text.toLowerCase();
  const hits = INTENSIFIER_WORDS.map(w => {
    const count = (lower.match(new RegExp("\\b" + w + "\\b", "g")) || []).length;
    return { word: w, count, density: count / totalWords };
  }).filter(x => x.count >= 3 && x.density >= 0.012);
  return hits.length ? hits.sort((a, b) => b.density - a.density).slice(0, 3) : null;
};

// Perplexity/lexical-variety score (calcPerplexityScore below) had zero fix
// pass at all - confirmed by grepping every usage in this file. But most of
// what drives that score is either function words ("the", "of", "and") that
// can't and shouldn't be reduced, or genuine TOPIC words that SHOULD repeat
// (confirmed against the human reference text used to calibrate this: "habit"/
// "habits" alone were 3.6% density in a real, confirmed pre-AI published
// piece - flagging that would be wrong, not a fix). So this is deliberately
// NOT a "use rarer/fancier words" pass - that would directly fight
// BANNED_WORDS, which exists to strip OUT exactly that kind of vocabulary.
// This targets the one real, safely-fixable slice: generic, topic-independent
// words that a careful writer would normally vary, distinct from
// VAGUE_WORDS/INTENSIFIER_WORDS above (checked for overlap, none here).
const GENERIC_FILLER_WORDS = ["different","various","significant","particular","certain",
  "specific","general","simply","basically","clearly","obviously","overall",
  "typically","generally","primarily","particularly","relatively","fairly",
  "somewhat","numerous","several","multiple","common","commonly"];
const findOverusedGenericWords = (text) => {
  const totalWords = text.trim().split(/\s+/).filter(Boolean).length;
  if (totalWords === 0) return null;
  const lower = text.toLowerCase();
  const hits = GENERIC_FILLER_WORDS.map(w => {
    const count = (lower.match(new RegExp("\\b" + w + "\\b", "g")) || []).length;
    return { word: w, count, density: count / totalWords };
  }).filter(x => x.count >= 3 && x.density >= 0.012);
  return hits.length ? hits.sort((a, b) => b.density - a.density).slice(0, 3) : null;
};

// Sourced from Wikipedia:Signs of AI writing, which tracks which "AI
// vocabulary" words are currently trending in LLM output as models update -
// distinct from BANNED_WORDS (older, more static corporate-jargon list).
// Density-gated like VAGUE_WORDS/GENERIC_FILLER_WORDS above rather than a
// single-hit ban: several of these ("crucial", "valuable", "pivotal") have
// plenty of legitimate one-off uses in normal writing, per that same
// Wikipedia page's own explicit caution that individual instances are
// coincidental and density is what actually matters. "key" and "align with"
// deliberately excluded - too common/legitimate to safely flag even with
// density gating. "testament" excluded - already covered via the "a
// testament to" phrase in FORMAL_PHRASES.
const AI_VOCABULARY_WORDS = ["emphasizing","highlighting","showcasing","crucial","pivotal",
  "meticulous","valuable","intricate","garner","bolstered","boasts","enduring",
  "interplay","fostering"];
const findOverusedAiVocabulary = (text) => {
  const totalWords = text.trim().split(/\s+/).filter(Boolean).length;
  if (totalWords === 0) return null;
  const lower = text.toLowerCase();
  const hits = AI_VOCABULARY_WORDS.map(w => {
    const count = (lower.match(new RegExp("\\b" + w + "\\b", "g")) || []).length;
    return { word: w, count, density: count / totalWords };
  }).filter(x => x.count >= 3 && x.density >= 0.012);
  return hits.length ? hits.sort((a, b) => b.density - a.density).slice(0, 3) : null;
};
// Confirmed live via a real generalization test: the prompt's own
// styleText-conditional contractions instruction (see buildSystemPrompt)
// was NOT reliably followed - a deliberately zero-contraction style sample
// still produced heavy contraction use in the output. Same "prompt-only
// isn't enough" lesson as every other pattern this session - this is the
// deterministic backstop for when the prompt instruction alone doesn't
// hold. Only fires on a clear mismatch (sample is genuinely near-zero
// contractions, not just "used fewer than usual") to avoid overcorrecting
// a sample that simply uses contractions at an ordinary rate.
const CONTRACTION_RE = /\b\w+'(?:t|s|re|ve|ll|d|m)\b/gi;
// `formality` param: contraction usage is register-dependent, not a fixed
// personal trait - a style sample only reflects whatever register it
// happened to be written in, but a job's explicit Formality setting is the
// user's specific, per-piece intent (see buildSystemPrompt's matching
// CONTRACTIONS logic). First version of this fix (2026-09-14) just
// disabled this check entirely whenever formality was set, reasoning the
// prompt instruction alone would handle it - live-tested same day and
// found that assumption false: with Formality=Formal set and a Voice
// Profile active, the model added contractions anyway ("to match the
// sample's conversational style," per its own changelog), and with no
// backstop left to catch it, the output shipped unfixed. Corrected: the
// expected behavior is now derived from formality FIRST (Formal/
// Professional -> expect none, Casual/Conversational -> contractions are
// fine, nothing to flag), falling back to the sample's own density only
// when no formality is set at all - so there's always a real check against
// whichever signal is actually supposed to govern this, not just an "off"
// switch whenever a Voice Profile happens to be active too.
// `isClientVoice`: a Client Voice profile (ghostwriting) always defers to
// the SAMPLE's own contraction pattern, regardless of formality - the
// whole point is exact impersonation, not adapting the sample to context
// the way a personal Voice Profile correctly does. Mirrors the same
// isClientVoice branch in buildSystemPrompt's CONTRACTIONS instruction.
const findContractionMismatch = (text, styleSample, formality, isClientVoice) => {
  let expectFew;
  if (FORMAL_REGISTER_VALUES.has(formality) && !isClientVoice) {
    expectFew = true;
  } else if ((!formality || isClientVoice) && styleSample) {
    const sampleWords = styleSample.trim().split(/\s+/).filter(Boolean).length;
    if (sampleWords < 30) return null;
    const sampleDensity = (styleSample.match(CONTRACTION_RE) || []).length / sampleWords;
    expectFew = sampleDensity < 0.002;
  } else {
    return null; // Casual/Conversational formality, or no signal at all either way - contractions are fine
  }
  if (!expectFew) return null;
  const textContractions = text.match(CONTRACTION_RE) || [];
  return textContractions.length >= 3 ? { count: textContractions.length, examples: [...new Set(textContractions)].slice(0, 5) } : null;
};

const countBannedWords = (text) => findBannedWords(text).length;

// FORMAL_PHRASES/HEDGING_PHRASES already exist for the highlight UI and the
// AI-judge's own prompt definition of formalPhrasing, but neither ever fed
// into a fix pass - a real gap, confirmed live: "here's how" (already on
// this exact list) appeared verbatim in a passage a real detector (Pangram)
// flagged as AI-generated. Requires 2+ total hits before flagging, same
// "recurs, not one-off" principle as every other pattern detector this
// session - calibrated against a real confirmed-human reference text, which
// had a single legitimate use of "on the other hand" that a 1-hit threshold
// would have wrongly flagged.
const findFormalPhrases = (text) => {
  const lower = text.toLowerCase();
  const hits = [...FORMAL_PHRASES, ...HEDGING_PHRASES].filter(p => lower.includes(p));
  return hits.length >= 2 ? hits : [];
};

const wordCount = (t) => t.trim() ? t.trim().split(/\s+/).length : 0;

// Turns a raw fetch/API failure into a message the user can actually act on,
// instead of a dead-end "something went wrong."
const describeApiError = (err) => {
  const msg = err?.message || "";
  // fetchWithTimeout aborts via AbortController, which throws a DOMException
  // named "AbortError" - a real, honest message instead of the generic
  // network-error fallback below, which reads as "something's wrong with
  // your connection" when actually the server was just too slow.
  if (err?.name === "AbortError") return "This is taking unusually long to respond. The server may be under heavy load - please try again in a moment.";
  if (/\b402\b|word_limit_reached/.test(msg) || err?.code === 402)
    return "You've used all your available words. Buy a top-up at humankind.xtoolstack.com/credits to keep going, or wait for your monthly reset.";
  if (/\b401\b/.test(msg)) return "Your session has expired — please refresh the page and log in again.";
  if (/\b429\b/.test(msg)) return "Rate limited — please wait a moment and try again.";
  if (/\b5\d\d\b/.test(msg)) return "The server had a problem processing that. Please try again in a moment.";
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) return "Couldn't reach the server. Check your connection and try again.";
  return "Something went wrong. Please try again.";
};

// Reads the JSON body before checking status so 402 word-limit messages
// (and other structured errors) reach the caller instead of being swallowed.
const checkOllamaResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(response.status === 402 ? (data.message || "word_limit_reached") : ("Ollama error: " + response.status));
    if (response.status === 402) err.code = 402;
    throw err;
  }
  if (data.error) throw new Error(data.error);
  return data;
};

// ─── SETTINGS PERSISTENCE ──────────────────────────────────────────────────
// Remembers context/intensity settings across visits so users don't have to
// re-set them each time (and re-burn tokens re-running Humanize after
// realizing they forgot).

const CONTEXT_SETTINGS_KEY = "humankind_context_settings";
const loadSavedContextSettings = () => {
  try {
    const raw = localStorage.getItem(CONTEXT_SETTINGS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const saveContextSettings = (settings) => {
  try { localStorage.setItem(CONTEXT_SETTINGS_KEY, JSON.stringify(settings)); } catch { /* storage unavailable - non-critical */ }
};

// Remembers the actual working text (input, revoiced output, changelog, both
// AI-score panels) across navigation - e.g. leaving for the SEO Optimizer
// page and coming back. Uses the same synchronous localStorage + lazy-init
// pattern as loadSavedContextSettings above (not the async window.storage
// pattern used for Voice Profiles/Prompt Versions) specifically because the
// value is correct on the very first render with no restore step to race
// against - the exact bug class that hit Voice Profiles earlier.
const WORKING_TEXT_KEY = "humankind_working_text";
const loadSavedWorkingText = () => {
  try {
    const raw = localStorage.getItem(WORKING_TEXT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const saveWorkingText = (state) => {
  try { localStorage.setItem(WORKING_TEXT_KEY, JSON.stringify(state)); } catch { /* storage unavailable - non-critical */ }
};

// ─── API CALLS ───────────────────────────────────────────────────────────────

const OLLAMA_URL = process.env.REACT_APP_OLLAMA_URL || "http://localhost:11434";
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || "llama3:latest";

// Added 2026-09-17: none of this pipeline's /api/chat calls had a
// client-side timeout before - a genuinely hung request could ride the
// full 240s backend CURLOPT_TIMEOUT (see proxy.php) with nothing but a
// spinner and no way to tell if it's stuck or just slow. Confirmed as a
// real, user-facing incident the same day (a 285s+ wait, no feedback, had
// to be told to just cancel and retry). Set below the backend's own 240s
// ceiling so the CLIENT always surfaces a clear, actionable timeout rather
// than the connection eventually dying from the backend's own abrupt
// cutoff with a generic, unexplained network error.
const FETCH_TIMEOUT_MS = 200000;
const fetchWithTimeout = (url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
};
// A genuinely different model generation from OLLAMA_MODEL, used only by
// crossModelRevision below - see that function's comment for why. glm-5.3
// (the newest available) requires an explicit "thinking" mode the proxy
// doesn't send and errors out with "thinking":{"type":"disabled"} - confirmed
// live against the real Z.ai API. glm-5.2 is the newest model that still
// accepts the same request shape the rest of this app already sends.
const SECONDARY_MODEL = process.env.REACT_APP_SECONDARY_MODEL || "glm-5.2";
// Login is only required in production - local dev talks directly to Ollama
// with no auth backend at all, so gating it locally would just break dev.
const REQUIRE_AUTH = process.env.REACT_APP_REQUIRE_AUTH === "true";

// The prompt requires these 4 fields every time - if any is missing/non-numeric,
// the model deviated from the requested shape. Silently defaulting a missing
// field to 0 would be indistinguishable from a genuine "no signal" judgment,
// so a response failing this check is treated as unreliable, not a low score.
const REQUIRED_ANALYSIS_FIELDS = ["uniformSentences", "predictableOpeners", "perfectSymmetry", "formalPhrasing"];

// Shared by analyseText below AND the best-of-N reranking step in
// handleHumanize (2026-09-15) - extracted so both use the exact same
// detector list rather than risking two copies drifting apart. Pure JS, no
// API call, safe to run on every reranking candidate for free. Combines
// every signal this project has calibrated against real third-party
// detector scans (the 10 structural pattern detectors, weighted 2x same as
// analyseText) with the two purely-computed proxies (perplexity,
// burstiness) and the new function-word signal - deliberately excludes the
// 4 LLM-judged signals (uniformSentences/predictableOpeners/perfectSymmetry/
// formalPhrasing), which need their own API call and would add latency to
// every reranking candidate for signals already shown today to be the
// least reliable indicator of real detector behavior.
const computeLocalAiTellScore = (text) => {
  const structuralHits = [
    findChoppyRun(text),
    (() => { const t = findTricolonSymmetry(text); return t && (t.hits || t.clustered || t.anaphora) ? t : null; })(),
    findTwistRun(text),
    findRhetoricalFlourishSentences(text),
    findFragmentStacking(text),
    findOverusedVagueWords(text),
    findRepeatedOpeners(text),
    findParagraphSymmetry(text),
    findParagraphOpenerViolations(text),
    findLowFunctionWordSentences(text),
  ].filter(Boolean).length;
  const structuralPatternScore = Math.min(100, structuralHits * 30);
  const perplexity = 100 - calcPerplexityScore(text);
  const burstinessScore = 100 - calcBurstinessScore(text);
  const functionWordScore = calcFunctionWordScore(text);
  const scores = [perplexity, burstinessScore, functionWordScore, structuralPatternScore, structuralPatternScore];
  return { structuralHits, structuralPatternScore, score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) };
};

const analyseText = async (text) => {
  const emDashCount = countEmDashes(text);
  const bannedCount = countBannedWords(text);
  const emDashScore = Math.min(100, emDashCount * 12);
  const bannedScore = Math.min(100, bannedCount * 8);

  const attemptOnce = async () => {
    const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: OLLAMA_MODEL,
        stream: false,
        format: "json",
        job_type: "analyze",
        temperature: 0, // scoring should be as consistent as possible run-to-run, not creative
        messages: [
          { role: "system", content: ANALYSIS_PROMPT },
          { role: "user", content: text },
        ],
      }),
    });
    const data = await checkOllamaResponse(response);
    const raw = data.message?.content || "{}";
    let parsed = null;
    try {
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
    } catch { return null; }
    if (!parsed) return null;
    if (!REQUIRED_ANALYSIS_FIELDS.every(k => Number.isFinite(Number(parsed[k])))) return null;
    // All 4 fields landing on exactly 0 together, with no actual topIssues
    // named, isn't a plausible careful judgment - real analyses (even of
    // very human-sounding text) still name specific low-level observations.
    // This is the model taking a shortcut, not a genuine "perfect" score.
    const allZero = REQUIRED_ANALYSIS_FIELDS.every(k => Number(parsed[k]) === 0);
    const noIssuesNamed = !Array.isArray(parsed.topIssues) || parsed.topIssues.every(t => !t || !String(t).trim());
    if (allZero && noIssuesNamed) return null;
    return parsed;
  };

  let parsed = await attemptOnce();
  if (!parsed) parsed = await attemptOnce(); // one retry - the required-fields check can reject a single flaky response
  if (!parsed) return null;

  const toScore = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0; };
  parsed.emDash = emDashScore;
  parsed.bannedWords = bannedScore;
  parsed.emDashCount = emDashCount;
  parsed.bannedCount = bannedCount;
  parsed.uniformSentences = toScore(parsed.uniformSentences);
  parsed.predictableOpeners = toScore(parsed.predictableOpeners);
  parsed.perfectSymmetry = toScore(parsed.perfectSymmetry);
  parsed.formalPhrasing = toScore(parsed.formalPhrasing);
  parsed.topIssues = Array.isArray(parsed.topIssues)
    ? parsed.topIssues
    : (typeof parsed.topIssues === "string" ? [parsed.topIssues] : []);
  parsed.verdict = typeof parsed.verdict === "string" ? parsed.verdict : "";
  const burstiness = calcBurstiness(text);
  if (burstiness?.lowBurstiness) {
    parsed.topIssues.push(`Low sentence-length variation (avg ${burstiness.avgLen} words, stdev ${burstiness.stdDev})`);
  }
  parsed.readability = calcReadability(text);
  // Computed directly from the text (not LLM-judged), inverted to the same
  // "AI-tell strength" polarity as the other scores: high = strong AI tell.
  parsed.perplexity = 100 - calcPerplexityScore(text);
  parsed.burstinessScore = 100 - calcBurstinessScore(text);
  // Unlike perplexity/burstiness above (theory-grounded proxies), this one
  // is grounded directly in a real third-party detector report on this
  // pipeline's own output - see calcFunctionWordScore's comment.
  parsed.functionWordScore = calcFunctionWordScore(text);

  // The 8 signals above are either raw LLM self-judgment (uniformSentences,
  // predictableOpeners, perfectSymmetry, formalPhrasing) or crude proxies
  // (perplexity is unigram word-rarity, not real next-token probability;
  // burstinessScore is just sentence-length stdev) - none of them draw on
  // the actual pattern detectors this app built and live-verified against
  // real Pangram/ZeroGPT/GPTZero scans throughout this project
  // (findChoppyRun, findTricolonSymmetry, etc. - used only in the fix
  // pipeline, `runConsolidatedCleanup`, never in this score). Confirmed via
  // a real ZeroGPT-flagged-99.2%-AI text that scored "22, Likely Human"
  // here: perfectSymmetry's own LLM judgment missed a real tricolon-cluster
  // `findTricolonSymmetry` catches directly. Folding those in grounds the
  // score in the same detectors already proven reliable, rather than
  // leaving it as pure LLM self-judgment plus two weak heuristics.
  const { structuralHits, structuralPatternScore } = computeLocalAiTellScore(text);
  parsed.structuralPatterns = structuralHits;
  parsed.structuralPatternScore = structuralPatternScore;
  if (structuralHits > 0) {
    parsed.topIssues.push(`${structuralHits} known AI structural pattern${structuralHits > 1 ? "s" : ""} detected directly (not just LLM-judged)`);
  }

  const scores = [parsed.bannedWords, parsed.emDash, parsed.uniformSentences,
    parsed.predictableOpeners, parsed.perfectSymmetry, parsed.formalPhrasing,
    parsed.perplexity, parsed.burstinessScore, parsed.functionWordScore,
    parsed.structuralPatternScore, parsed.structuralPatternScore]; // weighted 2x - the only bucket calibrated against real third-party detector scans
  parsed.overallAI = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  return parsed;
};

const humanizeText = async (text, intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, promptOverride) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "humanize",
      messages: [
        { role: "system", content: promptOverride || buildSystemPrompt(intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause) },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content || "";
};

// Appended to follow-up-pass prompts so Fact Lock survives beyond the initial
// humanize call - these run on still-tokenized text (unlocked only once, at
// the very end of the whole pipeline) specifically so this instruction has
// something to protect.
const LOCK_TOKEN_INSTRUCTION = " The text may contain tokens like [LOCK_0], [LOCK_1], etc. representing protected names, numbers, quotes, or links - copy each token through exactly as written, in the same position; never edit, remove, or translate the token itself.";

const expandText = async (text, targetWords, currentWords, hasLocks) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "expand",
      messages: [
        {
          role: "system",
          // NEVER INVENT DETAILS was previously only in buildSystemPrompt's
          // first-draft call - this is a completely separate API call with
          // its own system message, so it never inherited that rule. Live-
          // tested this was a real, severe gap: a 350->1000-word expansion
          // request produced an invented "studies have shown [fabricated
          // cognitive-benefit claims]" citation and other made-up asides,
          // because this prompt's own instruction ("add relevant examples,
          // extend explanations") actively invited it with nothing forbidding
          // fabrication. "Unpack ideas already present" (not "add new ones")
          // is the other real fix - the previous wording asked for new
          // examples/asides by name, which is precisely what led the model
          // to invent them once genuine unpacking ran out of material.
          content: `You are expanding an existing piece of writing to hit a target word count. Do not rewrite, restructure, or shorten anything already there, keep the exact voice, tone, and wording.

HARD RULE: NEVER INVENT DETAILS. Never add a fact, statistic, study, name, brand, or specific example that is not already in the original text. Reaching the target length by inventing plausible-sounding content ("studies have shown...", a fabricated example, an invented aside) is not allowed, even if the text is still short of the target - it is both inaccurate and a detectable AI tell in its own right.

Ways to add length using ONLY what's already there: unpack an idea already present into more sentences, explain a point already made in more depth, restate a claim already in the text from a slightly different angle, or add a transition/reflection connecting two ideas already present. If you run out of genuine material to unpack before reaching the target, get as close as you honestly can and stop - do not fabricate the remainder.

The current draft is ${currentWords} words; the target is ${targetWords} words. Output ONLY the complete expanded text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Targeted cleanup pass: trims a piece back down when it's grown well
// beyond the original's length with no explicit target word count set -
// the mirror image of expandText above, for the opposite failure. Confirmed
// live-necessary the same way as every other backstop this session: even
// with an explicit "stay within 10% of the original" HARD RULE, repeated in
// both the main rule set and the self-check, the model still expanded to
// 163-175% of the original word count on real test runs, filling the extra
// length with fabricated specifics (an invented "Google" reference, made-up
// period-detail asides) that weren't in the source. Asking harder in the
// prompt didn't fix it; this is the actual fix.
const compressToOriginalLength = async (text, targetWords, hasLocks) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "compress_to_length",
      temperature: 0, // a specific required edit, not creative writing
      messages: [
        {
          role: "system",
          content: `You are doing a targeted trim on an already-finished piece of writing. It has grown significantly longer than its source material, which means it almost certainly contains invented details, examples, or asides that were not in the original. Cut it down to roughly ${targetWords} words by removing anything that is not a rephrasing of something actually present in the original - invented names, statistics, rhetorical questions, asides, or explanations that go beyond what the source said. Do not remove any idea, fact, or section that WAS genuinely present in the original. Do not summarise what remains, just delete the added material. Output ONLY the complete trimmed text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Real, deterministic detector for 3+ consecutive sentences sharing the same
// opening two words - a parallel/symmetric-structure AI tell that survives
// even after every other word has been rewritten (e.g. "Not the humans...
// Not the corporate greed... Not the lack of regulation..."). Prompt
// instructions alone were tested and failed to reliably stop this; this
// catches it after the fact instead of hoping the model avoids it.
const findRepeatedOpeners = (text) => {
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
  const trimmed = sentences.map(s => s.trim()).filter(Boolean);
  const openerOf = (s) => s.replace(/^[^a-zA-Z0-9]+/, "").split(/\s+/).slice(0, 2).join(" ").toLowerCase();
  for (let i = 0; i < trimmed.length - 2; i++) {
    const a = openerOf(trimmed[i]);
    if (!a || a.split(" ").length < 2) continue;
    const b = openerOf(trimmed[i + 1]);
    const c = openerOf(trimmed[i + 2]);
    if (a === b && b === c) {
      return { opener: a, sentences: [trimmed[i], trimmed[i + 1], trimmed[i + 2]] };
    }
  }
  return null;
};

// Splits on one or more newlines, matching the paragraph convention already
// used elsewhere in this file (see exportDocx's own /\n+/ split).
const splitParagraphs = (text) => text.split(/\n+/).map(p => p.trim()).filter(Boolean);

const PARAGRAPH_VERB_RE = /^([A-Z][a-z']+(?:\s[a-z']+)?)\s+(is|are|get|gets|go|goes|has|have|means?|works?|helps?|makes?|need|needs|come|comes|feel|feels|look|looks|seem|seems|blow|blows)\b/;

// Extracts a lightweight opener "signature" for one paragraph: its first
// word, whether it opens with a gerund, and whether it opens with a [noun
// phrase] + [common verb] shape (e.g. "Road bikes are...", "Mountain bikes
// are..."). No real POS tagging - deliberately cheap regex heuristics, same
// style as the sentence-level detectors above.
const paragraphOpenerSignature = (para) => {
  const firstSentence = (para.match(/^[^.!?]*[.!?]?/) || [""])[0].trim();
  const words = firstSentence.split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const firstWordRaw = words[0].replace(/[^a-zA-Z']/g, "");
  if (!firstWordRaw) return null;
  const verbMatch = firstSentence.match(PARAGRAPH_VERB_RE);
  // Catches "Road bikes. Built for speed..." / "Mountain bikes. These go
  // anywhere..." style openers: the label word itself differs each time (so
  // firstWord/verb matching alone misses it), but the SHAPE repeats - a
  // short, verb-less noun-phrase fragment standing in for a topic sentence.
  const hasAnyVerb = /\b(is|are|was|were|be|been|has|have|had|will|would|can|could|should|do|does|did|go|goes|get|gets)\b/i.test(firstSentence);
  return {
    firstWord: firstWordRaw.toLowerCase(),
    isGerund: /ing$/i.test(firstWordRaw) && words.length > 1,
    verb: verbMatch ? verbMatch[2].toLowerCase() : null,
    isFragmentLabel: words.length <= 3 && !hasAnyVerb,
  };
};

// Real, deterministic detector for 3+ paragraphs (within a rolling window of
// 5) that open with the same word, the same gerund-led shape, the same [noun
// phrase] + [common verb] shape, or the same short label-fragment shape.
// Complements findRepeatedOpeners above, which only ever looks at
// consecutive *sentences* - paragraph-level predictable-opener patterns were
// scored by the AI judge but nothing in the pipeline ever caught or fixed
// them.
const findParagraphOpenerViolations = (text) => {
  const paragraphs = splitParagraphs(text);
  if (paragraphs.length < 3) return null;
  const sigs = paragraphs.map(paragraphOpenerSignature);
  const WINDOW = 5;
  for (let start = 0; start <= paragraphs.length - 3; start++) {
    const end = Math.min(start + WINDOW, paragraphs.length);
    const windowIdx = [];
    for (let i = start; i < end; i++) if (sigs[i]) windowIdx.push(i);

    const byFirstWord = new Map();
    const byVerb = new Map();
    const gerundIdx = [];
    const fragmentLabelIdx = [];
    for (const i of windowIdx) {
      const sig = sigs[i];
      if (!byFirstWord.has(sig.firstWord)) byFirstWord.set(sig.firstWord, []);
      byFirstWord.get(sig.firstWord).push(i);
      if (sig.verb) {
        if (!byVerb.has(sig.verb)) byVerb.set(sig.verb, []);
        byVerb.get(sig.verb).push(i);
      }
      if (sig.isGerund) gerundIdx.push(i);
      if (sig.isFragmentLabel) fragmentLabelIdx.push(i);
    }

    let hitIdx = null, patternLabel = null;
    for (const [word, idxs] of byFirstWord) {
      if (idxs.length >= 3) { hitIdx = idxs; patternLabel = `starting with "${word}"`; break; }
    }
    if (!hitIdx) for (const [verb, idxs] of byVerb) {
      if (idxs.length >= 3) { hitIdx = idxs; patternLabel = `following the same "[subject] ${verb}" opening shape`; break; }
    }
    if (!hitIdx && gerundIdx.length >= 3) { hitIdx = gerundIdx; patternLabel = "opening with a gerund (-ing word)"; }
    if (!hitIdx && fragmentLabelIdx.length >= 3) { hitIdx = fragmentLabelIdx; patternLabel = "opening with a short label-style fragment (e.g. \"Road bikes.\", \"Mountain bikes.\")"; }

    if (hitIdx) {
      return {
        pattern: patternLabel,
        keptParagraph: paragraphs[hitIdx[0]],
        offending: hitIdx.slice(1).map(i => paragraphs[i]), // keep the first, fix the rest
      };
    }
  }
  return null;
};

// Real, deterministic detector for repeated "rule of three" tricolons - a
// classic parallel/symmetric-structure AI tell (e.g. "fast, reliable, and
// easy to use" ... later ... "clean, simple, and effective") separate from
// the repeated-opener case above. A single balanced three-item list is normal
// human writing and shouldn't be flagged; this only fires when the SAME
// rhetorical device (near-equal-length three-item list) recurs 2+ times
// across the piece, matching the "AnalysisPanel" definition of perfectSymmetry
// ("overly balanced lists, three-part structures, or parallel constructions")
// far more broadly than the narrow repeated-opener case alone ever covered.
const findTricolonSymmetry = (text) => {
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
  const trimmed = sentences.map(s => s.trim()).filter(Boolean);
  // Item 1's true grammatical start isn't reliably findable (it follows
  // whatever lead-in the sentence has), so only the last two items - which
  // ARE cleanly delimited by the commas/and/or themselves - are compared for
  // matched length. That's also where the actual "cadence" tell lives: two
  // short, equal-length items landing right before the sentence's end.
  const tricolonRe = /^(.*?),\s*([^,]+?),?\s+(?:and|or)\s+([^,.!?]+)/;
  const hits = [];
  const looseIdx = [];
  trimmed.forEach((sentence, i) => {
    const match = sentence.match(tricolonRe);
    if (!match) return;
    const before = match[1].trim();
    const item2 = match[2].trim();
    const item3 = match[3].trim();
    if (!before) return;
    const item2Words = item2.split(/\s+/).filter(Boolean).length;
    const item3Words = item3.split(/\s+/).filter(Boolean).length;
    const allShort = item2Words <= 4 && item3Words <= 4;
    const maxDiff = Math.abs(item2Words - item3Words);
    if (allShort && maxDiff <= 1) {
      const item1 = before.split(/\s+/).filter(Boolean).slice(-4).join(" ");
      hits.push({ sentence, items: [item1, item2, item3] });
    }
    // Broader net: ANY 3-item "X, Y, and/or Z" list, regardless of item
    // length matching - catches uneven-length parallel lists (e.g. "cultures,
    // jobs, and people you'd never otherwise encounter") that the strict
    // equal-length check above misses. Real Pangram/ZeroGPT scan on a
    // properly-sized (non-fabricated) revoice still flagged that exact
    // sentence at 70.7% AI even though it's not length-matched. Capped at 8
    // words per item to exclude ordinary compound sentences with an
    // unrelated trailing clause ("...encounter, and puts you face to face
    // with people you'd never meet any other way" - a real independent
    // clause, not a list item) - confirmed false-positived on exactly that
    // shape during live testing without this cap. Also requires item2/item3
    // to contain no finite verb (be/have/do/modal) - a genuine list item is a
    // bare noun phrase ("jobs", "people you'd never otherwise encounter"),
    // while "the grammar rules can seem overwhelming" or "don't worry about
    // perfection" are full independent clauses that just happen to land
    // after a comma - confirmed false-positived on both of those exact
    // sentences (an ordinary "Sure, X, and Y" compound sentence and a
    // natural "do this, do this, and don't do that" imperative list) without
    // this check.
    const FINITE_VERB_MARKER_RE = /\b(am|is|are|was|were|be|been|being|have|has|had|do|does|did|don't|doesn't|didn't|can|can't|could|will|won't|would|shall|should|may|might|must)\b/i;
    const commaCount = (sentence.match(/,/g) || []).length;
    if (commaCount >= 2 && item2Words <= 8 && item3Words <= 8 &&
        !FINITE_VERB_MARKER_RE.test(item2) && !FINITE_VERB_MARKER_RE.test(item3)) {
      looseIdx.push(i);
    }
  });

  // Only fires when two such lists land within 4 sentences of each other -
  // that clustering (the same rhetorical device used twice, close together)
  // is the actual tell. Two incidental lists scattered naturally across a
  // long piece are normal human writing and must NOT fire: confirmed
  // false-positived on a real ~700-word confirmed-human reference text
  // without this window requirement (a mundane "add, install and configure"
  // list and an unrelated list 8 paragraphs later).
  let clustered = null;
  for (let i = 0; i < looseIdx.length - 1; i++) {
    if (looseIdx[i + 1] - looseIdx[i] <= 4) {
      clustered = [trimmed[looseIdx[i]], trimmed[looseIdx[i + 1]]];
      break;
    }
  }

  // Anaphora variant: a short lead-in word repeated 3+ times as a rhetorical
  // list-item opener in one sentence (e.g. "not about being special, but
  // about being smart, about strategy, and about showing up every day").
  // Restricted to genuinely rhetorical markers, NOT structural prepositions
  // like "of"/"with"/"for" - those recur incidentally in normal writing all
  // the time and false-positived here without this restriction (a real
  // human sentence used "of" 3x with no rhetorical intent at all).
  const anaphoraCandidates = ["about", "not", "never", "without", "no matter"];
  let anaphora = null;
  for (const sentence of trimmed) {
    for (const w of anaphoraCandidates) {
      const re = new RegExp(`\\b${w}\\b`, "gi");
      const m = sentence.match(re);
      if (m && m.length >= 3) { anaphora = { sentence, word: w, count: m.length }; break; }
    }
    if (anaphora) break;
  }

  const strictHits = hits.length >= 2 ? hits.slice(0, 3) : null;
  if (!strictHits && !clustered && !anaphora) return null;
  return { hits: strictHits, clustered, anaphora };
};

// Real, deterministic detector for a specific long-sentence AI tell: a
// justifying clause (because/since/when/while/although/though) stacked with
// a coordinating "and" or participial extension, capped with a trailing
// "that" clause delivering a rhetorical/emotional payoff - e.g. "Cycling
// rewards consistency when you find a bike you actually enjoy riding and
// set it up so it feels like part of your body, giving you thousands of
// kilometers of reliable, joyful riding back that makes you wonder why you
// ever stopped." Reverse-engineered from a real GPTZero scan that flagged a
// revoiced piece 100% AI: every one of its 5 named "high AI impact"
// sentences shared exactly this shape, while its "human impact" sentences
// (including an equally long one) did not - verified against both sets
// plus 5 unrelated generic long sentences (zero false positives) before
// shipping. Distinct from findTricolonSymmetry/findParagraphSymmetry above,
// which target balanced-list and paragraph-level repetition, not this
// single-sentence clause-stacking shape.
const RHETORICAL_SUBORDINATOR_RE = /\b(because|since|when|while|although|though)\b/i;
const TRAILING_FLOURISH_RE = /,\s+(?:[a-z]+ing\b|and\b)[^.!?]*\bthat\b[^.!?]*[.!?]\s*$/i;
const findRhetoricalFlourishSentences = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const hits = sentences.filter(s => {
    const words = s.split(/\s+/).filter(Boolean).length;
    return words >= 24 && RHETORICAL_SUBORDINATOR_RE.test(s) && TRAILING_FLOURISH_RE.test(s);
  });
  return hits.length ? hits.slice(0, 6) : null;
};

// Real, deterministic detector for stacked grammatical sentence fragments -
// e.g. "Endless accessories to choose from that can add up in cost, turning
// a simple purchase into a big expense." / "Enough jargon to write a
// dictionary, with terms like 'gravel grinding' and 'enduro' that might as
// well be a foreign language." Each opens with a quantifier noun phrase
// (Enough/Endless/Countless/Hundreds of...), has no finite verb governing
// the main clause (only an infinitive "to choose"/"to write"), and ends with
// a relative or participial tail delivering a punchy consequence. A single
// such fragment is normal stylistic writing; 2+ in a row is the recurring
// device this catches, same "recurs, not one-off" principle as
// findTricolonSymmetry above. Reverse-engineered from a real Pangram scan
// that flagged a passage using exactly this device as AI-generated.
const QUANTIFIER_OPENER_RE = /^(Enough|Endless|Countless|Hundreds of|Dozens of|Plenty of|So much|Too much|No shortage of|A world of)\b/i;
const INFINITIVE_RE = /\bto\s+[a-z]+\b/i;
const FINITE_VERB_RE = /\b(is|are|was|were|has|have|had|do|does|did|can|could|will|would|should|must|may|might)\b/i;
const FRAGMENT_TRAILING_TAIL_RE = /,\s*[^,.!?]*(that|which|turning|making|giving|leaving)\b[^.!?]*[.!?]\s*$/i;
const findFragmentStacking = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const hits = sentences.filter(s => {
    if (!QUANTIFIER_OPENER_RE.test(s)) return false;
    // A finite verb INSIDE a relative clause ("accessories that can add up")
    // doesn't count as the main clause's verb - only check the segment
    // before the first relative pronoun or comma, whichever comes first.
    const cutIdx = s.search(/,|\b(that|which|who)\b/i);
    const leading = cutIdx === -1 ? s : s.slice(0, cutIdx);
    if (FINITE_VERB_RE.test(leading)) return false;
    if (!INFINITIVE_RE.test(leading)) return false;
    return FRAGMENT_TRAILING_TAIL_RE.test(s);
  });
  return hits.length >= 2 ? hits.slice(0, 4) : null;
};

// Real, deterministic detector for the "not only X but also Y" family of
// rhetorical constructions - a recognizable, formulaic-sounding scaffold
// real writers rarely reach for outside of a single ironic/emphatic use.
// Requires 2+ occurrences before flagging, same "recurs, not one-off"
// principle as every other detector here - a single natural use of "not
// only... but also" is unremarkable.
const NOT_ONLY_RE = /\bnot only\b[^.!?]*?\bbut\b(?:\s+also)?/i;
const NOT_JUST_RE = /\bnot just\b[^.!?]*?\bbut\b/i;
const findNotOnlyButAlso = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const hits = sentences.filter(s => NOT_ONLY_RE.test(s) || NOT_JUST_RE.test(s));
  return hits.length >= 2 ? hits.slice(0, 4) : null;
};

// Real, deterministic backstop for the closing-paragraph summary tell -
// buildSystemPrompt's own SELF-CHECK already asks the model to catch this
// itself ("Does it end with a neat summary? If yes, cut or rewrite it"), but
// per this project's own repeated finding, a prompt-only instruction isn't
// reliable enough on its own - this is the deterministic backstop for when
// it doesn't comply. Only checks the FINAL paragraph (a summary phrase
// mid-piece is normal; it's specifically the closing-summary shape that's
// the tell) and only fires on phrases distinct enough not to appear in
// ordinary prose - deliberately excludes anything already covered by
// FORMAL_PHRASES/findFormalPhrases (e.g. "in conclusion") to avoid double-
// citing the same sentence from two different detectors in one pass.
const SUMMARY_CLOSING_PHRASES = ["to sum up", "to wrap up", "all in all", "in short",
  "at the end of the day", "when all is said and done", "the bottom line is",
  "ultimately, this shows", "overall, this"];
const findSummaryClosing = (text) => {
  const paragraphs = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  if (paragraphs.length < 2) return null;
  const last = paragraphs[paragraphs.length - 1];
  const lower = last.toLowerCase();
  const hit = SUMMARY_CLOSING_PHRASES.find(p => lower.includes(p));
  return hit ? { paragraph: last, phrase: hit } : null;
};

// A distinct, well-evidenced AI tell confirmed across 4 separate real
// GPTZero reports run the same day, on 4 different topics - each one
// specifically called out numbered/labeled section headers ("Step 1: Choose
// the Right Location", "Step 7: Water and Mulch") as a structural AI
// marker, and one report flagged it 4 separate times in a single piece.
// Already prompt-banned ("No textbook-style section headings" in
// buildSystemPrompt's BANNED STRUCTURES) but per this session's repeated
// finding that prompt-only rules aren't reliably followed, this is the
// deterministic backstop. Unlike the topic-sentence-opener pattern (a
// semantic judgment call, deliberately left without a backstop), this is
// purely syntactic - a numbered/labeled short line with no sentence-ending
// punctuation - so a cheap heuristic carries little false-positive risk.
// Live-confirmed gap (2026-09-08): a real 5-habit article's source used
// markdown headers ("### 1. Pay Yourself First (Automatically)"), and the
// original regex required the number pattern at the true start of the
// line - the "### " prefix meant it never matched, so this detector never
// fired on the SOURCE, the model got no explicit per-heading instruction,
// and all 5 headings survived into the output nearly verbatim (the generic
// "no textbook headings" prompt rule alone wasn't enough, same lesson as
// every other prompt-only fix this project has tried). Optional leading
// #{0,6} markdown prefix closes it without changing behavior on non-
// markdown numbered headings.
const findNumberedHeadings = (text) => {
  const matches = [...text.matchAll(/^#{0,6}\s*(?:step\s+\d+|\d+)[.:)]\s*([A-Z][^.!?\n]{2,50})$/gim)];
  if (!matches.length) return null;
  return matches.map(m => m[0].trim()).slice(0, 6);
};

// A distinct heading style the detector above misses entirely - confirmed
// by a real GPTZero report on a "7 habits" listicle that flagged "The 'One
// Thing' Principle.", "The Two-Minute Rule", "The Daily Shutdown Routine."
// as AI structural tells. No numbers, so findNumberedHeadings' pattern never
// matches - this is a NAMED section header instead. The reliable signal
// that distinguishes it from a genuine short sentence in the same "isolated
// short line" shape: TITLE CASE. A real sentence in this shape would be in
// normal sentence case ("The two-minute rule is simple."), only the first
// word capitalized - a heading capitalizes every significant word ("The Two-
// Minute Rule"). Requires 2+ capitalized content words (skipping small
// connector words) so a short ordinary sentence that happens to start with
// one capital doesn't false-positive.
const TITLE_CASE_SMALL_WORDS = new Set(["a","an","the","of","to","in","and","or","for","with","on","at","by"]);
// Live-confirmed gap (2026-09-08): a real heading, "Pay Yourself First
// (Automatically)", was missed because "(Automatically)" starts with "("
// - the old check (/^["']?[A-Z]/) only tolerated a single leading quote
// before the capital letter, not other leading punctuation. Same problem
// hit "The 48-Hour Rule for Impulse Buys" via "48-Hour" starting with a
// digit. Both are still genuinely Title Case (every significant WORD
// starts with a capital once you skip past its own leading punctuation/
// digits) - broadened to strip any leading non-letter characters first,
// not just an optional quote, before checking for the capital.
const isTitleCaseWord = (w) => /^[^a-zA-Z]*[A-Z]/.test(w);
// Live-confirmed false positive (2026-09-14, via the free analyzer): fires
// on "My Freelance Journey" - the confirmed-human reference text's own
// title, sitting alone at the top of the document. A single document title
// is normal for any piece of writing, human or AI; the real AI tell this
// detector targets is RECURRING Title-Case section headers through the
// body (a real listicle test case had 5). Same "require 2+ before
// flagging" fix already applied to tricolon/fragmentStacking/formalPhrases
// for the identical one-off-vs-recurring ambiguity - a lone hit is treated
// as a title, not evidence.
const findTitledHeadings = (text) => {
  const paragraphs = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const hits = [];
  for (const para of paragraphs) {
    if (/[.!?]\s+[A-Z]/.test(para)) continue; // more than one sentence - not an isolated heading line
    const words = para.replace(/[.!?]+$/, "").split(/\s+/).filter(Boolean);
    if (words.length < 2 || words.length > 8) continue;
    const significant = words.filter(w => !TITLE_CASE_SMALL_WORDS.has(w.toLowerCase().replace(/[^a-z]/gi, "")));
    if (significant.length < 2) continue;
    if (significant.every(isTitleCaseWord)) hits.push(para);
  }
  return hits.length >= 2 ? hits.slice(0, 6) : null;
};

// Real, deterministic detector for the single biggest tell this whole
// detection-hardening effort turned up: a run of 3+ consecutive short (under
// 10 words), grammatically clean sentences - "polished choppiness." The
// system prompt now explicitly forbids this (see buildSystemPrompt's VOICE
// section and SELF-CHECK), but confirmed live against a real Pangram
// re-scan that prompt instructions alone don't reliably prevent it - the
// model complies most of the time, not every time, the same "asking nicely
// isn't enough" lesson as every other pattern this session. This is the
// backstop for when it doesn't comply. Returns ALL offending runs, not just
// the longest one - live-testing found a real piece with one choppy run per
// paragraph, and citing only the longest left the second one unfixed.
// Threshold calibrated against a real, confirmed-100%-human reference text
// (Pangram + ZeroGPT both agreed): at <12 words it produced 3 false-positive
// runs on that genuinely human text (one 5 sentences long); dropping to <10
// eliminates all 3 false positives while still catching both real runs in a
// confirmed 100%-AI-flagged text. Don't raise this back toward 12 without
// re-checking against that same reference text.
const findChoppyRun = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const lens = sentences.map(s => s.split(/\s+/).filter(Boolean).length);
  const runs = [];
  let runStart = -1;
  for (let i = 0; i <= sentences.length; i++) {
    const isShort = i < sentences.length && lens[i] < 10;
    if (isShort) {
      if (runStart === -1) runStart = i;
    } else {
      if (runStart !== -1 && i - runStart >= 3) runs.push(sentences.slice(runStart, i));
      runStart = -1;
    }
  }
  return runs.length ? runs.slice(0, 3) : null;
};

// Real, deterministic detector for a new pattern found live, and an ironic
// one: an overcorrection of findChoppyRun above. Once the prompt started
// pushing the model to join short sentences with "and"/"but" instead of a
// period, it began applying that so consistently that a NEW uniform pattern
// emerged - every sentence in a run following the same "[action], but/and
// [short twist]" shape, e.g. "...it would stop working, but wrong, it kept
// running." / "...before it crashed, and it still worked." / "...back on,
// but just got a black screen." Fixing one uniformity accidentally
// introduced a different one. Deliberately narrow to avoid false-positiving
// on ordinary mid-sentence "and" usage (a broader "any sentence with a
// mid-sentence conjunction" version was tested first and produced false
// positives on the same confirmed-human reference text used elsewhere in
// this file's comments) - requires a comma-introduced conjunction in the
// back half of the sentence, followed by a SHORT (<=10 word) trailing
// clause, which is what actually makes it read as a "twist," not just any
// sentence that happens to contain "and".
const findTwistRun = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const hasTwist = (s) => {
    const m = s.match(/,\s*(but|and|so)\s+([^.!?]+)[.!?]?\s*$/i);
    if (!m) return false;
    const trailingWords = m[2].split(/\s+/).filter(Boolean).length;
    const idx = s.length - m[0].length;
    return trailingWords <= 10 && idx > s.length * 0.4;
  };
  const runs = [];
  let runStart = -1;
  for (let i = 0; i <= sentences.length; i++) {
    const isTwist = i < sentences.length && hasTwist(sentences[i]);
    if (isTwist) {
      if (runStart === -1) runStart = i;
    } else {
      if (runStart !== -1 && i - runStart >= 3) runs.push(sentences.slice(runStart, i));
      runStart = -1;
    }
  }
  return runs.length ? runs.slice(0, 3) : null;
};

// Live-confirmed (2026-09-15), a real recurring pattern no existing
// detector caught: a semicolon-joined "negate X; it's Y" contrastive-
// reframe template, repeated - e.g. "toxic positivity doesn't change
// reality; it just makes you feel guilty..." immediately followed by
// "mindset work isn't about slapping a smile on it; it's about
// fundamentally changing...". Same shape twice in a row survived straight
// through to a 91% GPTZero score. Requires 2+ occurrences before
// flagging, same "recurs, not one-off" convention as every other pattern
// detector here - a single genuine semicolon-negation is normal writing,
// not a tell. Verified clean against the confirmed-human reference text.
const findNegateReframeSemicolon = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const hits = [];
  for (const s of sentences) {
    const parts = s.split(";");
    if (parts.length !== 2) continue;
    const [before, after] = parts.map(p => p.trim());
    // Live-confirmed bug (2026-09-15): \bn't\b can never match inside a
    // real contraction like "doesn't" - \b requires a word/non-word
    // transition, and the letter immediately before "n't" ("e" in
    // "doesn't") is itself a word character, so there's no boundary
    // there. Dropped the leading \b for "n't" specifically; "not" still
    // needs both boundaries since it's a genuine standalone word.
    const hasNegation = /n't\b/i.test(before) || /\bnot\b/i.test(before);
    if (hasNegation && /^it\b/i.test(after)) hits.push(s);
  }
  return hits.length >= 2 ? hits.slice(0, 6) : null;
};

// Real, deterministic detector for 3+ consecutive paragraphs that share a
// near-identical shape (sentence count within 1 of the previous paragraph in
// the run, word count within 15%) - the paragraph-level counterpart to
// findTricolonSymmetry above. Catches templated-paragraph runs (e.g. every
// item in a list of options getting an identically-shaped paragraph) that
// sentence-level tricolon detection can't see, since no single sentence
// repeats a rule-of-three - the whole paragraph structure repeats instead.
const findParagraphSymmetry = (text) => {
  const paragraphs = splitParagraphs(text);
  if (paragraphs.length < 3) return null;
  const meta = paragraphs.map(p => {
    const sentences = (p.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
    return { sentenceCount: sentences.length, words: p.split(/\s+/).filter(Boolean).length };
  });
  for (let start = 0; start < paragraphs.length - 2; start++) {
    const run = [start];
    for (let i = start + 1; i < paragraphs.length; i++) {
      const a = meta[run[run.length - 1]], b = meta[i];
      const sentClose = Math.abs(a.sentenceCount - b.sentenceCount) <= 1;
      const wordClose = a.words > 0 && Math.abs(a.words - b.words) / a.words <= 0.15;
      if (sentClose && wordClose && b.sentenceCount >= 2) run.push(i);
      else break;
    }
    if (run.length >= 3) return { indices: run, paragraphs: run.map(i => paragraphs[i]) };
  }
  return null;
};

// Picks concrete sentences for the burstiness cleanup prompt to cite - naming
// exact content, the same proven pattern as findRepeatedOpeners/
// findTricolonSymmetry above, rather than leaving "which sentences" up to the
// model. A vague "wherever it reads naturally" version of this prompt was
// live-tested and found unreliable even at temperature 0 (the model's
// deterministic choice was simply to return the text unchanged) - naming the
// longest sentence as a required split target and two others as a required
// merge target is what actually got a verified, measurable fix.
const pickBurstinessTargets = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  if (sentences.length < 3) return null;
  const withLen = sentences.map((s, i) => ({ s, i, words: s.split(/\s+/).filter(Boolean).length }));
  const splitTarget = withLen.reduce((a, b) => (b.words > a.words ? b : a));
  const mergeTargets = withLen.filter(x => x.i !== splitTarget.i).slice(0, 2).map(x => x.s);
  return mergeTargets.length === 2 ? { splitTarget: splitTarget.s, mergeTargets } : null;
};

// Real, deterministic detector for average sentence length running too high
// across the WHOLE piece - distinct from the burstiness check above, which
// only measures VARIANCE (a piece can have great variance while its average
// is still too high, e.g. mixing 10- and 45-word sentences). Confirmed
// directly against a real GPTZero report citing exact published research
// figures: AI-typical average is 29.2 words/sentence vs. a 23.2-word human
// baseline, plus a separate finding that AI text has very few (2.4%) genuinely
// short 3-8 word sentences.
// Threshold tightened 2026-09-17 (was 26) per explicit direction: keep the
// average under 25, targeting the 23.2 human baseline specifically, not just
// "not egregiously long." Deliberately NOT paired with a mechanical floor
// backstop that would force short output back up - the one confirmed-human
// reference text this whole detection system is calibrated against sits at
// 15.5 words/sentence, well below even 23, so a floor around the target
// would flag and rewrite genuinely good, naturally terse human-style writing
// as if it were a problem. The target is enforced at generation time instead
// (see buildSystemPrompt's SENTENCE LENGTH TARGET rule) - a ceiling-only
// backstop here, a target-not-floor instruction in the prompt.
const findLongSentenceOvershoot = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  if (sentences.length < 4) return null;
  const withLen = sentences.map(s => ({ s, words: s.split(/\s+/).filter(Boolean).length }));
  const avg = withLen.reduce((a, b) => a + b.words, 0) / withLen.length;
  if (avg < 25) return null;
  const targets = [...withLen].sort((a, b) => b.words - a.words).slice(0, 3).map(x => x.s);
  return { avg: Math.round(avg * 10) / 10, targets };
};

// The floor counterpart to findLongSentenceOvershoot above - deliberately
// NOT built alongside it (see that function's comment on why a floor near
// the 23-word target would risk flagging the confirmed-human reference
// text, which sits at 15.5). Built after live-testing the new SENTENCE
// LENGTH TARGET prompt rule proved the concern real but the threshold
// wrong: real output from `aggressive` mode landed at 9.1 words/sentence
// on the first draft, and even after 2 full rounds of the EXISTING
// choppyRun backstop, only climbed to 11.0 then 12.85 - still well below
// both the 23 target AND the 15.5 human reference. That gap (real observed
// output at 9-13, confirmed-human reference at 15.5) gives real margin to
// calibrate a floor that catches the genuine problem without ever touching
// the reference - set to 13, comfortably below 15.5, comfortably above
// where real bad output has been measured landing.
const findShortSentenceUndershoot = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  if (sentences.length < 4) return null;
  const withLen = sentences.map((s, i) => ({ s, i, words: s.split(/\s+/).filter(Boolean).length }));
  const avg = withLen.reduce((a, b) => a + b.words, 0) / withLen.length;
  if (avg >= 13) return null;
  // Pairs the shortest sentences with their immediate next sentence as
  // concrete merge targets - same "name exact targets, don't leave it
  // abstract" pattern as pickBurstinessTargets/findLongSentenceOvershoot.
  const pairs = [...withLen].sort((a, b) => a.words - b.words)
    .filter(x => x.i < withLen.length - 1)
    .slice(0, 3)
    .map(x => ({ first: x.s, second: withLen[x.i + 1].s }));
  return pairs.length ? { avg: Math.round(avg * 10) / 10, pairs } : null;
};

// Sentence-level backstop for calcFunctionWordScore above - names specific
// offending sentences rather than leaving "loosen it up" to the model's
// judgment, matching this pipeline's established pattern (pickBurstinessTargets,
// findLongSentenceOvershoot). Threshold and "3+ before firing" both
// calibrated against real data, not guessed: at fnRatio < 0.30, only 1.3%
// of sentences in the confirmed-human reference text fall below it (2 out
// of 77, i.e. noise - a one-off is normal), while 17.9% did in the real
// third-party-flagged AI output this was built to catch (10 out of 56).
// Requires words.length >= 8 - short sentences/headings naturally have few
// function words and aren't a meaningful sample.
const findLowFunctionWordSentences = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const flagged = sentences.map(s => {
    const words = (s.toLowerCase().match(/[a-z']+/g) || []);
    if (words.length < 8) return null;
    const fnRatio = words.filter(w => FUNCTION_WORDS.has(w)).length / words.length;
    return fnRatio < 0.30 ? s : null;
  }).filter(Boolean);
  return flagged.length >= 3 ? flagged.slice(0, 5) : null;
};

// Targeted cleanup pass: brings the reading level down without otherwise
// changing meaning, structure, or voice - the enforcement backstop behind the
// prompt's reading-level ceiling, for when the model overshoots it anyway.
// Confirmed via real timing data that this pass was a major, avoidable
// latency driver: overshooting PAST the floor (not just under the ceiling)
// triggered expandShortSentences to correct back up, which then reintroduced
// patterns needing a full recleanup - three extra sequential calls (59s of
// a 110s total revoice) caused by one pass not aiming precisely enough.
// Now takes the full {min, max, label} range and explicitly targets the
// MIDPOINT, not just "below the ceiling" - reduces the chance of
// overshooting into floor-violation territory in the first place.
const simplifyReadability = async (text, readingTarget, hasLocks) => {
  const midpoint = Math.round((readingTarget.min + readingTarget.max) / 2);
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "simplify_readability",
      temperature: 0, // a specific required edit, not creative writing
      messages: [
        {
          role: "system",
          content: `You are doing a targeted simplification pass on an already-finished piece of writing. Its reading level is currently too high. Bring it down to land near a grade ${midpoint} Flesch-Kincaid reading level specifically - not just "under grade ${readingTarget.max}." Overshooting too far down (well below grade ${readingTarget.min}) is just as much a problem as staying too high, since it then needs a separate pass to raise it back up - aim for the middle of the grade ${readingTarget.min}-${readingTarget.max} range, not either edge. Use shorter sentences and more common, everyday words in place of complex or rare ones. Do not change the meaning, facts, tone, or overall structure, and do not shorten or lengthen it significantly. Output ONLY the complete simplified text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Picks up to 8 short-sentence expansion targets for the reading-level floor
// backstop below - the shortest sentences in the piece. Live-tested through
// several sizes before landing here: 5 targets barely moved the grade on a
// genuinely choppy piece (two full passes only nudged 3.0 to 3.3); 16+
// targets in one pass either got silently ignored (model just echoed the
// text back unchanged) or, with a more forceful instruction, caused the
// model to insert expanded versions ALONGSIDE the originals instead of
// replacing them, nearly doubling the word count with duplicated content.
// 8 targets with the "replace in place, don't duplicate" instruction below
// was the largest batch that stayed reliable - the outer loop in
// handleHumanize re-picks and re-runs this on whatever's still short.
const pickShortSentenceTargets = (text) => {
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  if (sentences.length === 0) return null;
  const withMeta = sentences.map((s, i) => ({ s, i, words: s.split(/\s+/).filter(Boolean).length }));
  const short = [...withMeta].sort((a, b) => a.words - b.words).slice(0, 8).map(x => x.s);
  return short.length ? short : null;
};

// Targeted cleanup pass: expands the shortest, most fragment-like sentences
// so the piece's Flesch-Kincaid grade clears the audience's reading-level
// floor, without touching anything else - the counterpart to
// simplifyReadability's ceiling enforcement above, which only ever pushed
// grade level down, never up. "Replace in place, don't duplicate" and "must
// replace every one" are both load-bearing instructions here, not filler -
// live-testing found the model silently no-ops without the second, and
// duplicates content alongside the original without the first (see comment
// on pickShortSentenceTargets above).
const expandShortSentences = async (text, targets, hasLocks) => {
  const list = targets.map((t, i) => `${i + 1}. "${t}"`).join("\n");
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "expand_short_sentences",
      messages: [
        {
          role: "system",
          content: `You are doing a targeted cleanup pass on an already-finished piece of writing. Its reading level has dropped too low - these sentences are too short and fragment-like:\n${list}\n\nFor each one, REPLACE it in place with a single longer sentence - merge in a CONCRETE detail, reason, consequence, or example that is already stated elsewhere in the piece. Do not invent a new fact, statistic, or specific example that isn't already present elsewhere in the piece.\n\nHARD RULE: the added material must be concrete, not a general evaluative comment. Vague "value statement" padding - calling something "invaluable," "a deeply personal choice," "not really living," "a significant barrier," or tacking on a generic closer like "both options have their merits" or "this is your personal journey" - is exactly the kind of hedge-heavy, abstract filler real detectors flag as AI-written. It reads as commentary bolted onto the piece, not part of it.\n  WRONG (abstract value-add, reads as AI-generated commentary): "If you're not sure you'll be in the same spot for five to seven years, rent. The flexibility that comes with renting can be invaluable in today's fast-paced, uncertain world."\n  RIGHT (same short sentence, extended with a concrete reason already in the piece): "If you're not sure you'll be in the same spot for five to seven years, rent, since selling early just hands your equity to realtor fees and closing costs."\n\nThis must end up as ONE sentence, not two - do not insert an extra sentence alongside the original, replace it entirely, and do not add any new sentence anywhere else in the piece. Keep the tone casual. Target roughly 12-18 words per replacement - stay in that range, don't overcorrect into much longer sentences, which just pushes the reading level too high again and creates another round-trip. You must replace every single one of these listed sentences - none should remain as a short fragment. Do not change any other sentence, do not alter meaning, structure, or voice elsewhere, do not duplicate any sentence. Output ONLY the complete corrected text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Consolidates what used to be up to 6 separate sequential targeted-cleanup
// round-trips (banned words, sentence-level opener, paragraph-level opener,
// tricolon, paragraph-level symmetry, burstiness) into ONE combined call.
// Confirmed live that chaining these as separate sequential passes was the
// real driver behind multi-minute revoices on longer text - each pass only
// ever cites a handful of specific, concrete issues (not the "too many
// similar targets in one prompt" failure mode found with the reading-floor
// fix), so batching the citations from all six into one prompt is safe and
// cuts the round-trip count dramatically. Detectors themselves are free
// (pure JS, no API call) - only the actual rewrite costs a request, and only
// when something was actually found.
// Pure detection - runs every calibrated pattern-detector against a piece of
// text and returns the same specific, worked-example issue descriptions
// runConsolidatedCleanup has always used, without making an API call. Split
// out so the same detection can feed the NEW single-pass rewrite (fold
// findings into the one generation call up front) as well as the original
// post-hoc cleanup pass (fix findings after the fact) - one detection
// implementation, two different places it gets used.
// Deterministic guard against a real, live-confirmed failure mode
// (2026-09-07): with a Voice Profile active, the model can return the
// writing sample itself - or otherwise drift onto unrelated content -
// instead of transforming the actual input. A long, dense system prompt
// with an ~1000-word "REAL WRITING SAMPLE" embedded in the middle of it
// is exactly the kind of thing a fast-tier model can lose track of.
// Measures how much of the SOURCE's distinctive vocabulary survived into
// the output - a legitimate rewrite, however heavily reworded, still
// carries over the concrete topic-specific nouns (a money-habits article
// stays about subscriptions/budgets/coffee even after a full rewrite);
// unrelated content shares almost none of them. Length>=5 filters out
// most function words without needing a full stopword list; the short
// exclusion set below catches the common length>=5 words that slip
// through and carry no topical meaning.
const CONTENT_OVERLAP_STOPWORDS = new Set([
  "about", "after", "again", "their", "there", "which", "would", "could",
  "should", "while", "these", "those", "where", "being", "doesn't",
  "because", "before", "still", "every", "other", "through", "without",
  "really", "actually", "something", "someone", "anything", "people",
  "think", "first", "start", "doing", "never", "always", "instead",
  "toward", "towards", "little", "enough", "around", "today", "during",
]);
const extractContentWords = (text) => {
  const words = (text || "").toLowerCase().match(/[a-z']+/g) || [];
  return new Set(words.filter(w => w.length >= 5 && !CONTENT_OVERLAP_STOPWORDS.has(w)));
};
// Recall-oriented on purpose: what fraction of the SOURCE's vocabulary
// made it into the output. Threshold (0.2) is a first-pass calibration
// from one confirmed failure case (near-zero overlap) plus reasoning
// about how much topic vocabulary a real rewrite should retain - not yet
// calibrated against a range of real legitimate rewrites the way this
// project's other detectors were. Revisit if it ever false-positives on
// a genuine heavy rewrite.
const MIN_CONTENT_OVERLAP = 0.2;
const contentOverlapRatio = (sourceText, outputText) => {
  const sourceWords = extractContentWords(sourceText);
  if (sourceWords.size === 0) return 1; // nothing to compare - don't false-positive
  const outputWords = extractContentWords(outputText);
  let matched = 0;
  sourceWords.forEach(w => { if (outputWords.has(w)) matched++; });
  return matched / sourceWords.size;
};

// Live-confirmed failure (2026-09-15), a real severe one - a genuine
// user's article got a fabricated opening paragraph (an unrelated
// autobiographical anecdote about being an engineer with writer's block)
// that turned out to be a near-verbatim echo of the FIRST PARAGRAPH of
// their active Voice Profile's writing sample. isValidOutput above only
// catches the case where the ENTIRE output is the sample (near-zero
// overlap with the source) - this is the partial version: only the
// OPENING is contaminated, so overall source-overlap still passes since
// the rest of the piece is a genuine rewrite. The literal few-shot
// framing of the style sample ("write as if you ARE this voice") makes
// the model treat it as content to continue, not just a style reference -
// this checks specifically for that failure mode: does the output's
// OPENING share content vocabulary with the SAMPLE far more than with the
// actual source/topic it's supposed to be about. Verified against the
// real failure case: 100% of the leaked opening's content words matched
// the sample, 0% matched the source.
const findSampleContentBleed = (outputText, styleSample, sourceText) => {
  if (!styleSample) return null;
  const opening = (outputText.split(/\n{2,}/)[0] || outputText.slice(0, 400));
  const openingWords = extractContentWords(opening);
  if (openingWords.size < 4) return null; // too short a sample to judge
  const sampleWords = extractContentWords(styleSample);
  const sourceWords = extractContentWords(sourceText);
  let sampleMatches = 0, sourceMatches = 0;
  openingWords.forEach(w => {
    if (sampleWords.has(w)) sampleMatches++;
    if (sourceWords.has(w)) sourceMatches++;
  });
  const sampleRatio = sampleMatches / openingWords.size;
  const sourceRatio = sourceMatches / openingWords.size;
  return (sampleRatio >= 0.25 && sampleRatio > sourceRatio * 1.5) ? { sampleRatio, sourceRatio } : null;
};

const detectIssues = (detectionText, styleText, formality, isClientVoice) => {
  const flagged = findBannedWords(detectionText);
  const repeated = findRepeatedOpeners(detectionText);
  const paraOpener = findParagraphOpenerViolations(detectionText);
  const tricolon = findTricolonSymmetry(detectionText);
  const paraSymmetry = findParagraphSymmetry(detectionText);
  const burstiness = calcBurstiness(detectionText);
  const burstTargets = burstiness?.lowBurstiness ? pickBurstinessTargets(detectionText) : null;
  const lengthOvershoot = findLongSentenceOvershoot(detectionText);
  const lengthUndershoot = findShortSentenceUndershoot(detectionText);
  const lowFunctionWordHits = findLowFunctionWordSentences(detectionText);
  const flourishHits = findRhetoricalFlourishSentences(detectionText);
  const fragmentHits = findFragmentStacking(detectionText);
  const formalHits = findFormalPhrases(detectionText);
  // A real, terse/clipped writing style can genuinely stack short sentences
  // on purpose ("Simple. Boring. Correct.") - confirmed live during a
  // generalization test with a deliberately clipped style sample, where this
  // backstop would have forcibly merged exactly the pattern that made the
  // sample's own voice recognizable. Originally skipped whenever ANY
  // personal style sample was active, deferring entirely to styleRules'
  // own judgment - but live-confirmed (2026-09-15) that this leaves a user
  // with a PERMANENTLY active profile with zero protection against this
  // pipeline's single most Pangram-evidenced AI tell, for every job,
  // forever, regardless of whether their actual sample calls for it: a
  // real user's own sample tested clean of this pattern, but their output
  // still had 3 real consecutive-short-sentence runs survive completely
  // uncaught straight through to an 88% GPTZero score. Now only deferred
  // when the SAMPLE ITSELF exhibits the same pattern - a genuinely terse
  // writer's voice stays protected, but an active profile no longer
  // blanket-disables this check regardless of whether it's actually terse.
  const sampleHasChoppyRun = styleText ? !!findChoppyRun(styleText) : false;
  const choppyRun = sampleHasChoppyRun ? null : findChoppyRun(detectionText);
  const twistRun = findTwistRun(detectionText);
  const negateReframeSemicolon = findNegateReframeSemicolon(detectionText);
  const contractionMismatch = findContractionMismatch(detectionText, styleText, formality, isClientVoice);
  const vagueWordHits = findOverusedVagueWords(detectionText);
  const intensifierHits = findOverusedIntensifiers(detectionText);
  const genericWordHits = findOverusedGenericWords(detectionText);
  const aiVocabHits = findOverusedAiVocabulary(detectionText);
  const notOnlyHits = findNotOnlyButAlso(detectionText);
  const summaryClosing = findSummaryClosing(detectionText);
  const numberedHeadings = findNumberedHeadings(detectionText);
  const titledHeadings = findTitledHeadings(detectionText);

  const issues = [];
  const ran = [];
  // Pushed first - confirmed via two separate real Pangram 100%-AI results
  // to be the single highest-impact issue this pipeline can catch, so it
  // gets priority if the model only partially complies with a long prompt.
  if (choppyRun) {
    const runsText = choppyRun.map((run, i) => `Run ${i + 1} (${run.length} sentences): ${run.map(s => `"${s}"`).join(" / ")}`).join("\n");
    issues.push(`- Runs of 3+ consecutive short (under 10 words), grammatically clean sentences - "polished choppiness," a strong and specific AI tell. Every run below needs fixing, not just one:\n${runsText}\nFor each run, merge at least half of its sentences into longer ones using "and", "but", or a comma instead of a period. Do not leave 3 or more short clean sentences in a row anywhere in the piece. Every fact must survive.`);
    ran.push("choppyRun");
  }
  if (twistRun) {
    // Prescribes a SPECIFIC technique per sentence rather than leaving "vary
    // the method" open-ended - live-tested that the open-ended version got
    // almost entirely ignored (the model made one trivial edit and left the
    // actual pattern intact), the same "be mechanically specific, not just
    // descriptive" lesson that made every other targeted pass in this
    // pipeline reliable.
    const techniques = [
      'rewrite it to open with a subordinate clause ("because", "when", or "since") instead of using a trailing ", but/and" twist',
      'rewrite it as a single plain sentence with no comma-conjunction twist at the end at all - state the result directly',
      'split it into two separate shorter sentences instead of joining the two halves with ", but/and"',
    ];
    const runsText = twistRun.map((run, ri) => {
      const [first, ...rest] = run;
      const restInstructions = rest.map((s, i) => `  - "${s}" -> ${techniques[i % techniques.length]}`).join("\n");
      return `Run ${ri + 1}: keep "${first}" exactly as it is, unchanged. For the rest of this run:\n${restInstructions}`;
    }).join("\n");
    issues.push(`- Runs of 3+ consecutive sentences that ALL follow the same "[action], but/and [short twist]" shape - a uniform, detectable AI pattern even though the sentences aren't short. Fix each listed sentence using the SPECIFIC technique given for it, not your own choice:\n${runsText}\nEvery fact must survive.`);
    ran.push("twistRun");
  }
  if (negateReframeSemicolon) {
    issues.push(`- These sentences repeat the same semicolon-joined "doesn't/isn't X; it's Y" contrastive-reframe template - a specific, detectable AI rhetorical pattern: ${negateReframeSemicolon.map(s => `"${s}"`).join(" / ")}. Break this template in each one - do not just swap words while keeping the same shape.\nWORKED EXAMPLE of the required transformation:\nBEFORE: "This isn't about willpower; it's about building better systems."\nAFTER (one option): "This isn't about willpower. Building better systems is what actually moves the needle."\nAFTER (another option): "Systems beat willpower, every time."\nApply this exact kind of transformation to each sentence listed above - they must come out looking structurally different from each other too, not all fixed the same way. Every fact must survive.`);
    ran.push("negateReframeSemicolon");
  }
  if (flagged.length) {
    issues.push(`- Banned/AI-flagged words still present: ${flagged.join(", ")}. Replace each with a natural alternative not on this list.`);
    ran.push("bannedWords");
  }
  if (repeated) {
    issues.push(`- Three consecutive sentences share the same opening words ("${repeated.opener}"): ${repeated.sentences.map(s => `"${s}"`).join(" / ")}. Rewrite these so they no longer share that opener.`);
    ran.push("sentenceOpener");
  }
  if (paraOpener) {
    issues.push(`- Multiple paragraphs open ${paraOpener.pattern}, specifically: ${paraOpener.offending.map(p => `"${p}"`).join(" / ")}. Rewrite ONLY the opening sentence of each so it no longer matches that shared pattern.`);
    ran.push("paragraphOpener");
  }
  if (tricolon) {
    if (tricolon.hits) {
      // Live-confirmed (2026-09-10): this bare instruction survived TWO
      // consecutive fix attempts on the same real output, unlike its
      // `clustered` sibling below - the difference is the worked example
      // that sibling has and this one never got. Same fix, generalized to
      // however many sentences `hits` found instead of exactly 2.
      issues.push(`- Repeated matched three-item lists (tricolons) - a specific, detectable AI rhetorical pattern, recurring across this piece: ${tricolon.hits.map(h => `"${h.sentence}"`).join(" / ")}. You must eliminate the list SHAPE in each one, not just swap a word.\nWORKED EXAMPLE of the required transformation:\nBEFORE: "The solution is fast, reliable, and effective."\nAFTER (one option): "The solution is fast and reliable, and it actually works when you need it to."\nAFTER (another option): "It's fast. Reliable. And it just plain works."\nThe point: break the "X, Y, and Z" comma rhythm entirely - either fold it into a longer flowing sentence with no third comma-item, or break it apart into short standalone fragments. Do not just replace individual words while keeping the same three-item shape.\nApply this exact kind of transformation to every sentence listed above (they must come out looking structurally different from each other too, not all fixed the same way). Every fact must survive.`);
    }
    if (tricolon.clustered) {
      // Abstract "rewrite this" instructions were live-tested and ignored
      // outright (the model left both sentences byte-for-byte unchanged) -
      // a worked before/after example of the exact required transformation,
      // the same lesson that made findTwistRun's fix reliable, is what
      // actually gets this followed.
      issues.push(`- Two sentences below each end in a clean comma-separated three-item list ("X, Y, and Z") - a specific, detectable AI rhetorical pattern. You must eliminate the list SHAPE in both, not just swap a word.\nWORKED EXAMPLE of the required transformation:\nBEFORE: "It opens doors to cultures, jobs, and people you'd never otherwise encounter."\nAFTER (one option): "It opens doors to cultures and jobs you'd never otherwise encounter, and puts you face to face with people you'd never meet any other way."\nAFTER (another option): "It opens doors. New cultures. Jobs you'd never have found. People you'd never otherwise meet."\nThe point: break the "X, Y, and Z" comma rhythm entirely - either fold it into a longer flowing sentence with no third comma-item, or break it apart into short standalone fragments. Do not just replace individual words while keeping the same "X, Y, and Z" shape.\nApply this exact kind of transformation to BOTH of the following (they must come out looking structurally different from each other too):\n1. "${tricolon.clustered[0]}"\n2. "${tricolon.clustered[1]}"\nEvery fact must survive.`);
    }
    if (tricolon.anaphora) {
      issues.push(`- The word "${tricolon.anaphora.word}" is repeated ${tricolon.anaphora.count} times as a rhetorical list-item lead-in within one sentence, a detectable AI parallelism tell: "${tricolon.anaphora.sentence}". Rewrite this sentence so "${tricolon.anaphora.word}" doesn't repeat that way - state the items in a plainer list or split into separate sentences. Every fact must survive.`);
    }
    ran.push("tricolon");
  }
  if (paraSymmetry) {
    issues.push(`- ${paraSymmetry.paragraphs.length} consecutive paragraphs follow the same template/shape: ${paraSymmetry.paragraphs.map(p => `"${p}"`).join(" / ")}. Keep the first as-is; merge the rest into one unevenly-weighted paragraph, preserving every fact.`);
    ran.push("paragraphSymmetry");
  }
  if (burstTargets) {
    issues.push(`- Sentence lengths are too uniform. Split this sentence into two: "${burstTargets.splitTarget}". Merge these two into one: "${burstTargets.mergeTargets[0]}" and "${burstTargets.mergeTargets[1]}".`);
    ran.push("burstiness");
  }
  if (lengthOvershoot) {
    issues.push(`- Average sentence length across this piece is ${lengthOvershoot.avg} words - confirmed against a real GPTZero report, AI-typical average is 29.2 words/sentence vs. a 23.2-word human baseline, and AI text has very few (2.4%) genuinely short 3-8 word sentences. Split each of these long sentences into two: ${lengthOvershoot.targets.map(s => `"${s}"`).join(" / ")}. For each split, make ONE of the two resulting sentences genuinely short - 3 to 8 words - not just moderately shorter. Every fact must survive.`);
    ran.push("lengthOvershoot");
  }
  if (lengthUndershoot) {
    issues.push(`- Average sentence length across this piece is ${lengthUndershoot.avg} words - well below the 23-word human target (confirmed against a real GPTZero report: AI-typical is 29.2, human baseline is 23.2 - this piece has overcorrected into choppiness, which is its own detectable AI tell). Merge each of these sentence pairs into one, joining with "and", "but", or a comma instead of the period between them: ${lengthUndershoot.pairs.map(p => `"${p.first}" + "${p.second}"`).join(" / ")}. Every fact must survive.`);
    ran.push("lengthUndershoot");
  }
  if (lowFunctionWordHits) {
    issues.push(`- These sentences pack in too many content words relative to ordinary connective words (articles, pronouns, "and"/"so"/"but", contractions) - confirmed by a real third-party detector report, low function-word density is a genuine AI tell, separate from word rarity: ${lowFunctionWordHits.map(s => `"${s}"`).join(" / ")}. Rewrite each to add back natural connective tissue - a pronoun, a contraction, a "so"/"and"/"but", a short lead-in like "honestly" or "look" - without losing the specific claim or any fact.\nWORKED EXAMPLE of the required transformation:\nBEFORE (tight, few connectives): "Twenty minutes a day beats one four-hour weekend binge, every single time."\nAFTER: "Honestly, twenty minutes a day is going to beat one of those four-hour weekend binges every time - that's just how it works."\nApply this same kind of loosening to each sentence listed above - add connective tissue, don't just swap individual words. Every fact must survive.`);
    ran.push("lowFunctionWordDensity");
  }
  if (flourishHits) {
    issues.push(`- These sentences stack a justifying clause (because/since/when/while) with an "and" or participial extension, capped by a trailing "that" clause delivering a rhetorical payoff - a strong, specific AI tell: ${flourishHits.map(s => `"${s}"`).join(" / ")}. Rewrite each as two or more plainer, shorter sentences. Drop the trailing "that ..." payoff clause entirely or state it as a separate, simple sentence. Every fact must survive.`);
    ran.push("rhetoricalFlourish");
  }
  if (fragmentHits) {
    issues.push(`- Stacked grammatical sentence fragments (a quantifier noun phrase with no main verb, ending in a relative/participial tail) - a detectable AI rhetorical device: ${fragmentHits.map(s => `"${s}"`).join(" / ")}. Rewrite each as a complete sentence with a real subject and main verb. Every fact must survive.`);
    ran.push("fragmentStacking");
  }
  if (formalHits.length) {
    issues.push(`- Formal/AI-flagged phrases still present: ${formalHits.join(", ")}. Rewrite ONLY the sentences containing these, replacing each phrase with a plainer, more natural alternative.`);
    ran.push("formalPhrases");
  }
  if (vagueWordHits) {
    const wordsText = vagueWordHits.map(h => `"${h.word}" (used ${h.count} times)`).join(", ");
    issues.push(`- Overused generic/low-information words - repeating the same predictable word choice this many times is a detectable AI tell: ${wordsText}. Find roughly half of each word's occurrences and replace them with a more specific, natural-sounding alternative appropriate to that sentence. Leave the rest as-is - don't eliminate the word entirely, just its overuse. Do not change anything else.`);
    ran.push("vagueWords");
  }
  if (intensifierHits) {
    const wordsText = intensifierHits.map(h => `"${h.word}" (used ${h.count} times)`).join(", ");
    issues.push(`- Overused intensifiers - repeating the same vague intensifier this many times is weaker than being specific, and a detectable AI tell: ${wordsText}. Find roughly half of each word's occurrences and either cut it entirely or replace it with a more specific, concrete detail. Leave the rest as-is - don't eliminate the word entirely, just its overuse. Do not change anything else.`);
    ran.push("intensifiers");
  }
  if (genericWordHits) {
    const wordsText = genericWordHits.map(h => `"${h.word}" (used ${h.count} times)`).join(", ");
    issues.push(`- Overused generic filler words - repeating the same low-information word this many times reduces lexical variety, a real detectable AI tell distinct from vocabulary sophistication: ${wordsText}. Find roughly half of each word's occurrences and replace them with a more varied, natural alternative appropriate to that sentence - do NOT reach for a more formal, technical, or unusual word to do it, plain everyday variety is the goal. Leave the rest as-is - don't eliminate the word entirely, just its overuse. Do not change anything else.`);
    ran.push("genericWords");
  }
  if (aiVocabHits) {
    const wordsText = aiVocabHits.map(h => `"${h.word}" (used ${h.count} times)`).join(", ");
    issues.push(`- Currently-trending AI vocabulary - these specific words are disproportionately common in recent LLM output right now (confirmed via Wikipedia's own tracked word list, which updates as models change): ${wordsText}. Find roughly half of each word's occurrences and replace them with a plainer, more ordinary word choice appropriate to that sentence. Leave the rest as-is - don't eliminate the word entirely, just its overuse. Do not change anything else.`);
    ran.push("aiVocabulary");
  }
  if (contractionMismatch) {
    issues.push(`- The active writing-style sample uses essentially no contractions, but this text still has ${contractionMismatch.count} (e.g. ${contractionMismatch.examples.map(c => `"${c}"`).join(", ")}). Expand every contraction to its full form ("don't" -> "do not", "it's" -> "it is") to match the sample's actual register. Do not change anything else about the sentence.`);
    ran.push("contractionMismatch");
  }
  if (notOnlyHits) {
    issues.push(`- The formulaic "not only X, but (also) Y" construction recurs: ${notOnlyHits.map(s => `"${s}"`).join(" / ")}. Rewrite each as a simpler, direct sentence without that scaffold - e.g. "Not only does this save time, but it also saves money" -> "This saves time and money."`);
    ran.push("notOnlyButAlso");
  }
  if (summaryClosing) {
    issues.push(`- The final paragraph closes with a summary/wrap-up phrase ("${summaryClosing.phrase}") - AI text almost always ends by restating what was just said; real writing tends to stop on something open, specific, or unresolved instead: "${summaryClosing.paragraph}". Rewrite this paragraph so it doesn't summarize or restate the piece - end on a concrete detail, a question, or a slight, natural digression instead. Every fact must survive.`);
    ran.push("summaryClosing");
  }
  if (numberedHeadings) {
    issues.push(`- Numbered/labeled section headings are still present - a detectable AI instructional-formatting tell, confirmed across multiple real GPTZero reports: ${numberedHeadings.map(h => `"${h}"`).join(" / ")}. Remove the "Step N:" / number label entirely from each one and rewrite it into a natural sentence that opens straight into its paragraph - not a standalone heading line. Every fact must survive.`);
    ran.push("numberedHeadings");
  }
  if (titledHeadings) {
    issues.push(`- Named, Title-Cased section headings are still present - a detectable AI listicle-formatting tell, confirmed by a real GPTZero report: ${titledHeadings.map(h => `"${h}"`).join(" / ")}. Remove each of these as a standalone heading line entirely and fold what it was labeling straight into the start of the paragraph that follows it, in normal sentence case, not title case. Every fact must survive.`);
    ran.push("titledHeadings");
  }

  return { issues, ran };
};

// ─── FREE ANALYZER (public, no auth, zero LLM tokens) ────────────────────────
// Every signal here is pure JS already used elsewhere in this pipeline - no
// network call, so this can run entirely client-side. Deliberately does NOT
// reuse analyseText: that function's 4 "AI-judged" signals come from a real
// LLM call, which is exactly the cost this free tool exists to avoid.

const FREE_ISSUE_LABELS = {
  choppyRun: "Runs of short, choppy sentences in a row",
  twistRun: "A repetitive \"but/and\" sentence pattern",
  bannedWords: "Overused AI buzzwords",
  sentenceOpener: "Repeated sentence openings",
  paragraphOpener: "Repeated paragraph openings",
  tricolon: "Overly balanced three-item lists",
  paragraphSymmetry: "Paragraphs that all follow the same template",
  burstiness: "Uniform sentence rhythm",
  lengthOvershoot: "Long, uniform sentences",
  rhetoricalFlourish: "Overly polished rhetorical sentence patterns",
  fragmentStacking: "Stacked sentence fragments",
  formalPhrases: "Overly formal or hedging phrases",
  vagueWords: "Overused generic words",
  intensifiers: "Overused intensifiers (\"very\", \"really\", etc.)",
  genericWords: "Overused generic filler words",
  aiVocabulary: "Currently-trending AI vocabulary",
  lowFunctionWordDensity: "Too few natural connective words (function-word density)",
  notOnlyButAlso: "A formulaic \"not only... but also\" construction",
  summaryClosing: "A tidy, AI-style summary ending",
  numberedHeadings: "AI-style numbered section headings",
  titledHeadings: "AI-style named section headings",
};

// Plain-English, one-line explanation for each pattern in FREE_ISSUE_LABELS -
// keyed by the LABEL TEXT (what actually lands in analyzeFree's `patterns`
// array), not the internal detector key. Free-page-only: the paid app's
// AnalysisPanel doesn't need this, visitors deciding whether to sign up do.
const FREE_PATTERN_EXPLANATIONS = {
  "Runs of short, choppy sentences in a row": "Three or more short, grammatically clean sentences in a row is a distinctive AI rhythm - human writing tends to run sentences together more.",
  "A repetitive \"but/and\" sentence pattern": "The same \"...but/and [short twist]\" sentence shape repeating is a uniform pattern AI writing tends to fall into.",
  "Overused AI buzzwords": "Specific words AI overuses heavily (like \"leverage\", \"streamline\", \"delve\") appear repeatedly in your text.",
  "Repeated sentence openings": "Consecutive sentences starting with the same words - a subtle repetition AI text falls into.",
  "Repeated paragraph openings": "Multiple paragraphs open the same way - a structural pattern readers (and detectors) notice.",
  "Overly balanced three-item lists": "AI frequently groups ideas into sets of three with equal weight. Human writers are less symmetrical.",
  "Paragraphs that all follow the same template": "Consecutive paragraphs match each other in length and shape - AI often produces uniformly-sized sections.",
  "Uniform sentence rhythm": "Sentence lengths across the piece are too consistent - human writing naturally varies more.",
  "Long, uniform sentences": "Average sentence length is unusually high and consistent - a known AI-typical pattern.",
  "Overly polished rhetorical sentence patterns": "Sentences that stack a justifying clause with a dramatic payoff - a polished rhetorical shape AI leans on.",
  "Stacked sentence fragments": "Runs of incomplete sentence fragments used as a stylistic device - a tell AI writing falls into.",
  "Overly formal or hedging phrases": "Phrases like \"it is important to note\" and \"moreover\" are AI staples.",
  "Overused generic words": "The same generic, low-information words (like \"good\", \"thing\", \"way\") repeated too often.",
  "Overused intensifiers (\"very\", \"really\", etc.)": "The same intensifying word repeated too often instead of being specific.",
  "Overused generic filler words": "Generic filler words (like \"various\", \"significant\") repeated instead of being replaced with something more specific.",
  "Currently-trending AI vocabulary": "Words like \"crucial\", \"pivotal\", or \"showcasing\" that current LLMs reach for disproportionately often, repeated enough to be a real signal rather than coincidence.",
  "Too few natural connective words (function-word density)": "Certain sentences pack in too many content words and too few natural connecting words, making them feel clipped and mechanical.",
  "A formulaic \"not only... but also\" construction": "The \"not only X, but also Y\" sentence template repeating - a formulaic construction AI reaches for often.",
  "A tidy, AI-style summary ending": "Ending on a neat restatement of what was just said - real writing often ends on something more open or specific.",
  "AI-style numbered section headings": "Numbered headings like \"Step 1:\" are a formatting pattern strongly associated with AI-generated instructional content.",
  "AI-style named section headings": "Named, Title-Case section headings are a listicle-style formatting pattern common in AI writing.",
};

// Preloaded on first load so a visitor with no text of their own still sees
// a real, alarming result immediately (see humankind-free-page-improvements.md
// Fix 2) - deliberately written to trip banned words, formal phrases, and
// low lexical variety at once.
const FREE_EXAMPLE_TEXT = `Artificial intelligence has fundamentally transformed the way we approach content creation in the modern digital landscape. It is important to note that leveraging these cutting-edge tools can significantly enhance productivity and streamline workflows. Moreover, AI-driven solutions provide actionable insights that empower creators to optimize their output and ensure that their content resonates with target audiences. Furthermore, the dynamic nature of these innovations underscores the need for robust strategies that foster engagement and drive meaningful results.`;

// Fix 5's generic 5-band scheme, plus the 3 tighter per-signal schemes Fix 4
// specifies (perplexity, burstiness, function-word density each read
// differently at the same raw number, so one universal band table would
// misrepresent at least two of them).
const FREE_RISK_BANDS = {
  generic: [[20, "Clean", "#3a7a3a"], [40, "Low risk", "#3a7a3a"], [60, "Caution", "#c8a04a"], [80, "High risk", "#b05050"], [101, "Very high", "#b05050"]],
  perplexity: [[20, "Good", "#3a7a3a"], [40, "Moderate", "#3a7a3a"], [60, "Caution", "#c8a04a"], [101, "High Risk", "#b05050"]],
  burstiness: [[20, "Good", "#3a7a3a"], [40, "Caution", "#c8a04a"], [101, "High Risk", "#b05050"]],
  functionWord: [[15, "Good", "#3a7a3a"], [30, "Caution", "#c8a04a"], [101, "Risk", "#b05050"]],
};
const freeRiskLabel = (score, bandKey = "generic") => {
  const bands = FREE_RISK_BANDS[bandKey] || FREE_RISK_BANDS.generic;
  for (const [max, label, color] of bands) if (score <= max) return { label, color };
  const last = bands[bands.length - 1];
  return { label: last[1], color: last[2] };
};

// Overall weighted verdict (Fix 3) - same methodology as the paid app's
// computeLocalAiTellScore/analyseText.overallAI for consistency: structural
// pattern count is weighted 2x since it's the bucket most directly tied to
// real detector-confirmed patterns, the rest averaged once each.
const FREE_VERDICT_TIERS = [
  { max: 15, label: "Likely Human", color: "#3a7a3a", icon: "✓", copy: "Your text shows mostly human writing patterns. It is unlikely to be flagged by standard AI detectors - though no analysis is a guarantee." },
  { max: 30, label: "Low AI Risk", color: "#3a7a3a", icon: "~", copy: "Your text has some AI-like patterns but mostly reads as human-written. A few issues are worth addressing before submitting or publishing." },
  { max: 50, label: "Mixed Signals", color: "#c8a04a", icon: "!", copy: "Your text contains a mix of human and AI patterns. Some detectors may flag it. The issues below are worth fixing." },
  { max: 75, label: "High AI Risk", color: "#b05050", icon: "✗", copy: "Your text shows strong AI detection signals. It is likely to be flagged by GPTZero, Turnitin, and similar detectors. The issues below explain why." },
  { max: 101, label: "Very Likely AI", color: "#b05050", icon: "✗", copy: "Your text reads as AI-generated to automated detectors. Multiple strong signals were found. See the issues below for what needs fixing." },
];
const computeFreeVerdict = (result) => {
  const bannedScore = Math.min(100, result.bannedCount * 8);
  const emDashScore = Math.min(100, result.emDashCount * 12);
  const uniformScore = 100 - result.burstinessScore;
  const structScore = Math.min(100, result.patterns.length * 30);
  const scores = [bannedScore, emDashScore, result.perplexityScore, uniformScore, result.functionWordScore, structScore, structScore];
  const overall = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const tier = FREE_VERDICT_TIERS.find(t => overall <= t.max) || FREE_VERDICT_TIERS[FREE_VERDICT_TIERS.length - 1];
  return { overall, ...tier };
};

const analyzeFree = (text) => {
  const words = wordCount(text);
  const sentences = (text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || []).map(s => s.trim()).filter(Boolean);
  const paragraphs = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const { ran } = detectIssues(text, "");
  return {
    words,
    sentenceCount: sentences.length,
    paragraphCount: paragraphs.length,
    avgSentenceLen: sentences.length ? Math.round((words / sentences.length) * 10) / 10 : 0,
    readability: calcReadability(text),
    burstiness: calcBurstiness(text),
    perplexityScore: calcPerplexityScore(text),
    burstinessScore: calcBurstinessScore(text),
    functionWordScore: calcFunctionWordScore(text),
    bannedCount: countBannedWords(text),
    bannedWordsFound: findBannedWords(text),
    emDashCount: countEmDashes(text),
    patterns: ran.map(key => FREE_ISSUE_LABELS[key]).filter(Boolean),
  };
};

// Runs detectIssues, then makes the actual cleanup API call if it found
// anything - the original post-hoc pass, now a thin wrapper over the shared
// detector above.
const runConsolidatedCleanup = async (lockedText, detectionText, hasLocks, styleText, formality, isClientVoice) => {
  const { issues, ran } = detectIssues(detectionText, styleText, formality, isClientVoice);
  if (issues.length === 0) return { text: lockedText, ran };

  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "consolidated_cleanup",
      temperature: 0, // specific required edits, not creative writing
      messages: [
        {
          role: "system",
          content: `You are doing a targeted cleanup pass on an already-finished piece of writing, fixing several specific detectable AI patterns in one pass:\n\n${issues.join("\n\n")}\n\nMake ONLY these specific fixes. Do not change any other sentence or paragraph, do not alter meaning, structure, length, or voice elsewhere. Output ONLY the complete corrected text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: lockedText },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return { text: data.message?.content?.trim() || lockedText, ran };
};

// Dedicated single-category safety net for banned words. Before the
// single-pass architecture rewrite, bannedWords had its own standalone
// full-retry loop (`selfCorrect`); that function no longer exists - it got
// folded into runConsolidatedCleanup's shared issue list along with ~19
// other categories. Live-confirmed (2026-09-15, real user report): with
// that many categories competing for the model's attention in one prompt,
// it can partially comply and silently skip specific ones - banned words
// survived straight through the 2-attempt consolidated loop to the final
// score. Isolating the fix to its own single-issue prompt removes the
// category competition entirely, restoring the old guarantee without
// reintroducing a second full shared-cleanup round-trip.
const fixBannedWordsOnly = async (text, flagged, hasLocks) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "banned_words_cleanup",
      temperature: 0,
      messages: [
        {
          role: "system",
          content: `Banned/AI-flagged words still present in this text: ${flagged.join(", ")}. Replace EVERY occurrence of each with a natural alternative not on this list. Do not change anything else - no other wording, structure, or meaning.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""} Output ONLY the complete corrected text with no labels, headers, or commentary.`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Dedicated single-category safety net for low function-word density,
// mirroring fixBannedWordsOnly above. Live-tested (2026-09-15): even an
// ISOLATED attempt at this fix (no competing categories at all) only
// achieved partial compliance on the real flagged text (5 flagged
// sentences down to 3, not 0) - worse odds inside the shared
// runConsolidatedCleanup prompt, where this competes with up to 20 other
// categories. One more isolated attempt, same pattern as bannedWords,
// gives it a second real chance without the category competition.
const fixLowFunctionWordDensityOnly = async (text, flagged, hasLocks) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: OLLAMA_MODEL,
      stream: false,
      job_type: "function_word_cleanup",
      temperature: 0,
      messages: [
        {
          role: "system",
          content: `These sentences pack in too many content words relative to ordinary connective words (articles, pronouns, "and"/"so"/"but", contractions) - low function-word density is a genuine, real-detector-confirmed AI tell, separate from word rarity: ${flagged.map(s => `"${s}"`).join(" / ")}. Rewrite EVERY ONE of these to add back natural connective tissue - a pronoun, a contraction, a "so"/"and"/"but", a short lead-in like "honestly" or "look" - without losing the specific claim or any fact.\nWORKED EXAMPLE of the required transformation:\nBEFORE (tight, few connectives): "Twenty minutes a day beats one four-hour weekend binge, every single time."\nAFTER: "Honestly, twenty minutes a day is going to beat one of those four-hour weekend binges every time - that's just how it works."\nApply this same kind of loosening to EVERY sentence listed above, not just some of them - do not skip any. Do not change anything else.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""} Output ONLY the complete corrected text with no labels, headers, or commentary.`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

// Every pass in this pipeline, including every fix above, runs on the same
// model (OLLAMA_MODEL) - re-prompted differently each time, but never a
// genuinely different model. Pangram's own writeup notes it produces false
// negatives "when a panel of AIs revises a story in a loop, even without
// adversarial prompting" (https://www.greaterwrong.com/posts/hrpQxfYvF6CBGWfJX/pangram-ai-detection-software-can-be-evaded) -
// a real, named weak spot that no amount of re-prompting the same model can
// address, since it's about the underlying model's output distribution, not
// the prompt. Runs on SECONDARY_MODEL (a different GLM generation from
// OLLAMA_MODEL) - the closest available "different AI" given production
// only has a single Z.ai key configured, not a genuinely different vendor.
// Deliberately placed BEFORE runConsolidatedCleanup in the pipeline (see
// handleHumanize), not after - a second model rewriting in its own voice can
// easily reintroduce a choppy run or a tricolon the first pass didn't have,
// so the calibrated pattern detectors need to run again afterward, not be
// bypassed by running last.
const crossModelRevision = async (text, hasLocks) => {
  const response = await fetchWithTimeout(`${OLLAMA_URL}/api/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: SECONDARY_MODEL,
      stream: false,
      job_type: "cross_model_revision",
      messages: [
        {
          role: "system",
          content: `You are doing one more pass on an already-finished, well-written piece of writing. Rewrite it in your own natural voice - genuinely different wording and sentence construction throughout, not a light touch or a synonym swap. You may reorder ideas within or across paragraphs if it reads more naturally that way - this is encouraged, not just allowed. What you must NOT do is drop any idea in the process of reordering: every claim, example, and point in the original has to still appear SOMEWHERE in your output, even if it moves to a different sentence or paragraph than where it started. Do not add new information, invented facts, or examples that aren't already in the original. Do not change the tone or register. This is a stylistic and structural rewrite, not a content edit - nothing gets cut, nothing gets invented.

Before finalizing, check your draft against the original point by point: is every single claim and example from the original still present somewhere in your rewrite? If you find one that quietly disappeared, add it back in, worded in your own voice, before outputting.

Output ONLY the complete rewritten text with no labels, headers, or commentary.${hasLocks ? LOCK_TOKEN_INSTRUCTION : ""}`
        },
        { role: "user", content: text },
      ],
    }),
  });
  const data = await checkOllamaResponse(response);
  return data.message?.content?.trim() || text;
};

const parseOutput = (raw) => {
  const hMatch = raw.match(/HUMANIZED\s*TEXT\s*:\s*([\s\S]*?)(?=CHANGE\s*LOG\s*:|$)/i);
  const cMatch = raw.match(/CHANGE\s*LOG\s*:\s*([\s\S]*?)$/i);
  return {
    humanized: hMatch ? hMatch[1].trim() : raw.trim(),
    changelog: cMatch ? cMatch[1].trim() : ""
  };
};

// ─── EXPORT HELPERS ───────────────────────────────────────────────────────────

const exportTxt = (text) => {
  const blob = new Blob([text], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "humanized.txt";
  a.click();
};

const exportMd = (text) => {
  const blob = new Blob([text], { type: "text/markdown" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "humanized.md";
  a.click();
};

const exportDocx = async (text) => {
  // Word opens HTML with a .doc extension natively, so that's the export format used here
  const html = `<html><head><meta charset="utf-8"/></head><body style="font-family:Calibri,sans-serif;font-size:12pt;line-height:1.6;max-width:800px;margin:40px auto;padding:20px">
${text.split(/\n+/).filter(p => p.trim()).map(p => `<p>${p.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</p>`).join('')}
</body></html>`;
  const blob = new Blob([html], { type: "application/msword" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "humanized.doc";
  a.click();
};

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

const ScoreBar = ({ label, value }) => {
  const pct = Math.round(value);
  const color = pct < 30 ? "#4a8a4a" : pct < 60 ? "#c8a04a" : "#b05050";
  return (
    <div style={{ marginBottom: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
        <span style={{ fontSize: "11px", color: "#555" }}>{label}</span>
        <span style={{ fontSize: "11px", color }}>{pct}</span>
      </div>
      <div style={{ height: "3px", background: "#eee", borderRadius: "2px" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: "2px", transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
};

// Free-page-only richer version of ScoreBar (humankind-free-page-improvements.md
// Fix 4 + Fix 5) - adds a plain-English explanation and a colour-coded risk
// label so a first-time visitor doesn't have to interpret a raw number
// themselves. `bandKey` picks which of FREE_RISK_BANDS' threshold schemes
// applies (perplexity/burstiness/functionWord read differently at the same
// raw score - see FREE_RISK_BANDS).
const FreeScoreBar = ({ label, value, explanation, foundNote, bandKey }) => {
  const pct = Math.round(value);
  const risk = freeRiskLabel(pct, bandKey);
  const clean = pct <= (FREE_RISK_BANDS[bandKey] || FREE_RISK_BANDS.generic)[0][0];
  return (
    <div style={{ marginBottom: "18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px", flexWrap: "wrap", gap: "6px" }}>
        <span style={{ fontSize: "13px", color: "#333", fontWeight: "600" }}>{label}</span>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "#888" }}>{pct}</span>
          <span style={{ fontSize: "11px", fontWeight: "700", color: risk.color }}>{clean ? "✓" : risk.color === "#c8a04a" ? "!" : "✗"} {risk.label}</span>
        </span>
      </div>
      <div style={{ height: "4px", background: "#eee", borderRadius: "2px", marginBottom: "6px" }}>
        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: risk.color, borderRadius: "2px", transition: "width 0.6s ease" }} />
      </div>
      {explanation && <div style={{ fontSize: "12px", color: "#777", lineHeight: "1.5" }}>{explanation}{foundNote ? <> <span style={{ color: "#555" }}>{foundNote}</span></> : ""}</div>}
    </div>
  );
};

const AnalysisPanel = ({ analysis, label, loading }) => {
  if (!analysis) {
    if (loading) return <div style={{ color: "#4A7FA5", fontSize: "13px", fontStyle: "italic" }}>Scoring...</div>;
    return <div style={{ color: "#aaa", fontSize: "13px", fontStyle: "italic" }}>No analysis yet.</div>;
  }
  const overall = analysis.overallAI ?? 0;
  const overallColor = overall < 30 ? "#4a8a4a" : overall < 60 ? "#c8a04a" : "#b05050";
  const overallLabel = overall < 30 ? "Likely Human" : overall < 60 ? "Mixed Signals" : "Likely AI";

  // Only ever set on humanized-text analyses (see handleHumanize) - the
  // original/pasted text was never generated under any of these settings, so
  // it has nothing to be "on target" against.
  const gw = analysis.generatedWith;
  const target = gw?.audienceLevel && AUDIENCE_READING_LEVELS[gw.audienceLevel];
  const actualGrade = analysis.readability ? Math.max(0, Math.round(analysis.readability.grade)) : null;
  const targetMet = target && actualGrade !== null ? (actualGrade >= target.min && actualGrade <= target.max) : null;
  const complexityCulprits = [gw?.persona === "academic" && "Academic persona", gw?.formality === "Formal" && "Formal formality"].filter(Boolean);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px", padding: "12px", background: "#f8f8f8", borderRadius: "5px", border: `1px solid ${overallColor}44` }}>
        <div style={{ fontSize: "32px", fontWeight: "300", color: overallColor, fontFamily: "Georgia, serif" }}>{overall}</div>
        <div>
          <div style={{ fontSize: "13px", color: overallColor, fontWeight: "600" }}>{overallLabel}</div>
          <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>{label}</div>
        </div>
      </div>
      {analysis.readability && (
        <div style={{ fontSize: "11px", color: "#888", marginBottom: "14px" }}>
          Readability: grade {actualGrade} reading level
          {target && (
            targetMet
              ? ` (target ${target.label} for ${gw.audienceLevel} audience — met)`
              : ` (target ${target.label} for ${gw.audienceLevel} audience — not met${complexityCulprits.length ? `, likely due to ${complexityCulprits.join(" / ")}` : ""})`
          )}
          {analysis.readability.grade > 16 ? " (may read as unnaturally dense — a sudden jump here alongside a low AI score can itself look off)" : ""}
        </div>
      )}
      <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#aaa", marginBottom: "8px" }}>Computed directly</div>
      <ScoreBar label={`Banned words${analysis.bannedCount !== undefined ? " (" + analysis.bannedCount + " found)" : ""}`} value={analysis.bannedWords ?? 0} />
      <ScoreBar label={`Em dashes${analysis.emDashCount !== undefined ? " (" + analysis.emDashCount + " found)" : ""}`} value={analysis.emDash ?? 0} />
      <ScoreBar label="Low lexical variety (perplexity)" value={analysis.perplexity ?? 0} />
      <ScoreBar label="Uniform sentence rhythm (burstiness)" value={analysis.burstinessScore ?? 0} />
      <ScoreBar label="Low function-word density" value={analysis.functionWordScore ?? 0} />
      <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#aaa", margin: "14px 0 8px" }}>AI-judged</div>
      <ScoreBar label="Uniform sentences" value={analysis.uniformSentences ?? 0} />
      <ScoreBar label="Predictable openers" value={analysis.predictableOpeners ?? 0} />
      <ScoreBar label="Perfect symmetry" value={analysis.perfectSymmetry ?? 0} />
      <ScoreBar label="Formal phrasing" value={analysis.formalPhrasing ?? 0} />
      {Array.isArray(analysis.topIssues) && analysis.topIssues.length > 0 && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#888", marginBottom: "8px" }}>Top Issues</div>
          {analysis.topIssues.filter(Boolean).map((issue, i) => (
            <div key={i} style={{ fontSize: "12px", color: "#555", marginBottom: "5px", paddingLeft: "10px", borderLeft: "2px solid #ddd" }}>{issue}</div>
          ))}
        </div>
      )}
      {analysis.verdict && (
        <div style={{ marginTop: "14px", fontSize: "12px", color: "#888", fontStyle: "italic" }}>{analysis.verdict}</div>
      )}
    </div>
  );
};

const Toggle = ({ label, checked, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }} onClick={() => onChange(!checked)}>
    <div style={{ width: "32px", height: "18px", background: checked ? "#00B4D8" : "#1E2D42", borderRadius: "9px", position: "relative", transition: "background 0.2s", border: "1px solid " + (checked ? "#00B4D8" : "#2A3F58") }}>
      <div style={{ position: "absolute", top: "2px", left: checked ? "14px" : "2px", width: "12px", height: "12px", background: checked ? "#0A1120" : "#4A7FA5", borderRadius: "50%", transition: "left 0.2s" }} />
    </div>
    <span style={{ fontSize: "12px", fontWeight: "500", color: "#8BAFC9", letterSpacing: "0.02em" }}>{label}</span>
  </div>
);

const RadioGroup = ({ label, options, value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <div style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.8px", textTransform: "uppercase", color: "#4A7FA5" }}>{label}</div>
    <div style={{ display: "flex", gap: "4px" }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)} style={{
          fontSize: "12px", padding: "7px 12px", borderRadius: "5px", border: "0.5px solid",
          borderColor: value === opt.value ? "#00B4D8" : "#1E2D42",
          background: value === opt.value ? "#00B4D8" : "#111D2C",
          color: value === opt.value ? "#0A1120" : "#4A7FA5",
          fontWeight: value === opt.value ? "600" : "500",
          cursor: "pointer", transition: "all 0.15s"
        }}>{opt.label}</button>
      ))}
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <div style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.8px", textTransform: "uppercase", color: "#4A7FA5" }}>{label}</div>
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      fontSize: "12px", height: "34px", padding: "0 10px", border: "0.5px solid #1E2D42",
      borderRadius: "6px", color: "#8BAFC9", background: "#111D2C", outline: "none", cursor: "pointer", minWidth: "130px"
    }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ─── VOICE PROFILES ───────────────────────────────────────────────────────────

// Shared by both the "add" and "edit" forms below - these used to be two
// separate, near-identical JSX blocks, which is exactly how the checkbox
// copy fix earlier didn't actually land everywhere: a change applied to
// one copy and not the other. One form, parameterized, closes that class
// of bug structurally instead of relying on remembering to edit both.
const VoiceProfileForm = ({ name, setName, text, setText, style, setStyle, isClientVoice, setIsClientVoice, showAdvanced, setShowAdvanced, behavioral, setBehavioral, onSubmit, submitLabel, onCancel, cancelLabel, background, border, textareaHeight, namePlaceholder }) => {
  // Local, UI-only collapse state - doesn't need to be lifted to the parent
  // the way showAdvanced is, since it's not saved data, just whether this
  // section is currently expanded. Resets each time the form mounts, which
  // is fine for a collapse toggle.
  const [showBehavioral, setShowBehavioral] = useState(false);
  const answeredCount = Object.keys(behavioral || {}).length;
  return (
  <div style={{ background, border: `1px solid ${border}`, borderRadius: "5px", padding: "14px" }}>
    <input value={name} onChange={e => setName(e.target.value)} placeholder={namePlaceholder}
      style={{ width: "100%", padding: "7px 10px", fontSize: "13px", fontFamily: "Georgia, serif", border: "1px solid #ddd", borderRadius: "3px", marginBottom: "10px", boxSizing: "border-box", outline: "none" }} />
    <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Paste 2-5 paragraphs of your own writing here (up to about 1000 words)..."
      style={{ width: "100%", height: textareaHeight, padding: "10px", fontSize: "13px", fontFamily: "Georgia, serif", border: "1px solid #ddd", borderRadius: "3px", resize: "vertical", outline: "none", boxSizing: "border-box", marginBottom: "4px" }} />
    <div style={{ fontSize: "11px", color: wordCount(text) > 1000 ? "#b05050" : "#999", marginBottom: "10px" }}>
      {wordCount(text)} / 1000 words{wordCount(text) > 1000 ? " — consider trimming to your most representative writing" : ""}
    </div>
    <button onClick={() => setShowAdvanced(!showAdvanced)} style={{ background: "none", border: "none", padding: 0, fontSize: "11px", color: "#00B4D8", cursor: "pointer", marginBottom: showAdvanced ? "10px" : "12px" }}>
      {showAdvanced ? "▾" : "▸"} Advanced options (writing style, ghostwriting)
    </button>
    {showAdvanced && (
      <div style={{ marginBottom: "4px" }}>
        <select value={style} onChange={e => setStyle(e.target.value)}
          style={{ width: "100%", padding: "7px 10px", fontSize: "13px", fontFamily: "Georgia, serif", border: "1px solid #ddd", borderRadius: "3px", marginBottom: "10px", boxSizing: "border-box", outline: "none", background: "#fff", color: style ? "#333" : "#999" }}>
          <option value="">What kind of writing is this sample? (optional)</option>
          {VOICE_STYLE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "flex-start", gap: "6px", fontSize: "12px", color: "#555", marginBottom: "10px", cursor: "pointer" }}>
          <input type="checkbox" checked={isClientVoice} onChange={e => setIsClientVoice(e.target.checked)} style={{ marginTop: "2px" }} />
          <span>
            This is a client's voice, not mine
            <div style={{ fontSize: "11px", color: "#999", fontStyle: "italic", marginTop: "1px" }}>Always sounds exactly like them — even overrides your Formality setting for this piece.</div>
          </span>
        </label>
      </div>
    )}
    <button onClick={() => setShowBehavioral(!showBehavioral)} style={{ background: "none", border: "none", padding: 0, fontSize: "11px", color: "#00B4D8", cursor: "pointer", marginBottom: showBehavioral ? "10px" : "12px" }}>
      {showBehavioral ? "▾" : "▸"} Communication style (optional, ~1 min){answeredCount ? ` · ${answeredCount}/${BEHAVIORAL_QUESTIONS.length} answered` : ""}
    </button>
    {showBehavioral && (
      <div style={{ marginBottom: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
          <div style={{ fontSize: "11px", color: "#888", lineHeight: "1.5" }}>
            A few quick questions about how you naturally communicate — not just how you write, but how you make a point. Skip any you're unsure about. Click a selected answer again to clear it.
          </div>
          {answeredCount > 0 && (
            <button type="button" onClick={() => setBehavioral({})}
              style={{ background: "none", border: "none", padding: 0, fontSize: "11px", color: "#b05050", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
              Clear all
            </button>
          )}
        </div>
        {BEHAVIORAL_QUESTIONS.map(q => (
          <div key={q.id} style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#555", marginBottom: "2px" }}>{q.section}</div>
            <div style={{ fontSize: "12px", color: "#333", marginBottom: "6px" }}>{q.question}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {q.options.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => {
                    const isSelected = behavioral?.[q.id] === opt.value;
                    if (isSelected) {
                      const next = { ...behavioral };
                      delete next[q.id];
                      setBehavioral(next);
                    } else {
                      setBehavioral({ ...behavioral, [q.id]: opt.value });
                    }
                  }}
                  style={{
                    textAlign: "left", padding: "7px 10px", fontSize: "12px", fontFamily: "Georgia, serif",
                    borderRadius: "4px", cursor: "pointer",
                    border: `1px solid ${behavioral?.[q.id] === opt.value ? "#00B4D8" : "#ddd"}`,
                    background: behavioral?.[q.id] === opt.value ? "#e6f9fc" : "#fff",
                    color: "#333",
                  }}>{opt.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
    <div style={{ display: "flex", gap: "8px" }}>
      <button onClick={onSubmit} disabled={!name.trim() || !text.trim()} style={{
        background: (!name.trim() || !text.trim()) ? "#eee" : "#00B4D8", color: (!name.trim() || !text.trim()) ? "#aaa" : "#0A1120",
        border: "none", borderRadius: "6px", padding: "8px 20px", fontSize: "12px", fontWeight: "700", fontFamily: "Georgia, serif", cursor: "pointer"
      }}>{submitLabel}</button>
      <button onClick={onCancel} style={{ background: "transparent", border: "1px solid #ddd", borderRadius: "3px", padding: "8px 14px", fontSize: "12px", fontFamily: "Georgia, serif", cursor: "pointer", color: "#555" }}>{cancelLabel}</button>
    </div>
  </div>
  );
};

const VoiceProfiles = ({ active, onSelect, onSave, onStyleChange, onOwnerChange, onBehavioralChange, onDelete }) => {
  const [profiles, setProfiles] = useState([]);
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");
  const [newStyle, setNewStyle] = useState("");
  const [newIsClientVoice, setNewIsClientVoice] = useState(false);
  const [newShowAdvanced, setNewShowAdvanced] = useState(false);
  const [newBehavioral, setNewBehavioral] = useState({});
  const [adding, setAdding] = useState(false);
  // Clicking a saved profile previously only activated it silently (onSave into
  // an invisible internal variable) with no way to see or change its text -
  // this opens it for viewing/editing too, which is what "clicking the title"
  // should actually do.
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editText, setEditText] = useState("");
  const [editStyle, setEditStyle] = useState("");
  const [editIsClientVoice, setEditIsClientVoice] = useState(false);
  const [editShowAdvanced, setEditShowAdvanced] = useState(false);
  const [editBehavioral, setEditBehavioral] = useState({});

  // Set only once the saved list has actually been read. If the read fails
  // (unreadable/corrupt data, storage blocked), saves are refused - otherwise
  // the in-memory list is empty and the next save overwrites the real data
  // with it, which is how saved profiles get silently wiped.
  const profilesLoadedOk = useRef(false);
  const [storageError, setStorageError] = useState("");

  useEffect(() => {
    const load = async () => {
      let loaded;
      try {
        const result = await window.storage.get("voice_profiles");
        loaded = result?.value ? JSON.parse(result.value) : [];
        if (!Array.isArray(loaded)) throw new Error("voice_profiles is not a list");
      } catch (err) {
        logError("Voice Profiles failed to load - saves paused to protect stored data", err?.stack || String(err), "voiceProfiles");
        setStorageError("Your saved Voice Profiles couldn't be read, so changes are paused to protect them. Reload the page to try again.");
        return;
      }
      profilesLoadedOk.current = true;
      setProfiles(loaded);
      // Only the list itself was ever restored - which profile was active,
      // and its actual text, never was, so the parent's styleText stayed
      // empty after every refresh even though a profile still existed.
      try {
        const activeResult = await window.storage.get("active_profile_id");
        const restoredId = activeResult?.value || null;
        if (restoredId) {
          const match = loaded.find(p => p.id === restoredId);
          if (match) {
            onSelect(match.id);
            onSave(match.text);
            onStyleChange(match.style || "");
            onOwnerChange(!!match.isClientVoice);
            onBehavioralChange(match.behavioral || null);
          }
        }
      } catch {}
    };
    load();
  }, []);

  const save = async (updated) => {
    if (!profilesLoadedOk.current) {
      setStorageError("Voice Profiles are still loading or couldn't be read, so this change wasn't saved. Reload the page and try again.");
      return;
    }
    setProfiles(updated);
    try {
      // Keep one previous copy so a bad write is never the only copy of a profile.
      const previous = await window.storage.get("voice_profiles");
      if (previous?.value) await window.storage.set("voice_profiles_backup", previous.value);
      await window.storage.set("voice_profiles", JSON.stringify(updated));
      setStorageError("");
    } catch (err) {
      logError("Voice Profiles failed to save", err?.stack || String(err), "voiceProfiles");
      setStorageError("This change couldn't be saved in this browser (storage may be full or blocked). Your previously saved profiles are unchanged.");
    }
  };

  const addProfile = () => {
    if (!newName.trim() || !newText.trim()) return;
    const profile = { id: Date.now().toString(), name: newName.trim(), text: newText.trim(), style: newStyle, isClientVoice: newIsClientVoice, behavioral: newBehavioral };
    const updated = [...profiles, profile];
    save(updated);
    setNewName(""); setNewText(""); setNewStyle(""); setNewIsClientVoice(false); setNewShowAdvanced(false); setNewBehavioral({}); setAdding(false);
    onSave(profile.text);
    onStyleChange(profile.style);
    onOwnerChange(profile.isClientVoice);
    onBehavioralChange(profile.behavioral);
    onSelect(profile.id);
  };

  const deleteProfile = (id) => {
    // No undo once this runs - confirm first. Previously fired immediately on
    // click with zero confirmation, which cost real saved profiles.
    const target = profiles.find(p => p.id === id);
    if (!window.confirm(`Delete the voice profile "${target?.name || "this profile"}"? This can't be undone.`)) return;
    const updated = profiles.filter(p => p.id !== id);
    save(updated);
    if (active === id) { onSelect(null); onSave(""); onStyleChange(""); onOwnerChange(false); onBehavioralChange(null); }
    if (editingId === id) setEditingId(null);
    onDelete(id);
  };

  const selectProfile = (p) => {
    onSelect(p.id);
    onSave(p.text);
    onStyleChange(p.style || "");
    onOwnerChange(!!p.isClientVoice);
    onBehavioralChange(p.behavioral || null);
    // The Add form staying open while an Edit form also opens (or vice
    // versa) was a real bug, not just clutter - both used to be able to
    // show on screen at once. Opening one now always closes the other.
    setAdding(false);
    if (editingId === p.id) {
      setEditingId(null); // clicking the already-open profile closes it
    } else {
      setEditingId(p.id);
      setEditName(p.name);
      setEditText(p.text);
      setEditStyle(p.style || "");
      setEditIsClientVoice(!!p.isClientVoice);
      setEditBehavioral(p.behavioral || {});
      setEditShowAdvanced(!!(p.style || p.isClientVoice)); // open already-set advanced options visibly, not hidden
    }
  };

  const saveEdit = () => {
    if (!editName.trim() || !editText.trim()) return;
    const updated = profiles.map(p => p.id === editingId ? { ...p, name: editName.trim(), text: editText.trim(), style: editStyle, isClientVoice: editIsClientVoice, behavioral: editBehavioral } : p);
    save(updated);
    if (active === editingId) { onSave(editText.trim()); onStyleChange(editStyle); onOwnerChange(editIsClientVoice); onBehavioralChange(editBehavioral); } // keep the active style text/tag/owner/behavioral in sync if this is the one in use
    setEditingId(null);
  };

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#888", marginBottom: "12px" }}>
        Voice Profiles — save samples of your writing to match your style
      </div>
      {storageError && (
        <div role="alert" style={{ fontSize: "12px", color: "#b05050", background: "#fbeeee", border: "1px solid #e8c4c4", borderRadius: "4px", padding: "8px 10px", marginBottom: "12px" }}>
          {storageError}
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
        {profiles.map(p => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <button onClick={() => selectProfile(p)} style={{
              fontSize: "12px", fontWeight: "600", padding: "6px 14px", borderRadius: "6px", border: "1px solid",
              borderColor: active === p.id ? "#00B4D8" : "#D1D5DB",
              background: active === p.id ? "#00B4D8" : "#fff",
              color: active === p.id ? "#0A1120" : "#374151",
              cursor: "pointer"
            }}>{p.name}{p.style ? ` · ${p.style}` : ""}{p.isClientVoice ? " · Client" : ""}</button>
            <button onClick={() => deleteProfile(p.id)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: "14px", padding: "0 4px" }}>×</button>
          </div>
        ))}
        {profiles.length < 5 && (
          <button onClick={() => { setEditingId(null); setAdding(!adding); }} style={{
            fontSize: "12px", padding: "5px 12px", borderRadius: "3px", border: "1px dashed #ddd",
            background: "#fff", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif"
          }}>+ Add Profile</button>
        )}
      </div>
      {adding && (
        <VoiceProfileForm
          name={newName} setName={setNewName} text={newText} setText={setNewText}
          style={newStyle} setStyle={setNewStyle} isClientVoice={newIsClientVoice} setIsClientVoice={setNewIsClientVoice}
          showAdvanced={newShowAdvanced} setShowAdvanced={setNewShowAdvanced}
          behavioral={newBehavioral} setBehavioral={setNewBehavioral}
          onSubmit={addProfile} submitLabel="Save Profile" onCancel={() => setAdding(false)} cancelLabel="Cancel"
          background="#f8f8f8" border="#eee" textareaHeight="100px" namePlaceholder="Profile name (e.g. My Blog Voice)"
        />
      )}
      {editingId && (
        <VoiceProfileForm
          name={editName} setName={setEditName} text={editText} setText={setEditText}
          style={editStyle} setStyle={setEditStyle} isClientVoice={editIsClientVoice} setIsClientVoice={setEditIsClientVoice}
          showAdvanced={editShowAdvanced} setShowAdvanced={setEditShowAdvanced}
          behavioral={editBehavioral} setBehavioral={setEditBehavioral}
          onSubmit={saveEdit} submitLabel="Save Changes" onCancel={() => setEditingId(null)} cancelLabel="Close"
          background="#f0f7fb" border="#cfe4ef" textareaHeight="140px" namePlaceholder="Profile name"
        />
      )}
    </div>
  );
};

const cleanedFileName = (name) => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? `${name}.cleaned` : `${name.slice(0, dot)}.cleaned${name.slice(dot)}`;
};
const FILE_MIME_TYPES = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
};

const FileMetadataCheck = () => {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // null = no file checked yet
  const [scanError, setScanError] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [cleanChanges, setCleanChanges] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!f) return;
    const ext = f.name.toLowerCase().split(".").pop();
    if (ext !== "docx" && ext !== "pdf") {
      setScanError("Only .docx and .pdf files can be checked for hidden metadata.");
      setResult(null);
      return;
    }
    setFile(f);
    setScanning(true);
    setScanError("");
    setResult(null);
    setCleanChanges(null);
    try {
      const detections = await scanFileMetadata(f);
      setResult(detections);
    } catch {
      setScanError("Couldn't read that file. It may be corrupted or in an unsupported format.");
    } finally {
      setScanning(false);
    }
  };

  const handleClean = async () => {
    if (!file) return;
    setCleaning(true);
    setScanError("");
    try {
      const cleaned = await cleanFileMetadata(file);
      if (!cleaned || cleaned.changes.length === 0) {
        setScanError("Couldn't remove the flagged metadata from this file.");
        return;
      }
      const ext = file.name.toLowerCase().split(".").pop();
      const blob = new Blob([cleaned.bytes], { type: FILE_MIME_TYPES[ext] || "application/octet-stream" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = cleanedFileName(file.name);
      a.click();
      setCleanChanges(cleaned.changes);
    } catch {
      setScanError("Couldn't clean that file's metadata.");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div style={{ padding: "16px 32px" }}>
      <div style={{ fontSize: "11px", color: "#888", marginBottom: "10px" }}>
        Check a .docx or .pdf file's hidden document properties for AI-tool creator tags (e.g. "ChatGPT", "Gemini"), and remove them if found. This runs entirely in your browser, the file is never uploaded anywhere.
      </div>
      <label style={{
        display: "inline-block", fontSize: "12px", padding: "8px 16px", borderRadius: "3px",
        border: "1px dashed #ddd", background: "#fff", color: "#555", cursor: "pointer", fontFamily: "Georgia, serif"
      }}>
        {file?.name || "Choose a .docx or .pdf file..."}
        <input type="file" accept=".docx,.pdf" onChange={handleFile} style={{ display: "none" }} />
      </label>

      {scanning && <div style={{ fontSize: "12px", color: "#888", marginTop: "10px" }}>Scanning...</div>}
      {scanError && <div style={{ fontSize: "12px", color: "#b05050", marginTop: "10px" }}>{scanError}</div>}
      {result && !scanning && (
        result.length === 0 ? (
          <div style={{ fontSize: "12px", color: "#4a8a4a", marginTop: "10px" }}>✓ No AI-tool metadata found in this file.</div>
        ) : (
          <div style={{ marginTop: "10px", padding: "12px", background: "#fff2c0", border: "1px solid #c0a020", borderRadius: "5px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#8a6d10", marginBottom: "6px" }}>⚠ AI-tool metadata found</div>
            {result.map((d, i) => (
              <div key={i} style={{ fontSize: "11px", color: "#8a6d10", marginBottom: "3px" }}>{d.flag}</div>
            ))}
            <div style={{ fontSize: "11px", color: "#8a6d10", margin: "8px 0" }}>
              The file wrapper itself carries this tag, humanizing the text alone won't remove it.
            </div>
            <button onClick={handleClean} disabled={cleaning} style={{
              fontSize: "12px", fontWeight: "600", padding: "7px 16px", borderRadius: "5px", border: "1px solid #8a6d10",
              background: cleaning ? "#f0e0a0" : "#fff", color: "#8a6d10", cursor: cleaning ? "default" : "pointer"
            }}>
              {cleaning ? "Cleaning..." : "Clean & Download"}
            </button>
          </div>
        )
      )}
      {cleanChanges && (
        <div style={{ marginTop: "10px", padding: "12px", background: "#eef7ee", border: "1px solid #a0c0a0", borderRadius: "5px" }}>
          <div style={{ fontSize: "12px", fontWeight: "600", color: "#3a6a3a", marginBottom: "6px" }}>✓ Removed and downloaded</div>
          {cleanChanges.map((c, i) => (
            <div key={i} style={{ fontSize: "11px", color: "#3a6a3a", marginBottom: "3px" }}>{c}</div>
          ))}
          <div style={{ fontSize: "11px", color: "#3a6a3a", marginTop: "8px", fontStyle: "italic" }}>
            This removes the flagged document-properties tag only, not embedded image/PDF XMP metadata or any statistical watermark in the generated text itself.
          </div>
        </div>
      )}
    </div>
  );
};

const errLogBtn = { fontSize: "11px", padding: "5px 12px", borderRadius: "3px", border: "1px solid #ddd", background: "#fff", color: "#555", cursor: "pointer", fontFamily: "Georgia, serif" };

const ErrorLogPanel = () => {
  const [log, setLog] = useState(() => getErrorLog());
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    const text = log.length
      ? log.slice().reverse().map(e => `[${new Date(e.time).toLocaleString()}] ${e.source}\n${e.message}\n${e.stack}`).join("\n\n---\n\n")
      : "No errors logged.";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard best-effort */ }
  };

  return (
    <div style={{ padding: "16px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "8px" }}>
        <div style={{ fontSize: "11px", color: "#888" }}>
          {log.length === 0
            ? "No errors logged. This captures crashes automatically, even ones that don't show a visible message, and persists across reloads until cleared."
            : `${log.length} error${log.length !== 1 ? "s" : ""} logged, most recent first.`}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => setLog(getErrorLog())} style={errLogBtn}>Refresh</button>
          <button onClick={copyAll} style={errLogBtn}>{copied ? "Copied!" : "Copy All"}</button>
          <button onClick={() => { clearErrorLog(); setLog([]); }} style={errLogBtn}>Clear</button>
        </div>
      </div>
      {log.length > 0 && (
        <div style={{ maxHeight: "260px", overflow: "auto", border: "1px solid #eee", borderRadius: "5px" }}>
          {log.slice().reverse().map((e, i) => (
            <div key={i} style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", fontSize: "11px" }}>
              <div style={{ color: "#aaa", marginBottom: "3px" }}>{new Date(e.time).toLocaleString()} — {e.source}</div>
              <div style={{ color: "#b05050", fontFamily: "monospace" }}>{e.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── AUTH ─────────────────────────────────────────────────────────────────────

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await fetch(`${OLLAMA_URL}/api/auth/login.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Login failed."); return; }
      onLogin(data.user);
    } catch {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", background: "#0F1724" }}>
      <style>{`* { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; } ::placeholder { color: #4A7FA5 !important; opacity: 1; }`}</style>
      <form onSubmit={submit} style={{ width: "320px", padding: "32px", background: "#0A1120", border: "0.5px solid #1E2D42", borderRadius: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
          <div style={{ width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg>
          </div>
          <div>
            <span style={{ fontSize: "15px", color: "#E8EDF5", fontWeight: "600" }}>HumanKind</span>
            <span style={{ fontSize: "13px", color: "#4A7FA5" }}> · Sign in</span>
          </div>
        </div>
        <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
          style={{ width: "100%", padding: "10px 12px", fontSize: "14px", border: "0.5px solid #1E2D42", borderRadius: "6px", marginBottom: "10px", boxSizing: "border-box", outline: "none", background: "#111D2C", color: "#E8EDF5" }} />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
          style={{ width: "100%", padding: "10px 12px", fontSize: "14px", border: "0.5px solid #1E2D42", borderRadius: "6px", marginBottom: "14px", boxSizing: "border-box", outline: "none", background: "#111D2C", color: "#E8EDF5" }} />
        {error && <div style={{ fontSize: "12px", color: "#F87171", marginBottom: "14px" }}>{error}</div>}
        <button type="submit" disabled={busy} style={{
          width: "100%", padding: "10px", fontSize: "13px", fontWeight: "700", borderRadius: "7px", border: "none",
          background: busy ? "#0A6E85" : "#00B4D8", color: "#0A1120", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1
        }}>{busy ? "Signing in..." : "Sign in"}</button>
        <div style={{ fontSize: "11px", color: "#4A7FA5", marginTop: "16px", textAlign: "center" }}>
          Accounts are created automatically once you subscribe. No account yet? Check your email after signing up.
        </div>
      </form>
    </div>
  );
};

// Public, no-auth entry point - the "free coffee" per the funnel spec: real
// value with zero LLM cost, so no rate-limiting or server involvement is
// needed for the analysis itself (see analyzeFree above - pure JS, runs
// entirely in the browser). The only things that cost anything (account
// creation, Stripe, an actual Revoice) happen after the visitor leaves this
// page, which is the whole point of the funnel this was built from.
const FREE_WORD_LIMIT = 1000;
const FREE_MIN_WORDS_TO_ANALYZE = 20;
const FREE_MIN_WORDS_FOR_MEANINGFUL_RESULT = 50;
const FreeAnalyzerPage = () => {
  const [text, setText] = useState(FREE_EXAMPLE_TEXT);
  const [result, setResult] = useState(null);
  const [whatChecksOpen, setWhatChecksOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const words = wordCount(text);
  const overLimit = words > FREE_WORD_LIMIT;
  const underMin = words < FREE_MIN_WORDS_TO_ANALYZE;
  const isExample = text === FREE_EXAMPLE_TEXT;

  const analyze = useCallback(() => {
    if (!text.trim() || overLimit || underMin) return;
    setResult(analyzeFree(text));
  }, [text, overLimit, underMin]);

  // Fix 2: a first-time visitor sees a real, alarming result immediately,
  // without needing to bring their own text or click anything first.
  // Runs once on mount only, to show the preloaded example's result
  // immediately - deliberately not re-run when `analyze` itself changes
  // identity (it depends on `text`), since this is a one-time "first
  // impression" effect, not a live-update-on-every-keystroke one.
  const analyzeOnMount = useRef(analyze);
  analyzeOnMount.current = analyze;
  useEffect(() => { analyzeOnMount.current(); }, []);

  const goRevoice = () => {
    try { sessionStorage.setItem("humankind_free_tool_text", isExample ? "" : text); } catch { /* non-critical - just skips the pre-fill */ }
    window.location.href = "/";
  };

  const verdict = result ? computeFreeVerdict(result) : null;

  const shareResult = async () => {
    if (!result || !verdict) return;
    const topIssues = result.patterns.slice(0, 3);
    const issuesLine = topIssues.length ? topIssues.join(", ") : "no major structural patterns";
    const summary = `I just checked my text on HumanKind's free AI writing analyzer.\nResult: ${verdict.label.toUpperCase()} (Score: ${verdict.overall})\nTop issues: ${issuesLine}.\nCheck your own text free at humankind.xtoolstack.com/free`;
    try {
      await navigator.clipboard.writeText(summary);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch { /* clipboard unavailable - button just won't show the "Copied" confirmation */ }
  };

  const labelStyle = { fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#aaa", marginBottom: "8px" };

  // Fix 6: CTA copy responds to the verdict severity instead of one generic
  // pitch for everyone.
  const ctaCopy = !verdict ? null : verdict.overall > 50 ? {
    heading: verdict.overall > 75 ? "Your text is likely to get flagged. HumanKind can fix it." : "Your text has some AI patterns worth fixing.",
    body: verdict.overall > 75
      ? "The full Humanizer rewrites your text to remove every AI pattern found above - in your voice, not a generic template. Real results, before/after scores, full transparency."
      : "HumanKind targets the specific issues found above and rewrites them - preserving your meaning while removing the signals that would get you flagged.",
    button: "Humanize My Text →",
  } : {
    heading: "Your text is mostly clean.",
    body: "If you want to go further - tighten your voice, match a specific writing style, or run an SEO check - HumanKind has the tools for that too.",
    button: "Try HumanKind Free →",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0F1724", fontFamily: "Georgia, serif" }}>
      <style>{`* { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; } ::placeholder { color: #4A7FA5 !important; opacity: 1; }`}</style>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "20px 32px", borderBottom: "0.5px solid #1E2D42" }}>
        <div style={{ width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg>
        </div>
        <span style={{ fontSize: "15px", color: "#E8EDF5", fontWeight: "600" }}>HumanKind</span>
        <span style={{ fontSize: "13px", color: "#4A7FA5" }}> · Check Your Writing Free</span>
      </div>

      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "48px 24px 80px", boxSizing: "border-box" }}>
        <h1 style={{ fontSize: "30px", color: "#E8EDF5", fontWeight: "600", marginBottom: "10px", textAlign: "center" }}>Check Your Writing Free</h1>
        <p style={{ fontSize: "15px", color: "#8BAFC9", textAlign: "center", marginBottom: "32px", lineHeight: "1.6" }}>
          Paste your text and get an instant analysis of how AI-detectable it is. We check the same signals GPTZero and Turnitin use to flag content - banned words, sentence rhythm, lexical variety, and 18 structural AI patterns. No account. No email. No catch.
        </p>

        <textarea
          value={text}
          onChange={e => { setText(e.target.value); setResult(null); }}
          placeholder="Paste your article, essay, blog post or other writing here..."
          style={{
            width: "100%", height: "260px", padding: "16px", fontSize: "14px", lineHeight: "1.6",
            border: `0.5px solid ${overLimit ? "#F87171" : "#1E2D42"}`, borderRadius: "8px", background: "#111D2C",
            color: "#E8EDF5", resize: "vertical", outline: "none", boxSizing: "border-box"
          }}
        />
        {isExample && (
          <div style={{ fontSize: "12px", color: "#4A7FA5", fontStyle: "italic", marginTop: "6px" }}>
            Example text loaded - paste your own text to analyze it
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", flexWrap: "wrap", gap: "10px" }}>
          <span style={{ fontSize: "12px", color: overLimit ? "#F87171" : "#4A7FA5" }}>
            {words} / {FREE_WORD_LIMIT} words{overLimit ? " — trim to your first " + FREE_WORD_LIMIT + " words for the free check" : ""}
          </span>
          <button onClick={analyze} disabled={!text.trim() || overLimit || underMin} title={underMin ? `Paste at least ${FREE_MIN_WORDS_TO_ANALYZE} words to analyze.` : undefined} style={{
            padding: "12px 24px", minHeight: "44px", fontSize: "14px", fontWeight: "700", borderRadius: "7px", border: "none",
            background: (!text.trim() || overLimit || underMin) ? "#0A6E85" : "#00B4D8", color: "#0A1120",
            cursor: (!text.trim() || overLimit || underMin) ? "default" : "pointer", opacity: (!text.trim() || overLimit || underMin) ? 0.6 : 1
          }}>Analyze My Text — Free</button>
        </div>
        {words > 0 && words < FREE_MIN_WORDS_FOR_MEANINGFUL_RESULT && (
          <div style={{ fontSize: "12px", color: "#e0b04a", marginTop: "8px" }}>
            ⚠ For a meaningful analysis, paste at least {FREE_MIN_WORDS_FOR_MEANINGFUL_RESULT} words. Shorter text produces unreliable scores.
          </div>
        )}

        {/* Fix 7 */}
        <div style={{ marginTop: "20px" }}>
          <button onClick={() => setWhatChecksOpen(o => !o)} style={{
            background: "none", border: "none", color: "#4A7FA5", fontSize: "13px", cursor: "pointer", padding: "6px 0", minHeight: "44px", display: "flex", alignItems: "center"
          }}>
            What does this check? {whatChecksOpen ? "▴" : "▾"}
          </button>
          {whatChecksOpen && (
            <div style={{ fontSize: "13px", color: "#8BAFC9", lineHeight: "1.7", background: "#111D2C", border: "0.5px solid #1E2D42", borderRadius: "8px", padding: "16px" }}>
              <p style={{ margin: "0 0 12px" }}>The free analyzer checks your text against the same signals AI detectors use to flag content - all calculated instantly in your browser with no server round-trip, no account, and no cost.</p>
              <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a86a8", marginBottom: "6px" }}>Computed with real math</div>
              <p style={{ margin: "0 0 12px" }}>
                Banned words - exact count of words and phrases AI overuses<br />
                Em dashes - counted exactly (AI uses them at a much higher rate than humans)<br />
                Lexical variety - how varied and unpredictable your vocabulary is<br />
                Sentence rhythm - how much your sentence lengths vary
              </p>
              <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#5a86a8", marginBottom: "6px" }}>Structural pattern detection</div>
              <p style={{ margin: "0 0 12px" }}>18 deterministic pattern checks - the same checks used in the full HumanKind product, scanning for the specific structures AI text consistently produces.</p>
              <p style={{ margin: 0 }}>This analysis does not rewrite your text. It shows you what needs fixing. The full Humanizer fixes it.</p>
            </div>
          )}
        </div>

        {result && verdict && (
          <div style={{ marginTop: "24px", background: verdict.overall > 50 ? "#fdf2f2" : verdict.overall > 30 ? "#fdf8ee" : "#f0f8f0", border: `1px solid ${verdict.color}`, borderRadius: "10px", padding: "20px 24px" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: verdict.color, marginBottom: "6px" }}>{verdict.icon} {verdict.label.toUpperCase()}</div>
            <div style={{ fontSize: "13px", color: "#444", lineHeight: "1.6" }}>{verdict.copy}</div>
          </div>
        )}

        {result && (
          <div style={{ marginTop: "16px", background: "#fff", borderRadius: "10px", padding: "28px", boxSizing: "border-box" }}>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "24px", paddingBottom: "20px", borderBottom: "1px solid #eee" }}>
              {[["Words", result.words], ["Sentences", result.sentenceCount], ["Paragraphs", result.paragraphCount], ["Avg. sentence length", result.avgSentenceLen + " words"]].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: "20px", color: "#222", fontWeight: "600" }}>{value}</div>
                  <div style={{ fontSize: "11px", color: "#888" }}>{label}</div>
                </div>
              ))}
            </div>

            {result.readability && (
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "18px" }}>
                Readability: grade {Math.max(0, Math.round(result.readability.grade))} reading level
              </div>
            )}

            <div style={labelStyle}>Writing profile</div>
            <FreeScoreBar
              label={`Banned words (${result.bannedCount} found)`}
              value={Math.min(100, result.bannedCount * 8)}
              explanation="AI tools overuse specific words and phrases that detectors flag heavily."
              foundNote={result.bannedWordsFound?.length ? `Found: ${result.bannedWordsFound.map(w => `"${w}"`).join(", ")}` : "None were found in your text."}
            />
            <FreeScoreBar
              label={`Em dashes (${result.emDashCount} found)`}
              value={Math.min(100, result.emDashCount * 12)}
              explanation="AI models use em dashes at a much higher rate than human writers."
              foundNote={result.emDashCount ? `${result.emDashCount} found - this is a strong AI signal.` : "None were found in your text."}
            />
            <FreeScoreBar
              label="Low lexical variety (perplexity)"
              value={result.perplexityScore}
              bandKey="perplexity"
              explanation="Measures how varied and unpredictable your vocabulary is. AI text uses the same common words repeatedly - human writers are more varied."
              foundNote={`Score above 60 = strong AI signal. Your score: ${Math.round(result.perplexityScore)}.`}
            />
            <FreeScoreBar
              label="Uniform sentence rhythm (burstiness)"
              value={100 - result.burstinessScore}
              bandKey="burstiness"
              explanation="Measures how much your sentence lengths vary. Human writers naturally mix short punchy sentences with longer ones; AI text tends to produce sentences of similar length throughout."
              foundNote={`Score above 30 = detectable rhythm. Your score: ${Math.round(100 - result.burstinessScore)}.`}
            />
            <FreeScoreBar
              label="Low function-word density"
              value={result.functionWordScore}
              bandKey="functionWord"
              explanation={'Measures how often you use natural connecting words like "the", "and", "but", "with", "that", "which". AI text often skips these in favour of direct, declarative sentences.'}
              foundNote={`Score above 25 = worth noting. Your score: ${Math.round(result.functionWordScore)}.`}
            />

            <div style={{ marginTop: "22px" }}>
              <div style={labelStyle}>Structural patterns found</div>
              {result.patterns.length ? result.patterns.map((p, i) => (
                <div key={i} style={{ marginBottom: "10px", paddingLeft: "10px", borderLeft: "2px solid #ddd" }}>
                  <div style={{ fontSize: "12px", color: "#333", fontWeight: "600" }}>{p}</div>
                  {FREE_PATTERN_EXPLANATIONS[p] && <div style={{ fontSize: "12px", color: "#777", marginTop: "2px", lineHeight: "1.5" }}>{FREE_PATTERN_EXPLANATIONS[p]}</div>}
                </div>
              )) : (
                <div style={{ fontSize: "12px", color: "#4a8a4a" }}>✓ None of HumanKind's known AI structural patterns were found in your text.</div>
              )}
            </div>

            <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #eee" }}>
              <button onClick={shareResult} style={{
                background: "none", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 14px", minHeight: "44px",
                fontSize: "12px", color: "#555", cursor: "pointer"
              }}>{shareCopied ? "✓ Copied to clipboard" : "Share this result"}</button>
            </div>
          </div>
        )}

        {result && ctaCopy && (
          <div style={{ marginTop: "32px", textAlign: "center", padding: "32px", background: "#0A1120", border: "0.5px solid #1E2D42", borderRadius: "10px", boxSizing: "border-box" }}>
            <h2 style={{ fontSize: "19px", color: "#E8EDF5", fontWeight: "600", marginBottom: "8px" }}>{ctaCopy.heading}</h2>
            <p style={{ fontSize: "13px", color: "#8BAFC9", marginBottom: "20px", lineHeight: "1.6" }}>
              {ctaCopy.body}
            </p>
            <button onClick={goRevoice} style={{
              padding: "12px 28px", minHeight: "44px", fontSize: "14px", fontWeight: "700", borderRadius: "7px", border: "none",
              background: "#00B4D8", color: "#0A1120", cursor: "pointer"
            }}>{ctaCopy.button}</button>
            <div style={{ fontSize: "12px", color: "#5a86a8", marginTop: "14px" }}>Plans from $14.99/month · 5,000 bonus words on signup</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Must match PLAN_WORDS in api/stripe/webhook.php - these are the real plan
// slugs used everywhere else (Stripe plan matching, word allowances), not
// placeholder labels. Picking one here also grants the matching word
// allowance server-side (see update_user.php) - it's not just a label.
const PLAN_OPTIONS = [
  { value: "none", label: "No Plan" },
  { value: "starter", label: "Starter (37,500 words/mo)" },
  { value: "pro", label: "Pro (125,000 words/mo)" },
  { value: "max", label: "Max (300,000 words/mo)" },
];

const AdminUserRow = ({ u, onChange }) => {
  const [resetting, setResetting] = useState(false);
  const [rowMsg, setRowMsg] = useState(""); // status/error feedback for both patch() and resetPassword()

  const patch = async (fields) => {
    try {
      const res = await fetch(`${OLLAMA_URL}/api/admin/update_user.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: u.id, ...fields }),
      });
      const data = await res.json();
      if (res.ok) { onChange(data.user); setRowMsg(""); }
      else setRowMsg(data.error || "Update failed.");
    } catch {
      setRowMsg("Couldn't reach the server.");
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset the password for ${u.email}? A new password will be emailed to them and they'll be signed out everywhere.`)) return;
    setResetting(true); setRowMsg("");
    try {
      const res = await fetch(`${OLLAMA_URL}/api/admin/reset_password.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: u.id }),
      });
      const data = await res.json();
      setRowMsg(res.ok ? "New password sent." : (data.error || "Failed."));
    } catch {
      setRowMsg("Couldn't reach the server.");
    } finally {
      setResetting(false);
    }
  };

  const expiryValue = u.expires_at ? new Date(u.expires_at).toISOString().slice(0, 10) : "";
  const isExpired = u.expires_at && new Date(u.expires_at) < new Date();

  return (
    <tr style={{ borderBottom: "0.5px solid #1E2D42" }}>
      <td style={{ padding: "8px 12px", color: "#E8EDF5" }}>{u.email}</td>
      <td style={{ padding: "8px 12px" }}>
        <select value={u.plan} onChange={e => patch({ plan: e.target.value })}
          style={{ fontSize: "12px", padding: "4px 8px", border: "0.5px solid #1E2D42", borderRadius: "5px", background: "#111D2C", color: "#8BAFC9" }}>
          {PLAN_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </td>
      <td style={{ padding: "8px 12px" }}>
        <input type="date" value={expiryValue} onChange={e => patch({ expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
          style={{ fontSize: "12px", padding: "4px 8px", border: "0.5px solid #1E2D42", borderRadius: "5px", background: "#111D2C", color: isExpired ? "#F87171" : "#8BAFC9" }} />
        {isExpired && <span style={{ color: "#F87171", fontSize: "11px", marginLeft: "6px" }}>Expired</span>}
      </td>
      <td style={{ padding: "8px 12px", color: "#8BAFC9" }}>{u.is_superadmin ? "✓" : ""}</td>
      <td style={{ padding: "8px 12px", color: "#4A7FA5" }}>{new Date(u.created_at).toLocaleDateString()}</td>
      <td style={{ padding: "8px 12px" }}>
        <button onClick={resetPassword} disabled={resetting} style={{
          fontSize: "11px", padding: "5px 10px", borderRadius: "5px", border: "0.5px solid #1E2D42",
          background: "transparent", color: "#8BAFC9", cursor: resetting ? "default" : "pointer"
        }}>{resetting ? "..." : "Reset Password"}</button>
        {rowMsg && <div style={{ fontSize: "10px", color: rowMsg.includes("sent") ? "#4A7FA5" : "#F87171", marginTop: "3px" }}>{rowMsg}</div>}
      </td>
    </tr>
  );
};

// ─── HELP PAGE ─────────────────────────────────────────────────────────────

const FEATURE_TABLE = [
  { feature: "Humanize", does: "Rewrites AI-generated text so it no longer matches AI-detector patterns — vocabulary, sentence rhythm, structure.", benefit: "Turns flagged AI text into something that reads as genuinely human-written." },
  { feature: "AI Score", does: "Runs 8 separate signals (4 computed directly, 4 LLM-judged) and averages them into one 0-100 score.", benefit: "See exactly which patterns are driving your AI-detection risk, not just a single opaque number." },
  { feature: "Invisible Character Removal", does: "Automatically strips zero-width characters and exotic Unicode spaces some AI tools inject.", benefit: "Removes a hidden AI fingerprint you can't even see, before it ever reaches the model." },
  { feature: "File Metadata Check & Clean", does: "Scans .docx/.pdf hidden document properties for AI-tool tags (e.g. \"ChatGPT\"), and can strip them.", benefit: "Removes the giveaway that's invisible in the text itself but sits in the file's properties." },
  { feature: "Voice Profiles", does: "Save a sample of your own writing; the model matches its sentence length, vocabulary, and tone.", benefit: "Output sounds like *you*, not a generic \"human-sounding\" template everyone else is also using." },
  { feature: "Context Controls", does: "Six settings (Writing Goal, Audience, Formality, Tone, Detection Resistance, Persona) shape the rewrite.", benefit: "Tailored output for the actual audience and purpose, instead of one-size-fits-all rewriting." },
  { feature: "Detection Resistance", does: "Adds a targeted instruction aimed at the specific signals GPTZero/Turnitin-style detectors weigh heavily.", benefit: "An extra edge against the two most common classroom/publishing detectors specifically." },
  { feature: "Reading-Level Ceiling", does: "When Audience is set, caps output complexity to that audience's grade-level range and enforces it after generation if needed.", benefit: "Output is genuinely readable for who it's for, not needlessly dense just because Formality is set high." },
  { feature: "Redline / Change Log", does: "Shows a word-level diff and a plain-English list of what changed.", benefit: "See and verify exactly what was altered, rather than trusting a black box." },
  { feature: "Fact Lock", does: "Protects names, numbers, quotes, and links from being altered by any pass, including follow-up cleanup passes.", benefit: "Rewrite freely without worrying a date, price, or name gets silently changed." },
  { feature: "Export", does: "Download the result as .txt, .md, or .doc.", benefit: "Get the finished text straight into whatever you're using it for." },
];

const HelpTable = () => (
  <div style={{ overflowX: "auto", border: "0.5px solid #1E2D42", borderRadius: "8px", marginBottom: "40px" }}>
    <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "0.5px solid #1E2D42", textAlign: "left", background: "#111D2C" }}>
          <th style={{ padding: "10px 14px", color: "#4A7FA5", fontWeight: "600" }}>Feature</th>
          <th style={{ padding: "10px 14px", color: "#4A7FA5", fontWeight: "600" }}>What it does</th>
          <th style={{ padding: "10px 14px", color: "#4A7FA5", fontWeight: "600" }}>Benefit to you</th>
        </tr>
      </thead>
      <tbody>
        {FEATURE_TABLE.map(row => (
          <tr key={row.feature} style={{ borderBottom: "0.5px solid #1E2D42" }}>
            <td style={{ padding: "10px 14px", color: "#E8EDF5", fontWeight: "600", whiteSpace: "nowrap" }}>{row.feature}</td>
            <td style={{ padding: "10px 14px", color: "#8BAFC9" }}>{row.does}</td>
            <td style={{ padding: "10px 14px", color: "#8BAFC9" }}>{row.benefit}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const HelpSection = ({ title, children }) => (
  <div style={{ marginBottom: "32px" }}>
    <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#00B4D8", marginBottom: "12px" }}>{title}</div>
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</div>
  </div>
);

const HelpRow = ({ name, does, benefit }) => (
  <div style={{ padding: "12px 16px", background: "#0A1120", border: "0.5px solid #1E2D42", borderRadius: "8px" }}>
    <div style={{ fontSize: "13px", fontWeight: "600", color: "#E8EDF5", marginBottom: "4px" }}>{name}</div>
    <div style={{ fontSize: "12px", color: "#8BAFC9", lineHeight: "1.6", marginBottom: "4px" }}>{does}</div>
    <div style={{ fontSize: "12px", color: "#4A7FA5", lineHeight: "1.6" }}><span style={{ color: "#00B4D8" }}>Benefit: </span>{benefit}</div>
  </div>
);

const WALKTHROUGH_STEPS = [
  { title: "Paste your text", body: "Paste the AI-generated text into the Original Text box on the left. Nothing happens automatically at this point — no scanning, no scoring, no network call." },
  { title: "Set Context Controls (optional, but worth it)", body: "Set Writing Goal, Audience, Formality, Tone, and Persona so the rewrite is shaped for what you're actually producing, instead of a generic pass. If you skip this and click Humanize, you'll get a one-time prompt asking if you want to go back and set them first." },
  { title: "Set Target Words if you need a specific length", body: "Only set this if your input needs to grow or shrink to a real target — leaving an old value in here from a previous piece of text is the most common cause of a Humanize run taking much longer than expected, since the app will try to expand toward it." },
  { title: "Turn on Detection Resistance and/or Lock Names/Numbers if relevant", body: "Boosted Detection Resistance targets GPTZero/Turnitin specifically. Lock Names/Numbers protects any names, numbers, quotes, or links in your text from being altered anywhere in the pipeline." },
  { title: "(Optional) Check AI Score on the original first", body: "Run Check AI Score before humanizing to see your starting risk level. If it's already low, you may not need a full rewrite at all." },
  { title: "Click Humanize", body: "Runs the full pipeline: invisible-character cleanup, the rewrite itself, then any needed follow-up passes (expanding to your word target, removing flagged words, enforcing the reading-level ceiling) automatically." },
  { title: "Review the result", body: "Read the Humanized tab to confirm it still says what you meant. Check Redline to see exactly what changed word-by-word, or Change Log for a plain-English summary." },
  { title: "Check the AI Score's After column", body: "Confirms your new risk level and readability grade. If Audience was set, it'll tell you whether the reading-level target was actually met." },
  { title: "Not happy? Adjust and re-run, or edit by hand", body: "Change any setting and click Humanize again — it always regenerates from your original input, not the previous output, so changes don't compound. You can also edit the Humanized text directly in the box." },
  { title: "Copy or Export", body: "Copy to clipboard, or Export as .txt/.md/.doc once you're satisfied." },
  { title: "If your source was a saved file, check it too", body: "If the AI text came from a .docx or .pdf someone generated (not just pasted), use gear menu → Check File to scan for AI-tool tags hiding in the file's own metadata, and clean it if found — humanizing the text alone doesn't touch the file wrapper." },
];

const HelpStep = ({ n, title, body }) => (
  <div style={{ display: "flex", gap: "14px", marginBottom: "16px" }}>
    <div style={{ flexShrink: 0, width: "26px", height: "26px", borderRadius: "50%", background: "#00B4D8", color: "#0A1120", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center" }}>{n}</div>
    <div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: "#E8EDF5", marginBottom: "3px" }}>{title}</div>
      <div style={{ fontSize: "12px", color: "#8BAFC9", lineHeight: "1.6" }}>{body}</div>
    </div>
  </div>
);

const HelpPage = () => (
  <div style={{ minHeight: "100vh", fontFamily: "Georgia, serif", background: "#0F1724", color: "#E8EDF5" }}>
    <style>{`* { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; }`}</style>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", background: "#0A1120", borderBottom: "0.5px solid #1E2D42" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg>
        </div>
        <div>
          <span style={{ fontSize: "15px", color: "#E8EDF5", fontWeight: "600" }}>HumanKind</span>
          <span style={{ fontSize: "13px", color: "#4A7FA5" }}> · Help</span>
        </div>
      </div>
      <a href="/app" style={{ fontSize: "13px", padding: "6px 14px", border: "0.5px solid #1E2D42", borderRadius: "6px", color: "#8BAFC9", textDecoration: "none" }}>← Back to App</a>
    </div>

    <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      <h1 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>What Humankind does, and why it helps</h1>
      <p style={{ fontSize: "13px", color: "#8BAFC9", lineHeight: "1.7", marginBottom: "24px" }}>
        Humankind rewrites AI-generated text so it reads as genuinely human-written, and gives you an honest, transparent read on how likely it is to get flagged. Everything below is real — every score shown is either computed directly from your text or judged by the model, and it's always labeled which. Nothing here claims to detect or remove a cryptographic AI watermark; no tool can do that.
      </p>

      <h2 style={{ fontSize: "17px", fontWeight: "700", marginBottom: "16px" }}>Start to finish, step by step</h2>
      <div style={{ marginBottom: "36px" }}>
        {WALKTHROUGH_STEPS.map((s, i) => <HelpStep key={i} n={i + 1} title={s.title} body={s.body} />)}
      </div>

      <HelpTable />

      <h2 style={{ fontSize: "17px", fontWeight: "700", marginBottom: "16px" }}>Every button and switch</h2>

      <HelpSection title="Header">
        <HelpRow name="🔒 Secure & Private badge" does="A trust signal only — not clickable, no action." benefit="Reassurance that your pasted text isn't shared beyond generating your result." />
        <HelpRow name="⚙️ Settings (gear icon)" does="Opens a small menu with Error Log and Check File." benefit="Keeps diagnostic and file-checking tools out of the way until you need them." />
        <HelpRow name="↳ Error Log" does="Shows every error the app has logged, most recent first, with a Refresh, Copy All, and Clear action." benefit="If something goes wrong, this shows you (or support) exactly what happened instead of a dead end." />
        <HelpRow name="↳ Check File" does="Upload a .docx or .pdf to scan its hidden document properties for AI-tool tags, with a Clean & Download option if any are found." benefit="Catches and removes the AI giveaway hiding in a file's metadata, not just its visible text." />
        <HelpRow name="Voice Profiles" does="Opens a drawer to save, select, or delete saved writing samples used to match your personal voice." benefit="Every Humanize run can sound like you specifically, not a generic template." />
        <HelpRow name="Sign out" does="Ends your session." benefit="Standard account security." />
      </HelpSection>

      <HelpSection title="Context Controls">
        <HelpRow name="Writing Goal" does="Shapes tone, structure, and vocabulary for a specific content type (SEO Blog, Academic, Business, Creative, Social Media, Email)." benefit="The rewrite fits the actual format you're producing, not a generic essay shape." />
        <HelpRow name="Audience" does="Calibrates vocabulary/complexity for who's reading (General, Professional, Expert, Children), and sets a soft target reading-level grade range that's enforced after generation if the model overshoots it." benefit="Output that's genuinely readable for its intended reader, with a real ceiling, not just a vague instruction." />
        <HelpRow name="Formality" does="Sets the register (Casual, Conversational, Professional, Formal) maintained throughout." benefit="Consistent tone matching the context you're writing for." />
        <HelpRow name="Tone" does="Sets the emotional register (Neutral, Persuasive, Empathetic, Authoritative, Enthusiastic)." benefit="Matches the feeling of the piece to its purpose." />
        <HelpRow name="Detection Resistance" does="Off, or Boosted — adds sentence-length variance (and rarer vocabulary, unless a reading-level ceiling is active) targeting the specific signals GPTZero and Turnitin weigh heavily." benefit="A concrete edge against the two most common detectors, not a vague promise." />
        <HelpRow name="Persona" does="Applies a specific voice (Academic, Casual Blog, Reddit Comment, ESL Speaker)." benefit="Output matches a recognizable, specific human voice rather than generic “AI trying to sound human.”" />
        <HelpRow name="Intensity" does="Light / Medium / Aggressive — how hard the rewrite works to restructure the text." benefit="Control the trade-off between a light touch-up and a full rewrite." />
        <HelpRow name="Humanize Speed" does="Thorough runs every AI-score polish pass (banned words, sentence-pattern fixes, rhythm, reading level) after the main rewrite. Quick skips all of them for a faster result." benefit="Trade a few extra seconds of polish for speed when you're in a hurry — Target Words is always honored either way." />
      </HelpSection>

      <HelpSection title="Settings Bar">
        <HelpRow name="Target Words" does="A hard word-count target; the app automatically expands the output over up to 2 extra passes if it falls short." benefit="Hit a required length (e.g. an assignment minimum) without manually padding it yourself." />
        <HelpRow name="Script Mode" does="Formats output for reading aloud — short lines, natural spoken pauses." benefit="Ready for video/voiceover scripts instead of formatted for silent reading." />
        <HelpRow name="Breath Pauses" does="(Only visible with Script Mode on) adds natural trailing pauses (“...”) and breath-point periods." benefit="Sounds like natural speech, not a script being read flatly." />
        <HelpRow name="Change Log" does="Toggles whether a plain-English list of what changed is generated and shown." benefit="Transparency into the edit, or skip it for a faster result if you don't need it." />
        <HelpRow name="Lock Names/Numbers" does="Protects names, numbers, quotes, and links from being altered by the rewrite or any follow-up cleanup pass." benefit="Rewrite freely without a date, price, or name silently changing underneath you." />
        <HelpRow name="Casual Grammar" does="Allows minor natural looseness — the occasional fragment, run-on, or sentence starting with “And”/“So”." benefit="Avoids overly polished output that itself reads as suspiciously perfect." />
        <HelpRow name="Prompt Peek" does="Shows the exact system prompt the model receives for your current settings." benefit="Full transparency into exactly what's being asked of the model — nothing hidden." />
      </HelpSection>

      <HelpSection title="Output Tabs & History">
        <HelpRow name="Humanized" does="The rewritten text, editable in place." benefit="Your finished result, ready to copy or refine further by hand." />
        <HelpRow name="Redline" does="(Appears once you've humanized) a word-level diff between original and humanized." benefit="See precisely what changed, word by word." />
        <HelpRow name="Change Log" does="(Appears if the Change Log toggle is on and one was generated) a plain-English summary of the edits." benefit="A quick, readable summary instead of parsing a diff yourself." />
        <HelpRow name="AI Score" does="The Before/After analysis panels, described in the feature table above." benefit="Verify your result before you use it, not after someone else flags it." />
        <HelpRow name="History (H1/E1/H2...)" does="Every humanize (H) or manual edit (E) creates a restorable version." benefit="Experiment freely — you can always step back to an earlier version." />
      </HelpSection>

      <HelpSection title="Actions">
        <HelpRow name="Humanize" does="Runs the full rewrite pipeline: cleanup, generation, and any needed follow-up passes (expand to target words, remove flagged words, enforce reading level)." benefit="One click for AI-tell removal, length control, and readability, all handled together." />
        <HelpRow name="Check AI Score" does="Scores the original text on its own, without touching any existing humanized result." benefit="Check your source text's risk before committing tokens to a full rewrite." />
        <HelpRow name="Copy" does="Copies the humanized text to your clipboard." benefit="Quick handoff to wherever you're using the result." />
        <HelpRow name="Export ▾" does="Download as .txt, .md, or .doc." benefit="Get the file format you actually need." />
        <HelpRow name="Clear" does="Resets both text panels." benefit="Start fresh without reloading the page." />
      </HelpSection>
    </div>
  </div>
);

const AdminPage = ({ currentUser, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createdMsg, setCreatedMsg] = useState("");

  const loadUsers = async () => {
    setLoading(true); setLoadError("");
    try {
      const res = await fetch(`${OLLAMA_URL}/api/admin/list_users.php`, { credentials: "include" });
      const data = await res.json();
      if (res.ok) setUsers(data.users || []);
      else setLoadError(data.error || "Couldn't load the user list.");
    } catch {
      setLoadError("Couldn't reach the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateError(""); setCreatedMsg("");
    try {
      const res = await fetch(`${OLLAMA_URL}/api/admin/create_user.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.error || "Couldn't create account."); return; }
      setCreatedMsg(`Account created for ${data.user.email} — password sent via MailerLite.`);
      setNewEmail("");
      loadUsers();
    } catch {
      setCreateError("Couldn't reach the server. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  if (!currentUser?.is_superadmin) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", gap: "12px", background: "#0F1724" }}>
        <div style={{ fontSize: "16px", color: "#F87171" }}>Admin access required</div>
        <a href="/app" style={{ fontSize: "13px", color: "#8BAFC9" }}>← Back to app</a>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", fontFamily: "Georgia, serif", background: "#0F1724" }}>
      <style>{`* { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; } ::placeholder { color: #4A7FA5 !important; opacity: 1; }`}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", background: "#0A1120", borderBottom: "0.5px solid #1E2D42" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg>
          </div>
          <div>
            <span style={{ fontSize: "15px", color: "#E8EDF5", fontWeight: "600" }}>HumanKind</span>
            <span style={{ fontSize: "13px", color: "#4A7FA5" }}> · Admin</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <a href="/app" style={{ fontSize: "13px", padding: "6px 14px", border: "0.5px solid #1E2D42", borderRadius: "6px", color: "#8BAFC9", textDecoration: "none" }}>← Back to App</a>
          <button onClick={onLogout} style={{ fontSize: "13px", padding: "6px 14px", border: "0.5px solid #1E2D42", borderRadius: "6px", background: "transparent", color: "#8BAFC9", cursor: "pointer" }}>Sign Out</button>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <form onSubmit={createUser} style={{ display: "flex", gap: "8px", marginBottom: "18px", alignItems: "center" }}>
          <input type="email" placeholder="new.customer@email.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} required
            style={{ padding: "8px 12px", fontSize: "13px", border: "0.5px solid #1E2D42", borderRadius: "6px", outline: "none", width: "260px", background: "#111D2C", color: "#E8EDF5" }} />
          <button type="submit" disabled={creating} style={{
            fontSize: "13px", fontWeight: "700", padding: "9px 18px", borderRadius: "7px", border: "none",
            background: creating ? "#0A6E85" : "#00B4D8", color: "#0A1120", cursor: creating ? "default" : "pointer"
          }}>{creating ? "Creating..." : "Create Account"}</button>
        </form>
        {createError && <div style={{ fontSize: "12px", color: "#F87171", marginBottom: "14px" }}>{createError}</div>}
        {createdMsg && <div style={{ fontSize: "12px", color: "#4ADE80", marginBottom: "14px" }}>{createdMsg}</div>}

        {loading ? (
          <div style={{ fontSize: "12px", color: "#4A7FA5" }}>Loading users...</div>
        ) : loadError ? (
          <div style={{ fontSize: "12px", color: "#F87171" }}>
            {loadError}{" "}
            <button onClick={loadUsers} style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "5px", border: "0.5px solid #1E2D42", background: "transparent", color: "#8BAFC9", cursor: "pointer" }}>Retry</button>
          </div>
        ) : (
          <div style={{ border: "0.5px solid #1E2D42", borderRadius: "8px", overflow: "auto" }}>
            <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid #1E2D42", textAlign: "left" }}>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}>Email</th>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}>Plan</th>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}>Expires</th>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}>Admin</th>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}>Created</th>
                  <th style={{ padding: "8px 12px", color: "#4A7FA5", fontWeight: "600" }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <AdminUserRow key={u.id} u={u} onChange={updated => setUsers(prev => prev.map(x => x.id === updated.id ? updated : x))} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── HIGHLIGHT ENGINE ────────────────────────────────────────────────────────

const HIGHLIGHT_RULES = [
  {
    id: "emdash",
    label: "Em dash",
    color: "#ffd6d6",
    border: "#e08080",
    tip: "Em dashes are a strong AI tell. Replace with a comma, period, or conjunction.",
    kind: "pattern",
    pattern: /—/g,
  },
  {
    id: "banned",
    label: "Banned word",
    color: "#ffe4c0",
    border: "#e0a060",
    tip: "This word appears on the banned list — common in AI-generated text.",
    kind: "words",
    words: BANNED_WORDS,
  },
  {
    id: "formal",
    label: "Formal phrase",
    color: "#ddeeff",
    border: "#6090c0",
    tip: "This phrase sounds formal or robotic — typical of AI writing.",
    // Was highlighting every single occurrence of anything in the raw
    // FORMAL_PHRASES list, which disagreed with the actual fix pass -
    // findFormalPhrases requires 2+ total hits (recurs, not one-off) before
    // treating anything as a real issue, calibrated against a real human
    // reference text that had one legitimate use of "on the other hand."
    // Highlighting a phrase the fix pass would correctly leave alone is a
    // real inconsistency, not just a cosmetic one - it tells a user to fix
    // something that isn't actually flagged as a problem.
    kind: "dynamicPhrases",
    find: (text) => findFormalPhrases(text).filter(p => FORMAL_PHRASES.includes(p)),
  },
  {
    id: "hedging",
    label: "Hedging phrase",
    color: "#f0e0f5",
    border: "#a070b0",
    tip: "Vague, unsupported attribution — a common AI tell when it has no real source.",
    // Same fix as "formal" above - shares findFormalPhrases's calibrated 2+
    // threshold (it checks both lists combined) rather than flagging any
    // single occurrence from the raw HEDGING_PHRASES list.
    kind: "dynamicPhrases",
    find: (text) => findFormalPhrases(text).filter(p => HEDGING_PHRASES.includes(p)),
  },
  {
    id: "intensifier",
    label: "Overused intensifier",
    color: "#fff2c0",
    border: "#c0a020",
    tip: "Overused intensifier — usually weaker than being specific.",
    // Was flagging every single occurrence of "very"/"really" etc., which
    // false-positived on the confirmed-human reference text - now shares
    // findOverusedIntensifiers' density threshold instead.
    kind: "dynamicWords",
    find: findOverusedIntensifiers,
  },
  {
    id: "repeatedOpener",
    label: "Repeated sentence opener",
    color: "#e0f0ff",
    border: "#5090c0",
    tip: "These sentences share the same opening words - a detectable parallel-structure AI pattern.",
    kind: "sentences",
    find: findRepeatedOpeners,
  },
  {
    id: "tricolon",
    label: "Balanced three-item list",
    color: "#f5e8d0",
    border: "#c09050",
    tip: "A matched-length three-item list - a detectable AI \"rule of three\" pattern, especially when it recurs.",
    kind: "sentences",
    find: findTricolonSymmetry,
  },
  {
    id: "flourish",
    label: "Stacked-clause flourish",
    color: "#ffe0f0",
    border: "#c05090",
    tip: "A long sentence stacking a because/since/when clause with an \"and\" or -ing extension, ending in a rhetorical \"that...\" payoff - a strong, specific AI tell confirmed against a real third-party detector scan.",
    kind: "sentences",
    find: (text) => { const hits = findRhetoricalFlourishSentences(text); return hits ? { sentences: hits } : null; },
  },
  // The rules below were added alongside their detector functions - kept in
  // sync here so anything the fix pipeline knows how to find is also visible
  // to a user editing the output by hand, not just fixed automatically.
  {
    id: "choppyRun",
    label: "Choppy sentence run",
    color: "#d0f0e0",
    border: "#50a080",
    tip: "3+ short, grammatically clean sentences in a row - \"polished choppiness,\" a strong AI tell.",
    kind: "sentences",
    find: (text) => { const runs = findChoppyRun(text); return runs ? { sentences: runs.flat() } : null; },
  },
  {
    id: "twistRun",
    label: "Repeated twist pattern",
    color: "#ffe8d0",
    border: "#d09050",
    tip: "3+ sentences in a row sharing the same \"[action], but/and [twist]\" shape - uniform even though the sentences aren't short.",
    kind: "sentences",
    find: (text) => { const runs = findTwistRun(text); return runs ? { sentences: runs.flat() } : null; },
  },
  {
    id: "fragmentStacking",
    label: "Stacked fragment",
    color: "#e0e0ff",
    border: "#8080c0",
    tip: "A quantifier-opener sentence fragment with no main verb, ending in a relative/participial tail - a detectable AI rhetorical device.",
    kind: "sentences",
    find: (text) => { const hits = findFragmentStacking(text); return hits ? { sentences: hits } : null; },
  },
  {
    id: "notOnlyButAlso",
    label: "'Not only...but also'",
    color: "#f0d0e8",
    border: "#b05090",
    tip: "The formulaic \"not only X, but also Y\" construction - real writers rarely reach for this outside a single emphatic use.",
    kind: "sentences",
    find: (text) => { const hits = findNotOnlyButAlso(text); return hits ? { sentences: hits } : null; },
  },
  {
    id: "summaryClosing",
    label: "Summary-style ending",
    color: "#fff0d0",
    border: "#c09030",
    tip: "The final paragraph restates or summarizes rather than ending on something open - a strong, specific AI closing tell.",
    kind: "sentences",
    find: (text) => { const hit = findSummaryClosing(text); return hit ? { sentences: [hit.paragraph] } : null; },
  },
  {
    id: "paragraphSymmetry",
    label: "Matched-template paragraphs",
    color: "#e8e0f5",
    border: "#9070b0",
    tip: "Consecutive paragraphs following the same template/shape - a detectable structural AI pattern.",
    kind: "sentences",
    find: (text) => { const result = findParagraphSymmetry(text); return result ? { sentences: result.paragraphs } : null; },
  },
  {
    id: "paragraphOpener",
    label: "Repeated paragraph opener",
    color: "#d0f0f0",
    border: "#50a0a0",
    tip: "Multiple paragraphs open the same way - a detectable structural AI pattern.",
    kind: "sentences",
    find: (text) => { const result = findParagraphOpenerViolations(text); return result ? { sentences: result.offending } : null; },
  },
  {
    id: "vagueWords",
    label: "Overused vague word",
    color: "#f5f0d0",
    border: "#b0a050",
    tip: "A generic, low-information word repeated often enough to be a detectable predictability signal.",
    kind: "dynamicWords",
    find: findOverusedVagueWords,
  },
  // Sentence-level flags for the two "Uniform rhythm" / "Low function-word
  // density" score rows. The same detectors already feed the cleanup pass;
  // these make the specific sentences visible and editable in the output.
  {
    id: "lowFunctionWord",
    label: "Dense, connective-light sentence",
    color: "#e8f0d8",
    border: "#7a9a50",
    tip: "Packs in lots of content words and few connectives (and, but, a pronoun, a contraction) - reads clipped and mechanical.",
    kind: "sentences",
    find: (text) => { const hits = findLowFunctionWordSentences(text); return hits ? { sentences: hits } : null; },
  },
  {
    id: "sentenceTooLong",
    label: "Overly long sentence",
    color: "#f8e0e0",
    border: "#b05050",
    tip: "Sentences here average well above the human-typical length. Splitting the longest ones is the usual fix.",
    kind: "sentences",
    find: (text) => { const hit = findLongSentenceOvershoot(text); return hit ? { sentences: hit.targets } : null; },
  },
  {
    id: "sentenceTooShort",
    label: "Choppy short sentence",
    color: "#e0ecf8",
    border: "#5080b0",
    tip: "Sentences here are much shorter than typical human writing - merging a pair usually reads more naturally.",
    kind: "sentences",
    find: (text) => { const hit = findShortSentenceUndershoot(text); return hit ? { sentences: hit.pairs.flatMap(p => [p.first, p.second]) } : null; },
  },
];

// Matcher functions: each takes (text, rule) and returns [{start, end}] spans.
const RULE_MATCHERS = {
  pattern: (text, rule) => {
    const spans = [];
    const re = new RegExp(rule.pattern.source, "g");
    let m;
    while ((m = re.exec(text)) !== null) spans.push({ start: m.index, end: m.index + m[0].length });
    return spans;
  },
  words: (text, rule) => {
    const spans = [];
    for (const word of rule.words) {
      const re = new RegExp("\\b" + word.replace(/[-]/g, "[-]") + "\\b", "gi");
      let m;
      while ((m = re.exec(text)) !== null) spans.push({ start: m.index, end: m.index + m[0].length });
    }
    return spans;
  },
  phrases: (text, rule) => {
    const spans = [];
    for (const phrase of rule.phrases) {
      const re = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
      let m;
      while ((m = re.exec(text)) !== null) spans.push({ start: m.index, end: m.index + m[0].length });
    }
    return spans;
  },
  // Like "phrases" above, but the phrase list is computed per-text (via
  // rule.find) rather than a fixed array - needed for the formal/hedging
  // rules, which only highlight phrases findFormalPhrases actually flagged
  // (2+ total hits), not every occurrence of anything in the raw list.
  dynamicPhrases: (text, rule) => {
    const spans = [];
    const phrases = rule.find(text);
    if (!phrases || !phrases.length) return spans;
    for (const phrase of phrases) {
      const re = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
      let m;
      while ((m = re.exec(text)) !== null) spans.push({ start: m.index, end: m.index + m[0].length });
    }
    return spans;
  },
  // Reuses the actual detector functions (findRepeatedOpeners/findTricolonSymmetry)
  // rather than a separate word/phrase list, so what's highlighted here always
  // matches what the correction pipeline itself would flag and try to fix.
  sentences: (text, rule) => {
    const spans = [];
    const result = rule.find(text);
    if (!result) return spans;
    const sentenceList = result.sentences || [
      ...(result.hits ? result.hits.map(h => h.sentence) : []),
      ...(result.clustered || []),
      ...(result.anaphora ? [result.anaphora.sentence] : []),
    ];
    for (const sentence of sentenceList) {
      const idx = text.indexOf(sentence);
      if (idx !== -1) spans.push({ start: idx, end: idx + sentence.length });
    }
    return spans;
  },
  // Like "words" above, but the word list is computed per-text (via
  // rule.find) rather than a fixed array - needed for findOverusedVagueWords,
  // whose flagged words depend on which ones actually recur often enough in
  // THIS text, not a static list.
  dynamicWords: (text, rule) => {
    const spans = [];
    const hits = rule.find(text);
    if (!hits) return spans;
    for (const { word } of hits) {
      const re = new RegExp("\\b" + word + "\\b", "gi");
      let m;
      while ((m = re.exec(text)) !== null) spans.push({ start: m.index, end: m.index + m[0].length });
    }
    return spans;
  },
};

const buildHighlightedHTML = (text) => {
  if (!text) return "";

  // We'll build an array of {start, end, ruleId, label, color, border, tip}
  const spans = [];
  for (const rule of HIGHLIGHT_RULES) {
    const matcher = RULE_MATCHERS[rule.kind];
    if (!matcher) continue;
    for (const span of matcher(text, rule)) spans.push({ ...span, ...rule });
  }

  // Must return the same parts-array shape as the highlighted case below - a
  // bare string here sends HighlightedText down its read-only fallback, which
  // silently disables editing and hides the "no issues" banner whenever a
  // text has zero highlight matches.
  if (spans.length === 0) return [{ type: "text", text }];

  // Sort by start, remove overlaps (keep first)
  spans.sort((a, b) => a.start - b.start);
  const clean = [];
  let cursor = 0;
  for (const sp of spans) {
    if (sp.start >= cursor) {
      clean.push(sp);
      cursor = sp.end;
    }
  }

  // Build output as array of React elements
  const parts = [];
  let pos = 0;
  for (const sp of clean) {
    if (sp.start > pos) parts.push({ type: "text", text: text.slice(pos, sp.start) });
    parts.push({ type: "mark", text: text.slice(sp.start, sp.end), rule: sp });
    pos = sp.end;
  }
  if (pos < text.length) parts.push({ type: "text", text: text.slice(pos) });
  return parts;
};

const EditableMarks = memo(({ parts, onChange }) => (
  <div
    contentEditable
    suppressContentEditableWarning
    onBlur={(e) => onChange && onChange(e.currentTarget.innerText)}
    style={{ fontSize: "14px", lineHeight: "1.8", color: "#111", whiteSpace: "pre-wrap", position: "relative", cursor: "text", outline: "none" }}
  >
    {parts.map((p, i) => {
      if (p.type === "text") return <span key={i}>{p.text}</span>;
      return (
        <span key={i} style={{ position: "relative", display: "inline" }}>
          <span
            data-issue-mark={p.rule.id}
            title={`${p.rule.label}: ${p.rule.tip}`}
            style={{
              background: p.rule.color,
              borderBottom: "2px solid " + p.rule.border,
              borderRadius: "2px",
              // Was "help" (a "?" cursor) - reasonable for a read-only
              // tooltip target, but this text is editable, so it hid the
              // normal text-insertion cursor a user needs to see where
              // clicking will actually place their edit point. The title
              // attribute's tooltip works purely off hover, not this cursor
              // style, so reverting it doesn't lose the hover explanation.
              cursor: "text",
              padding: "1px 2px",
            }}
          >{p.text}</span>
        </span>
      );
    })}
  </div>
));

const HighlightedText = ({ text, onChange, showHighlights = true }) => {
  // When highlighting is off, still route through the same editable-parts
  // shape (a single unmarked text part) rather than the plain non-editable
  // fallback below - editing must keep working either way.
  const parts = useMemo(
    () => (showHighlights ? buildHighlightedHTML(text) : (text ? [{ type: "text", text }] : "")),
    [text, showHighlights]
  );

  // "N issues found" was previously just a passive count with no way to
  // actually get to any of them beyond scanning the whole text by eye - this
  // lets a user step through flagged spots directly, one click at a time.
  const containerRef = useRef(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const jumpToIssue = (delta) => {
    const el = containerRef.current;
    if (!el) return;
    const marks = el.querySelectorAll("[data-issue-mark]");
    if (!marks.length) return;
    const next = ((focusIdx + delta) % marks.length + marks.length) % marks.length;
    setFocusIdx(next);
    marks[next].scrollIntoView({ behavior: "smooth", block: "center" });
    marks[next].style.outline = "2px solid #333";
    setTimeout(() => { if (marks[next]) marks[next].style.outline = ""; }, 1200);
  };

  if (!Array.isArray(parts)) {
    return <div style={{ fontSize: "14px", lineHeight: "1.8", color: "#111", whiteSpace: "pre-wrap" }}>{text}</div>;
  }

  const issueCount = parts.filter(p => p.type === "mark").length;

  return (
    <div ref={containerRef}>
      {showHighlights && issueCount > 0 && (
        <div style={{ marginBottom: "10px", fontSize: "11px", color: "#888", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: "600", color: "#555" }}>{issueCount} issue{issueCount !== 1 ? "s" : ""} found</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <button type="button" onClick={() => jumpToIssue(-1)} title="Jump to previous issue" style={{ fontSize: "11px", padding: "2px 7px", borderRadius: "3px", border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>‹ Prev</button>
            <button type="button" onClick={() => jumpToIssue(1)} title="Jump to next issue" style={{ fontSize: "11px", padding: "2px 7px", borderRadius: "3px", border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Next ›</button>
          </span>
          {HIGHLIGHT_RULES.map(r => {
            const count = parts.filter(p => p.type === "mark" && p.rule.id === r.id).length;
            if (!count) return null;
            return (
              <span key={r.id} style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", background: r.color, border: "1px solid " + r.border, borderRadius: "2px", display: "inline-block" }} />
                <span>{r.label} ({count})</span>
              </span>
            );
          })}
        </div>
      )}
      {showHighlights && issueCount === 0 && (
        <div style={{ marginBottom: "10px", fontSize: "11px", color: "#4a8a4a" }}>✓ No issues detected in output</div>
      )}
      {/* key={text} forces a remount instead of a diff against contentEditable's own browser-mutated DOM - fixes a removeChild crash. */}
      <EditableMarks key={text} parts={parts} onChange={onChange} />
    </div>
  );
};

const RedlineDiff = ({ oldText, newText }) => {
  const diff = useMemo(() => computeWordDiff(oldText, newText), [oldText, newText]);
  return (
    <div style={{ fontSize: "14px", lineHeight: "1.8", whiteSpace: "pre-wrap" }}>
      {diff.map((d, i) => {
        if (d.type === "same") return <span key={i}>{d.text}</span>;
        if (d.type === "removed") return <span key={i} style={{ background: "#ffe0e0", color: "#a04040", textDecoration: "line-through" }}>{d.text}</span>;
        return <span key={i} style={{ background: "#e0f5e0", color: "#3a7a3a" }}>{d.text}</span>;
      })}
    </div>
  );
};

// ─── MAIN APP ────────────────────────────────────────────────────────────────

function MainApp({ onLogout }) {
  const [savedWorkingText] = useState(loadSavedWorkingText);
  const [input, setInput] = useState(() => {
    // Carries text forward from the free analyzer's "Revoice My Text" CTA
    // (see FreeAnalyzerPage) - the visitor already pasted and analyzed
    // this, so making them re-paste it after signing up would undercut the
    // whole point of that funnel. Takes priority over the locally-saved
    // working text since it reflects more recent, explicit intent; cleared
    // immediately so it doesn't leak into a later unrelated session.
    try {
      const fromFreeTool = sessionStorage.getItem("humankind_free_tool_text");
      if (fromFreeTool) {
        sessionStorage.removeItem("humankind_free_tool_text");
        return fromFreeTool;
      }
    } catch { /* sessionStorage unavailable - falls through to the saved working text */ }
    return savedWorkingText.input ?? "";
  });
  const [humanized, setHumanized] = useState(savedWorkingText.humanized ?? "");
  const [changelog, setChangelog] = useState(savedWorkingText.changelog ?? "");
  const [beforeAnalysis, setBeforeAnalysis] = useState(savedWorkingText.beforeAnalysis ?? null);
  const [afterAnalysis, setAfterAnalysis] = useState(savedWorkingText.afterAnalysis ?? null);
  const [loading, setLoading] = useState(false);
  const [humanizeStage, setHumanizeStage] = useState(""); // real per-stage progress text, shown while loading
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scoringAfter, setScoringAfter] = useState(false); // after-score runs in the background once text is ready, doesn't block the UI
  const [scoringOnly, setScoringOnly] = useState(false);
  const scoredInputRef = useRef(null); // which input string `beforeAnalysis` was computed for, so Humanize can reuse it instead of re-scoring
  const [error, setError] = useState("");
  const [cleanupNotice, setCleanupNotice] = useState("");
  const [versionHistory, setVersionHistory] = useState([]); // [{ text, source: "humanize"|"edit", time }]
  const [currentVersionIdx, setCurrentVersionIdx] = useState(-1);
  const currentVersionIdxRef = useRef(-1);

  const pushVersion = useCallback((text, source) => {
    // If we'd previously restored an older version, a new change discards the redo branch.
    setVersionHistory(prev => [...prev.slice(0, currentVersionIdxRef.current + 1), { text, source, time: Date.now() }]);
    currentVersionIdxRef.current += 1;
    setCurrentVersionIdx(currentVersionIdxRef.current);
  }, []);

  const restoreVersion = (idx) => {
    const v = versionHistory[idx];
    if (!v) return;
    setHumanized(v.text);
    currentVersionIdxRef.current = idx;
    setCurrentVersionIdx(idx);
  };
  const [activeTab, setActiveTab] = useState("output");
  const [copied, setCopied] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);

  // Context settings - restored from localStorage so users don't lose their
  // setup (and re-burn tokens re-running) between visits.
  const [savedSettings] = useState(loadSavedContextSettings);
  const [targetWords, setTargetWords] = useState(savedSettings.targetWords ?? "");
  const [writingGoal, setWritingGoal] = useState(savedSettings.writingGoal ?? "");
  const [audienceLevel, setAudienceLevel] = useState(savedSettings.audienceLevel ?? "");
  const [formality, setFormality] = useState(savedSettings.formality ?? "");
  const [emotionalTone, setEmotionalTone] = useState(savedSettings.emotionalTone ?? "");

  // Intensity & modes
  const [intensity, setIntensity] = useState(savedSettings.intensity ?? "aggressive");
  // Quick skips the follow-up AI-score polish passes (banned words, symmetry,
  // tricolon, burstiness, readability) for speed - Thorough runs all of them.
  // Expand (Target Words) always runs regardless, since that's honoring an
  // explicit user setting, not optional detection-polish.
  const [revoiceSpeed, setRevoiceSpeed] = useState(savedSettings.revoiceSpeed ?? "thorough");
  // Snapshot of which speed mode actually produced the CURRENTLY DISPLAYED
  // result - not the live revoiceSpeed value, which the user could change
  // after generating (same "snapshot settings at generation time" pattern
  // already used for afterA.generatedWith). Drives the Quick-mode caveat.
  const [resultWasQuick, setResultWasQuick] = useState(false);
  const [scriptMode, setScriptMode] = useState(savedSettings.scriptMode ?? false);
  const [showChangelog, setShowChangelog] = useState(true);
  const [factLockEnabled, setFactLockEnabled] = useState(savedSettings.factLockEnabled ?? false);
  const [bypassTarget, setBypassTarget] = useState(savedSettings.bypassTarget ?? "");
  const [imperfectionMode, setImperfectionMode] = useState(savedSettings.imperfectionMode ?? false);
  const [persona, setPersona] = useState(savedSettings.persona ?? "");
  const [writingStyle, setWritingStyle] = useState(savedSettings.writingStyle ?? "");
  const [breathPause, setBreathPause] = useState(savedSettings.breathPause ?? false);

  useEffect(() => {
    saveContextSettings({
      targetWords, writingGoal, audienceLevel, formality, emotionalTone,
      intensity, scriptMode, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, revoiceSpeed, writingStyle,
    });
  }, [targetWords, writingGoal, audienceLevel, formality, emotionalTone,
      intensity, scriptMode, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, revoiceSpeed, writingStyle]);

  useEffect(() => {
    saveWorkingText({ input, humanized, changelog, beforeAnalysis, afterAnalysis });
  }, [input, humanized, changelog, beforeAnalysis, afterAnalysis]);

  const contextNudgeShownRef = useRef(false);

  // Style / voice
  const [profilesOpen, setProfilesOpen] = useState(false);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [styleText, setStyleText] = useState("");
  const [voiceStyle, setVoiceStyle] = useState("");
  const [behavioralProfile, setBehavioralProfile] = useState(null);
  const [isClientVoice, setIsClientVoice] = useState(false);
  // Which profile was active never persisted, only the saved profiles list
  // itself did - restoring it (with the matching text) happens inside
  // VoiceProfiles once it loads the list, to avoid a race between the two
  // components' independent storage reads. This just persists it on change -
  // but this effect ALSO fires on the very first render, while activeProfileId
  // is still its initial null (the async restore hasn't resolved yet), which
  // was wiping out the just-saved value before it could ever be read back.
  // Skipping the first firing avoids that self-inflicted race entirely.
  const skipFirstProfilePersist = useRef(true);
  useEffect(() => {
    if (skipFirstProfilePersist.current) { skipFirstProfilePersist.current = false; return; }
    (async () => {
      try {
        if (activeProfileId) await window.storage.set("active_profile_id", activeProfileId);
        else await window.storage.delete("active_profile_id");
      } catch {}
    })();
  }, [activeProfileId]);
  const [fileCheckOpen, setFileCheckOpen] = useState(false);
  const [errorLogOpen, setErrorLogOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [promptPeekOpen, setPromptPeekOpen] = useState(false);
  // Prompt Peek was read-only-preview only - this adds an actual editable
  // custom-prompt override, saved as numbered versions (never overwritten,
  // same pattern as Revoiced-text version history) rather than a single
  // editable slot. A saved version, once selected active, takes over from the
  // settings-driven prompt entirely - the dropdowns go inert for prompt
  // construction until you explicitly switch back to Live.
  const [promptVersions, setPromptVersions] = useState([]);
  const [activePromptVersionId, setActivePromptVersionId] = useState(null); // null = Live (from settings)
  const [promptEditMode, setPromptEditMode] = useState(false);
  const [promptDraft, setPromptDraft] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const result = await window.storage.get("prompt_versions");
        const loaded = result?.value ? JSON.parse(result.value) : [];
        setPromptVersions(loaded);
        // The list of versions was persisted, but which one was active never
        // was - restore it too, checked against what actually loaded so a
        // deleted/missing version doesn't get silently re-selected.
        const activeResult = await window.storage.get("active_prompt_version_id");
        const restoredId = activeResult?.value || null;
        if (restoredId && loaded.some(v => v.id === restoredId)) {
          setActivePromptVersionId(restoredId);
        }
      } catch {}
    })();
  }, []);

  // Same self-inflicted race as the profile-persist effect above: this also
  // fires on the very first render with the initial null, wiping the saved
  // value before the restore effect above can read it. Skip the first firing.
  const skipFirstPromptPersist = useRef(true);
  useEffect(() => {
    if (skipFirstPromptPersist.current) { skipFirstPromptPersist.current = false; return; }
    (async () => {
      try {
        if (activePromptVersionId) await window.storage.set("active_prompt_version_id", activePromptVersionId);
        else await window.storage.delete("active_prompt_version_id");
      } catch {}
    })();
  }, [activePromptVersionId]);

  const savePromptVersions = async (updated) => {
    setPromptVersions(updated);
    try { await window.storage.set("prompt_versions", JSON.stringify(updated)); } catch {}
  };

  const activePromptVersion = promptVersions.find(v => v.id === activePromptVersionId) || null;

  const startEditingPrompt = (liveSystemPrompt) => {
    setPromptDraft(activePromptVersion ? activePromptVersion.text : liveSystemPrompt);
    setPromptEditMode(true);
  };

  const saveNewPromptVersion = () => {
    if (!promptDraft.trim()) return;
    const nextNumber = promptVersions.length ? Math.max(...promptVersions.map(v => v.number)) + 1 : 1;
    const version = { id: Date.now().toString(), number: nextNumber, text: promptDraft.trim(), createdAt: Date.now() };
    savePromptVersions([...promptVersions, version]);
    setActivePromptVersionId(version.id);
    setPromptEditMode(false);
  };

  const goLivePrompt = () => {
    setActivePromptVersionId(null);
    setPromptEditMode(false);
  };

  const deletePromptVersion = (id) => {
    const target = promptVersions.find(v => v.id === id);
    if (!window.confirm(`Delete prompt version ${target ? "v" + target.number : ""}? This can't be undone.`)) return;
    savePromptVersions(promptVersions.filter(v => v.id !== id));
    if (activePromptVersionId === id) setActivePromptVersionId(null);
  };

  const runAnalysis = async (text) => {
    if (!text.trim()) return null;
    return await analyseText(text);
  };

  const handleHumanize = async () => {
    if (!input.trim()) return;
    const contextUnset = !writingGoal && !audienceLevel && !formality && !targetWords;
    if (contextUnset && !contextNudgeShownRef.current) {
      contextNudgeShownRef.current = true;
      const proceed = window.confirm(
        "You haven't set Writing Goal, Audience, Formality, or Target Words above — humanizing now will use general defaults.\n\nClick Cancel to go back and set them, or OK to humanize anyway."
      );
      if (!proceed) return;
    }
    // Reuse an already-computed score for this exact text instead of paying
    // for another full analysis round-trip - the most common source of
    // needless latency (checking score, then humanizing the same text right after).
    const canReuseBeforeAnalysis = !!beforeAnalysis && scoredInputRef.current === input;

    setLoading(true); setError(""); setCleanupNotice("");
    setHumanized(""); setChangelog("");
    setAfterAnalysis(null); setResultWasQuick(false);
    if (!canReuseBeforeAnalysis) setBeforeAnalysis(null);
    setActiveTab("output");

    let cleanInput = input;
    const invisible = detectInvisibleChars(input);
    if (invisible.length > 0) {
      cleanInput = stripInvisibleChars(cleanInput);
    }
    const { text: unrolledInput, listFound } = unrollLists(cleanInput);
    cleanInput = unrolledInput;
    if (invisible.length > 0 || listFound) {
      setInput(cleanInput);
      const notices = [];
      if (invisible.length > 0) notices.push(`removed ${invisible.length} hidden/unusual character${invisible.length !== 1 ? "s" : ""}`);
      if (listFound) notices.push("converted list formatting to flowing text");
      setCleanupNotice(`Before processing: ${notices.join(", ")}.`);
    }

    let lockTokens = [];
    if (factLockEnabled) {
      const { locked, tokens } = lockProtectedContent(cleanInput);
      cleanInput = locked;
      lockTokens = tokens;
    }

    try {
      setHumanizeStage(canReuseBeforeAnalysis ? "Rewriting..." : "Analyzing original & rewriting...");
      // The equivalent AFTER-analysis call below is explicitly best-effort
      // (wrapped so a scoring failure never blocks anything) - this BEFORE
      // call had no such protection, so a transient failure here (a 429,
      // a network blip - both real, already seen live this project) would
      // abort the entire humanize before a single rewrite call even ran,
      // for what is purely an informational score. Matched to the same
      // best-effort pattern: a failed before-score just means the panel
      // shows nothing for "before," not that the humanize itself fails.
      let beforeA = null;
      if (canReuseBeforeAnalysis) {
        beforeA = beforeAnalysis;
      } else {
        try { beforeA = await runAnalysis(input); } catch { /* best-effort - proceed without a before-score */ }
        setBeforeAnalysis(beforeA);
        scoredInputRef.current = input;
      }
      const hasLocks = lockTokens.length > 0;
      // Kept tokenized (not unlocked) through every pass below, so Fact Lock
      // actually survives expand/self-correct/simplify instead of only
      // protecting the first draft. Metrics (word count, banned words,
      // readability) are computed on the unlocked view for accuracy; only the
      // text sent to/from the model stays tokenized. Unlocked once, for real,
      // right before display/scoring at the end.
      const displayText = (t) => hasLocks ? unlockContent(t, lockTokens) : t;

      // ARCHITECTURE CHANGE: this used to chain 6-10 separate sequential LLM
      // correction passes (expand, cross-model revision, consolidated
      // cleanup, simplify, floor-loop, recheck, recleanup) - each one an
      // extra full API round-trip. Real evidence against that design, same
      // day: (1) direct competitor comparison - commercial humanizers do a
      // SINGLE full-document rewrite plus light cleanup, take under a
      // minute, and score better on real detectors than anything this
      // pipeline produced; (2) published research on why - at least one
      // vendor (Refrazr) explicitly deleted a 150+ rule mechanical post-
      // processing system after finding the mechanical alterations
      // THEMSELVES became a detectable AI-like signal; (3) our own
      // findLongSentenceOvershoot/expandShortSentences pair already showed
      // two correction passes can fight each other (one pushes the grade up,
      // the other pushes it down, needing a third pass just to recheck).
      // New architecture: detect issues in the ORIGINAL input up front (free
      // - pure JS, no API call), fold them into ONE comprehensive rewrite
      // instruction alongside voice/reading-level/word-count targets, make
      // ONE generation call, then only light mechanical cleanup (no further
      // LLM calls) - matching what the real research says leading humanizer
      // products actually do.
      // Quick mode (2026-09-16, wired up for real - the toggle existed but
      // did nothing since the single-pass architecture rewrite, a real gap
      // caught during a pre-launch readiness check). Gates only the
      // AI-detection POLISH passes (consolidated cleanup, bannedWords
      // safety net, low-function-word-density safety net) - these are the
      // actual latency drivers (each is a full sequential LLM round-trip)
      // and they exist purely to improve detector scores, not correctness.
      // Deliberately does NOT gate isValidOutput/findSampleContentBleed
      // (content-fidelity guards) or Fact Lock restore - those prevent
      // genuinely broken/fabricated output, a different and worse failure
      // than "still somewhat AI-detectable," and skipping them for speed
      // would trade a real correctness risk for a polish gain.
      const runPolishPasses = revoiceSpeed !== "quick";
      const runOneCandidate = async () => {
      // Fast heuristic scan - reuses the exact same calibrated detectors
      // that used to run AFTER generation (in runConsolidatedCleanup) to
      // find and fix patterns already present in the source, so the single
      // rewrite call can address them from the start instead of in a
      // separate follow-up round-trip.
      const { issues: preIssues } = detectIssues(cleanInput, styleText, formality, isClientVoice);
      const readingTarget = audienceLevel && AUDIENCE_READING_LEVELS[audienceLevel];
      const extraInstructions = [
        preIssues.length
          ? `\n\nHARD REQUIREMENT: the source draft below already has these specific detectable AI patterns - fix every one of them as a natural part of your rewrite, don't treat them as a separate pass:\n${preIssues.join("\n\n")}`
          : '',
        readingTarget
          ? `\n\nREADING LEVEL TARGET: land near a grade ${Math.round((readingTarget.min + readingTarget.max) / 2)} Flesch-Kincaid reading level (the ${readingTarget.label} range) in this single pass - this is both a ceiling and a floor, aim for the middle of the range, not either edge.`
          : '',
      ].filter(Boolean).join("");
      const basePrompt = buildSystemPrompt(intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, voiceStyle, isClientVoice, writingStyle, behavioralProfile);
      const onePassPrompt = activePromptVersion?.text || (basePrompt + extraInstructions);

      const generateAndParse = async (promptToUse) => {
        const raw = await humanizeText(cleanInput, intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, promptToUse);
        const parsed = parseOutput(raw);
        return { parsed, text: stripEmDashes(parsed.humanized) };
      };

      let { parsed, text: finalText } = await generateAndParse(onePassPrompt);

      // Deterministic guard: confirm the output actually engages with the
      // input before doing anything else with it. Live-confirmed failure
      // (2026-09-07): with a Voice Profile active, the model returned the
      // ~1100-word writing sample itself instead of rewriting an unrelated
      // 829-word article - near-zero vocabulary overlap with the source.
      // Also catches a genuinely empty result (an empty string has zero
      // overlap with anything, but a naive `finalText && ...` guard would
      // short-circuit past it instead of retrying - fixed by checking
      // emptiness explicitly rather than relying on the overlap check
      // alone to cover that case). One retry, in the SAME parallel
      // candidate slot (not a new sequential pass - only costs anything on
      // the rare failure case), with an explicit correction naming the
      // exact failure. If that still doesn't recover, throw so
      // Promise.allSettled drops this candidate rather than ever handing
      // the user output that isn't a real rewrite of what they submitted.
      const isValidOutput = (t) => !!t && contentOverlapRatio(cleanInput, t) >= MIN_CONTENT_OVERLAP;
      if (!isValidOutput(finalText)) {
        const correctionPrompt = onePassPrompt + `\n\nCRITICAL CORRECTION: your previous output did not address the actual input text at all - either it came back empty, or it returned unrelated content (for example, reproducing the writing sample instead of applying its voice to the input, or drifting onto a different topic). You MUST rewrite the ORIGINAL TEXT given in the user message below, covering the same specific facts and topic. Do not output the writing sample, a different article, an empty response, or any other text - only a complete transformed version of that specific input.`;
        const retry = await generateAndParse(correctionPrompt);
        if (isValidOutput(retry.text)) {
          parsed = retry.parsed;
          finalText = retry.text;
        } else {
          throw new Error("Generation did not produce valid output related to the source input, even after a corrective retry");
        }
      }

      // Narrower, partial version of the same failure - see
      // findSampleContentBleed's comment. The check above only catches a
      // TOTAL takeover (whole output = the sample); this catches the
      // opening being contaminated while the rest of the piece is a
      // genuine rewrite, which the overall-overlap check above can't see
      // since most of the document still overlaps fine with the source.
      const bleed = styleText ? findSampleContentBleed(finalText, styleText, cleanInput) : null;
      if (bleed) {
        const correctionPrompt = onePassPrompt + `\n\nCRITICAL CORRECTION: your previous output opened with content, an anecdote, or a topic taken from the REFERENCE WRITING SAMPLE itself, not from the actual article you were asked to rewrite. The writing sample is for STYLE REFERENCE ONLY - never repeat, paraphrase, or continue any of its actual content, story, or specific details. Your entire output must be about the ORIGINAL TEXT given in the user message below, written in the sample's voice, but never about anything the sample itself describes.`;
        const retry = await generateAndParse(correctionPrompt);
        if (isValidOutput(retry.text) && !findSampleContentBleed(retry.text, styleText, cleanInput)) {
          parsed = retry.parsed;
          finalText = retry.text;
        } else if (isValidOutput(retry.text)) {
          // Retry fixed content-fidelity but the bleed persisted - still
          // strictly better than the original (which failed the source
          // overlap floor risk too, since a contaminated opening drags
          // overall overlap down), and never worth silently discarding a
          // real generation over one residual issue this deep into the
          // pipeline. Left as-is rather than looping indefinitely.
          parsed = retry.parsed;
          finalText = retry.text;
        }
      }

      // Fact Lock verification - checks the [LOCK_n] TOKENS themselves
      // survived, not just whether their values got altered. Kept as a real
      // correctness check (not a style pass), so it stays even in the
      // light-cleanup-only architecture - only fires if something's
      // actually missing, which is rare.
      let lockRestoreRan = false;
      if (hasLocks && finalText) {
        try {
          const missing = findMissingLockedFacts(finalText, lockTokens);
          if (missing.length) {
            setHumanizeStage("Restoring protected content that was dropped...");
            finalText = await restoreMissingLockedFacts(finalText, missing, hasLocks);
            lockRestoreRan = true;
            setHumanized(displayText(finalText));
          }
        } catch { /* skip - the pre-existing Fact Lock protection (values not altered) still held */ }
      }

      // Post-generation safety net: the pre-generation `preIssues` scan (top
      // of this function) only ever checks the ORIGINAL INPUT, before
      // generation - it can never catch a pattern the REWRITE ITSELF
      // introduces on a source that didn't have it, since there was nothing
      // to cite in the first place. Confirmed live and real (2026-09-10): a
      // final humanize output still tripped `choppyRun` and `tricolon` -
      // this pipeline's own two most-evidenced AI tells (`choppyRun` alone
      // is tied to three separate real Pangram 100%-AI results across this
      // project's history) - because those specific runs of sentences only
      // existed in the model's rewrite, not the source. `runConsolidatedCleanup`
      // already existed for exactly this (built for the old sequential-pass
      // architecture, still fully calibrated, just no longer called anywhere
      // after the single-pass rebuild) - reused here as ONE conditional
      // post-generation pass covering every detector `detectIssues` knows
      // about (which already includes the length-overshoot check this
      // replaces, so nothing lost there), rather than writing a narrower
      // one-off check. It already no-ops internally (no API call) when
      // nothing's found, so this only costs anything when the output
      // actually still has a known issue.
      // Live-confirmed (2026-09-10, same incident): a single consolidated
      // pass citing 2+ issue categories at once can fix some and silently
      // skip others - documented before this rebuild as "partial
      // compliance." Was 2 attempts here, capped at that after a "pattern
      // that survives both is left as-is" tradeoff. Cut back to 1
      // (2026-09-15): with ~20 issue categories now possible in one shared
      // prompt (this list has grown twice since the 2-attempt cap was set),
      // a real user hit a 135s wait sitting specifically in this loop - a
      // real, reported latency regression, not a hypothetical. A repeat of
      // the SAME overloaded shared prompt was never actually the most
      // reliable way to catch what the first attempt missed anyway - the
      // dedicated bannedWords safety net below (isolated, no category
      // competition) already proved that pattern works better for the
      // single most-reported category. One shared attempt, then move on.
      const cleanupRanSet = new Set();
      if (runPolishPasses && finalText) {
        try {
          for (let attempt = 0; attempt < 1; attempt++) {
            setHumanizeStage("Checking output for residual AI-detectable patterns...");
            const cleanupResult = await runConsolidatedCleanup(finalText, finalText, hasLocks, styleText, formality, isClientVoice);
            if (!cleanupResult.ran.length) break;
            finalText = cleanupResult.text;
            cleanupResult.ran.forEach(r => cleanupRanSet.add(r));
            setHumanized(displayText(finalText));
          }
        } catch { /* skip - not worth failing the whole revoice over one cleanup pass */ }
      }
      // Deterministic verification, not another shared-prompt attempt: check
      // whether banned words specifically survived the consolidated pass
      // above (see fixBannedWordsOnly's comment - a real, reported failure
      // mode) and fix just that, in isolation, if so.
      if (runPolishPasses && finalText) {
        const stillFlagged = findBannedWords(finalText);
        if (stillFlagged.length) {
          try {
            setHumanizeStage("Fixing remaining banned words...");
            finalText = await fixBannedWordsOnly(finalText, stillFlagged, hasLocks);
            cleanupRanSet.add("bannedWords");
            setHumanized(displayText(finalText));
          } catch { /* skip - not worth failing the whole humanize over this */ }
        }
      }
      // Same isolated-safety-net pattern as banned words above, for the new
      // low-function-word-density signal (see fixLowFunctionWordDensityOnly's
      // comment). Briefly tried 2 attempts (2026-09-15) after a real user
      // report showed 1 attempt sometimes leaves flagged sentences - but
      // live-confirmed the SAME day that the backend proxy has a 240s
      // CURLOPT_TIMEOUT per call (see proxy.php) - 2 attempts here means up
      // to 480s of worst-case exposure for THIS ONE PASS ALONE, on top of
      // every other sequential call already in the pipeline before it. A
      // real user hit exactly this (285s+ and climbing, stuck on this
      // exact stage). Reverted to 1 - the latency risk this uncovers is
      // worse than the quality gap it was trying to close. If this needs
      // revisiting, it should come with an actual client-side timeout on
      // the fetch itself, not just "try more times and hope."
      if (runPolishPasses && finalText) {
        const stillLowFn = findLowFunctionWordSentences(finalText);
        if (stillLowFn) {
          try {
            setHumanizeStage("Loosening overly dense sentences...");
            finalText = await fixLowFunctionWordDensityOnly(finalText, stillLowFn, hasLocks);
            cleanupRanSet.add("lowFunctionWordDensity");
            setHumanized(displayText(finalText));
          } catch { /* skip - not worth failing the whole humanize over this */ }
        }
      }
      const cleanupRan = [...cleanupRanSet];

      // Light mechanical cleanup only, from here on - no further LLM calls.
      // Markdown/bullet/chatbot-opener/visible-Unicode artifacts the model
      // was already instructed not to produce. Em dashes stripped again
      // here (not just after the first draft in generateAndParse above) -
      // confirmed live (2026-09-15) that both the Fact Lock restore pass
      // and the consolidated cleanup pass above are themselves fresh LLM
      // rewrites that can reintroduce an em dash the first strip already
      // removed, with nothing downstream catching it until now. A pure
      // deterministic regex, safe to run more than once.
      if (finalText) finalText = stripOutputArtifacts(stripEmDashes(finalText));

      return { finalText, parsed, flags: { lockRestoreRan, cleanupRan } };
      };

      // Best-of-N reranking: N=2, run in PARALLEL (was N=2, sequential).
      // The candidates are completely independent, so there was never a
      // real reason to wait for candidate 1 before starting candidate 2 -
      // running them concurrently cuts wall-clock time roughly in half.
      // Tried N=3 first: live-tested and confirmed the parallelization
      // itself works (real concurrent requests, not accidentally still
      // sequential), but one of three concurrent calls hit a real 429 rate
      // limit from the API - handled gracefully (Promise.allSettled, only
      // needs one candidate to succeed), but a genuine signal not to push
      // concurrency higher, especially with real user traffic layered on
      // top. Kept at N=2 concurrent as the safer choice with the same
      // measured benefit already confirmed live.
      const RERANK_N = 2;
      setHumanizeStage(`Generating ${RERANK_N} candidates in parallel...`);
      const settled = await Promise.allSettled(Array.from({ length: RERANK_N }, () => runOneCandidate()));
      const candidates = settled.filter(r => r.status === "fulfilled").map(r => r.value);
      if (candidates.length === 0) throw new Error("Humanize failed - no candidate completed successfully");

      let winner = candidates[0];
      if (candidates.length > 1) {
        setHumanizeStage("Comparing candidates against the AI detector...");
        // Reranking used to rely ONLY on the external /api/detect-score.php
        // call (a real trained classifier - p_human is a genuine softmax
        // probability from a locally-hosted transformer, not a heuristic -
        // confirmed by reading detector_service.py directly). But both
        // candidates already run through every deterministic signal this
        // project has built and calibrated against real third-party
        // detector reports today (computeLocalAiTellScore) - that
        // information was being thrown away at exactly the moment it could
        // decide between two finished candidates, for free, with zero extra
        // API calls (it's pure JS on text already in memory). Combined here
        // rather than added as yet another backstop pass. Weighted 65/35
        // toward the external classifier since it's a genuine trained
        // model, not just a proxy - the local score is real signal, not a
        // replacement for it.
        const scores = await Promise.all(candidates.map(async (c) => {
          const text = displayText(c.finalText);
          const local = computeLocalAiTellScore(text).score; // 0-100, higher = more AI-like
          const localHuman = 100 - local; // 0-100, higher = more human-like
          let externalHuman = null;
          try {
            const res = await fetchWithTimeout(`${OLLAMA_URL}/api/detect-score.php`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text }),
            }, 25000); // this endpoint's own backend timeout is 30s (see detect-score.php) - stay under it
            if (res.ok) {
              const data = await res.json();
              if (typeof data.p_human === "number") externalHuman = data.p_human * 100; // 0-1 -> 0-100
            }
          } catch { /* fails open below - local score alone still ranks this candidate */ }
          return externalHuman !== null ? (externalHuman * 0.65 + localHuman * 0.35) : localHuman;
        }));
        let bestIdx = 0, bestScore = -1;
        scores.forEach((s, i) => { if (s > bestScore) { bestScore = s; bestIdx = i; } });
        winner = candidates[bestIdx];
      }

      let finalText = winner.finalText;
      const parsed = winner.parsed;
      const { lockRestoreRan, cleanupRan } = winner.flags;

      finalText = displayText(finalText);
      setHumanized(finalText);
      setResultWasQuick(!runPolishPasses);
      if (finalText) pushVersion(finalText, "humanize");
      if (showChangelog) {
        // The model's own changelog now covers the whole rewrite (voice,
        // structure, and every pre-detected AI pattern were all folded into
        // the single generation call) - only genuinely separate follow-up
        // steps (Fact Lock restore, the post-generation cleanup pass) are
        // worth noting here.
        const extraNotes = [
          lockRestoreRan && "Restored protected (Fact Lock) content that had been dropped during editing.",
          cleanupRan.length && `Fixed ${cleanupRan.length} detectable AI pattern${cleanupRan.length > 1 ? "s" : ""} introduced by the rewrite itself (${cleanupRan.join(", ")}).`,
        ].filter(Boolean);
        setChangelog(extraNotes.length ? `${parsed.changelog}\n\n${extraNotes.map(n => "- " + n).join("\n")}` : parsed.changelog);
      }

      // Text is ready to use - unlock the UI now instead of making the user
      // wait through the after-score too. That score still runs and appears
      // automatically, just in the background via its own small indicator.
      setLoading(false);
      setHumanizeStage("");

      if (finalText) {
        setScoringAfter(true);
        try {
          const afterA = await runAnalysis(finalText);
          // Snapshot the settings this text was actually generated under, so
          // the readability target shown against it doesn't drift if the
          // user changes Audience/Persona/Formality afterward.
          if (afterA) afterA.generatedWith = { audienceLevel, persona, formality };
          setAfterAnalysis(afterA);
        } catch { /* humanized text is already shown; scoring is best-effort */ }
        finally {
          setScoringAfter(false);
        }
      }
    } catch (err) {
      logError(err?.message, err?.stack, "handleHumanize");
      setError(describeApiError(err));
    } finally {
      setLoading(false); setHumanizeStage("");
    }
  };

  const handleCheckScore = async () => {
    if (!input.trim()) return;
    setScoringOnly(true); setError("");
    setBeforeAnalysis(null); // afterAnalysis left alone - this only re-scores the original text
    setActiveTab("analysis");
    try {
      const result = await runAnalysis(input);
      if (result) { setBeforeAnalysis(result); scoredInputRef.current = input; }
      else setError("Could not parse the score. Try again.");
    } catch (err) {
      logError(err?.message, err?.stack, "handleCheckScore");
      setError(describeApiError(err));
    } finally {
      setScoringOnly(false);
    }
  };

  const editSeqRef = useRef(0);
  const handleHumanizedEdit = useCallback(async (newText) => {
    setHumanized(newText);
    pushVersion(newText, "edit");
    const seq = ++editSeqRef.current;
    const afterA = newText.trim() ? await analyseText(newText) : null;
    if (editSeqRef.current === seq) setAfterAnalysis(afterA);
  }, [pushVersion]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(humanized);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy to clipboard. Please copy the text manually.");
    }
  };

  const clear = () => {
    setInput(""); setHumanized(""); setChangelog("");
    setBeforeAnalysis(null); setAfterAnalysis(null); setError(""); setResultWasQuick(false);
  };

  const tabs = [
    { id: "output", label: "Humanized" },
    ...(humanized ? [{ id: "diff", label: "Redline" }] : []),
    ...(showChangelog && changelog ? [{ id: "log", label: "Change Log" }] : []),
    { id: "analysis", label: "AI Score" },
  ];

  const busy = loading || scoringOnly;

  // Real elapsed-time counter, not a fake progress bar - measured live
  // that a single call to the model can genuinely take anywhere from
  // ~9s to 90+ seconds for the IDENTICAL request, confirmed as real API
  // response-time variance (not prompt size, not this app's own code -
  // directly tested, a 134x larger prompt made no measurable difference).
  // A silent wait reads as "stuck"; a visible, honest counter at least
  // confirms it's still working during a slow run.
  useEffect(() => {
    if (!busy) { setElapsedSeconds(0); return; }
    setElapsedSeconds(0);
    const interval = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [busy]);

  const s = {
    app: { minHeight: "100vh", background: "#0F1724", color: "#E8EDF5", fontFamily: "Georgia, serif" },
    header: { background: "#0A1120", borderBottom: "0.5px solid #1E2D42", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    logoMark: { width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    logo: { fontSize: "15px", color: "#E8EDF5", letterSpacing: "-0.3px", fontWeight: "600" },
    logoSub: { fontSize: "13px", color: "#4A7FA5", letterSpacing: "0.2px" },
    trustBadge: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#4A7FA5", border: "0.5px solid #1E2D42", borderRadius: "20px", padding: "5px 12px" },
    ghostBtn: { background: "transparent", border: "0.5px solid #1E2D42", borderRadius: "6px", color: "#8BAFC9", fontSize: "13px", padding: "6px 14px", cursor: "pointer" },
    controlsBar: { background: "#0A1120", borderBottom: "0.5px solid #1E2D42", padding: "14px 24px", display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" },
    settingsBar: { background: "#0A1120", borderBottom: "0.5px solid #1E2D42", padding: "14px 24px", display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" },
    lightDrawer: { borderBottom: "0.5px solid #1E2D42", background: "#fff", overflow: "hidden", transition: "max-height 0.3s ease" },
    main: { padding: "24px", maxWidth: "1400px", margin: "0 auto" },
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "16px" },
    label: { fontSize: "10px", fontWeight: "600", letterSpacing: "0.8px", textTransform: "uppercase", color: "#4A7FA5", marginBottom: "10px", height: "34px", boxSizing: "border-box", display: "flex", justifyContent: "space-between", alignItems: "center" },
    textarea: { width: "100%", height: "400px", background: "#fff", border: "none", borderRadius: "8px", color: "#1a1a1a", fontFamily: "Georgia, serif", fontSize: "14px", lineHeight: "1.8", padding: "16px", resize: "none", outline: "none", boxSizing: "border-box", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" },
    outputBox: { width: "100%", height: "400px", background: "#fff", border: "none", borderRadius: "8px", padding: "16px", boxSizing: "border-box", overflowY: "auto", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" },
    tabBar: { display: "flex", alignItems: "flex-end", gap: "4px", marginBottom: "10px", height: "34px", boxSizing: "border-box", borderBottom: "0.5px solid #1E2D42" },
    tab: (a) => ({ fontSize: "12px", fontWeight: "600", letterSpacing: "0.02em", padding: "8px 14px", cursor: "pointer", background: "none", border: "none", color: a ? "#00B4D8" : "#4A7FA5", borderBottom: a ? "2px solid #00B4D8" : "2px solid transparent", marginBottom: "-1px" }),
    actions: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" },
    btnP: (d) => ({ background: d ? "#0A6E85" : "#00B4D8", color: "#0A1120", border: "none", borderRadius: "7px", padding: "12px 24px", fontSize: "14px", fontWeight: "700", fontFamily: "Georgia, serif", cursor: d ? "not-allowed" : "pointer", opacity: d ? 0.6 : 1 }),
    btnCheckScore: (d) => ({ background: "transparent", color: "#8BAFC9", border: "0.5px solid #4A7FA5", borderRadius: "7px", padding: "12px 24px", fontSize: "14px", fontWeight: "600", fontFamily: "Georgia, serif", cursor: d ? "not-allowed" : "pointer", opacity: d ? 0.5 : 1 }),
    btnS: (a) => ({ background: a ? "#0E2A38" : "transparent", color: a ? "#00B4D8" : "#8BAFC9", border: `0.5px solid ${a ? "#00B4D8" : "#1E2D42"}`, borderRadius: "6px", padding: "11px 18px", fontSize: "12px", fontFamily: "Georgia, serif", cursor: "pointer", letterSpacing: "0.04em" }),
    btnG: { background: "transparent", color: "#8BAFC9", border: "0.5px solid #1E2D42", borderRadius: "6px", padding: "11px 14px", fontSize: "12px", fontFamily: "Georgia, serif", cursor: "pointer" },
    spinner: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "12px" },
    wc: { fontSize: "11px", color: "#2A4A65" },
    wcDiff: (before, after) => ({ fontSize: "11px", color: after < before * 0.85 ? "#F87171" : "#2A4A65" }),
  };

  return (
    <div style={s.app}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        textarea:focus{border-color:#bbb!important}
        select:focus{outline:none}
        * { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; }
        ::placeholder { color: #9ca3af !important; opacity: 1; }
        .btn-primary:hover:not(:disabled) { background: #00CFF0 !important; }
        .btn-secondary:hover:not(:disabled) { background: #111D2C !important; }
        .ghost-btn:hover { background: #111D2C !important; }
      `}</style>

      {/* Header */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={s.logoMark}>
            <svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg>
          </div>
          <div>
            <span style={s.logo}>HumanKind</span>
            <span style={s.logoSub}> · Text Humanizer</span>
            <span style={{ fontSize: "10px", color: "#4a6580", marginLeft: "6px", verticalAlign: "middle" }}>v{APP_VERSION}</span>
          </div>
          <div style={{ display: "flex", gap: "4px", marginLeft: "10px" }}>
            <a href="/app" style={{ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", textDecoration: "none", color: "#0A1120", background: "#00B4D8", fontWeight: "700" }}>Humanizer</a>
            <a href="/seo" style={{ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", textDecoration: "none", color: "#8BAFC9", background: "transparent" }}>SEO Optimizer</a>
          </div>
        </div>
        <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
          <span style={s.trustBadge}>
            <span style={{ width: "6px", height: "6px", background: "#00B4D8", borderRadius: "50%" }} />
            Secure &amp; Private
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
          <div style={{ position: "relative" }}>
            <button className="ghost-btn" onClick={() => setSettingsMenuOpen(!settingsMenuOpen)} title="Settings" style={{
              ...s.ghostBtn, fontSize: "14px", padding: "6px 12px", lineHeight: 1
            }}>⚙️</button>
            {settingsMenuOpen && (
              <div style={{
                position: "absolute", top: "44px", right: 0, background: "#fff", border: "1px solid #E5E7EB",
                borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.3)", zIndex: 20, minWidth: "160px", overflow: "hidden"
              }}>
                <button onClick={() => { setErrorLogOpen(!errorLogOpen); setSettingsMenuOpen(false); }} style={{
                  display: "block", width: "100%", textAlign: "left", padding: "10px 16px", fontSize: "12px",
                  background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", color: "#333"
                }}>Error Log</button>
                <button onClick={() => { setFileCheckOpen(!fileCheckOpen); setSettingsMenuOpen(false); }} style={{
                  display: "block", width: "100%", textAlign: "left", padding: "10px 16px", fontSize: "12px",
                  background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", color: "#333"
                }}>Check File</button>
                <a href="/help" target="_blank" rel="noopener noreferrer" style={{
                  display: "block", width: "100%", boxSizing: "border-box", textAlign: "left", padding: "10px 16px", fontSize: "12px",
                  background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", color: "#333", textDecoration: "none"
                }}>Help</a>
                <a href="/credits" target="_blank" rel="noopener noreferrer" style={{
                  display: "block", width: "100%", boxSizing: "border-box", textAlign: "left", padding: "10px 16px", fontSize: "12px",
                  background: "none", border: "none", cursor: "pointer", color: "#333", textDecoration: "none"
                }}>Buy Credits</a>
              </div>
            )}
          </div>
          <button className="ghost-btn" onClick={() => setProfilesOpen(!profilesOpen)} style={{
            ...s.ghostBtn, color: (profilesOpen || !!activeProfileId) ? "#00B4D8" : "#8BAFC9", borderColor: (profilesOpen || !!activeProfileId) ? "#00B4D8" : "#1E2D42"
          }}>
            {activeProfileId ? "✓ Voice Active" : "Voice Profiles"} {profilesOpen ? "▲" : "▼"}
          </button>
          {REQUIRE_AUTH && (
            <button className="ghost-btn" onClick={onLogout} style={s.ghostBtn}>Sign out</button>
          )}
          </div>
        </div>
      </div>

      {/* Error Log Drawer */}
      <div style={{ ...s.lightDrawer, maxHeight: errorLogOpen ? "400px" : "0" }}>
        <ErrorLogPanel />
      </div>

      {/* File Metadata Check Drawer */}
      <div style={{ ...s.lightDrawer, maxHeight: fileCheckOpen ? "300px" : "0" }}>
        <FileMetadataCheck />
      </div>

      {/* Voice Profiles Drawer */}
      <div style={{ ...s.lightDrawer, padding: "0 32px", maxHeight: profilesOpen ? "80vh" : "0", overflow: profilesOpen ? "auto" : "hidden" }}>
        <VoiceProfiles
          active={activeProfileId}
          onSelect={setActiveProfileId}
          onSave={setStyleText}
          onStyleChange={setVoiceStyle}
          onOwnerChange={setIsClientVoice}
          onBehavioralChange={setBehavioralProfile}
          onDelete={() => {}}
        />
      </div>

      {/* Controls Bar */}
      <div style={s.controlsBar}>
          <SelectField label="Writing Goal" value={writingGoal} onChange={setWritingGoal} options={[
            { value: "", label: "Any" },
            { value: "SEO Blog Post", label: "SEO Blog" },
            { value: "Academic Essay", label: "Academic" },
            { value: "Business Report", label: "Business" },
            { value: "Creative Story", label: "Creative" },
            { value: "Social Media Post", label: "Social Media" },
            { value: "Email", label: "Email" },
          ]} />
          <SelectField label="Audience" value={audienceLevel} onChange={setAudienceLevel} options={[
            { value: "", label: "Any" },
            { value: "General Public", label: "General" },
            { value: "Professional", label: "Professional" },
            { value: "Expert", label: "Expert" },
            { value: "Children", label: "Children" },
          ]} />
          <SelectField label="Formality" value={formality} onChange={setFormality} options={[
            { value: "", label: "Any" },
            { value: "Casual", label: "Casual" },
            { value: "Conversational", label: "Conversational" },
            { value: "Professional", label: "Professional" },
            { value: "Formal", label: "Formal" },
          ]} />
          <SelectField label="Tone" value={emotionalTone} onChange={setEmotionalTone} options={[
            { value: "", label: "Any" },
            { value: "Neutral", label: "Neutral" },
            { value: "Persuasive", label: "Persuasive" },
            { value: "Empathetic", label: "Empathetic" },
            { value: "Authoritative", label: "Authoritative" },
            { value: "Enthusiastic", label: "Enthusiastic" },
          ]} />
          <SelectField label="Detection Resistance" value={bypassTarget} onChange={setBypassTarget} options={[
            { value: "", label: "Off" },
            { value: "boosted", label: "Boosted (GPTZero + Turnitin)" },
          ]} />
          <SelectField label="Persona" value={persona} onChange={setPersona} options={[
            { value: "", label: "Default" },
            { value: "academic", label: "Academic" },
            { value: "casual_blog", label: "Casual Blog" },
            { value: "reddit", label: "Reddit Comment" },
            { value: "esl", label: "ESL Speaker" },
          ]} />
          <SelectField label="Writing Style" value={writingStyle} onChange={setWritingStyle} options={[
            { value: "", label: "Any" },
            ...VOICE_STYLE_OPTIONS.map(s => ({ value: s, label: s })),
          ]} />
        <RadioGroup label="Intensity" value={intensity} onChange={setIntensity}
          options={[{ value: "light", label: "Light" }, { value: "medium", label: "Medium" }, { value: "aggressive", label: "Aggressive" }]} />
        <RadioGroup label="Humanize Speed" value={revoiceSpeed} onChange={setRevoiceSpeed}
          options={[{ value: "thorough", label: "Thorough" }, { value: "quick", label: "Quick" }]} />
      </div>

      {/* Settings Bar */}
      <div style={s.settingsBar}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", fontWeight: "500", color: "#8BAFC9" }}>Target Words</span>
          <input type="number" value={targetWords} onChange={e => setTargetWords(e.target.value)} placeholder="e.g. 900"
            style={{ width: "80px", padding: "6px 8px", fontSize: "12px", border: "0.5px solid #1E2D42", borderRadius: "6px", color: "#8BAFC9", background: "#111D2C", outline: "none" }} />
        </div>
        <div style={{ width: "1px", height: "28px", background: "#1E2D42" }} />
        <Toggle label="Script Mode" checked={scriptMode} onChange={setScriptMode} />
        {scriptMode && <Toggle label="Breath Pauses" checked={breathPause} onChange={setBreathPause} />}
        {scriptMode && wordCount(humanized || input) > 0 && (
          <span style={{ fontSize: "11px", color: "#4A7FA5" }}>
            ⏱ ~{Math.max(1, Math.round(wordCount(humanized || input) / 150))} min speak time
          </span>
        )}
        <Toggle label="Change Log" checked={showChangelog} onChange={setShowChangelog} />
        <Toggle label="Highlight Issues" checked={showHighlights} onChange={setShowHighlights} />
        <Toggle label="Lock Names/Numbers" checked={factLockEnabled} onChange={setFactLockEnabled} />
        <Toggle label="Casual Grammar" checked={imperfectionMode} onChange={setImperfectionMode} />
        <div style={{ width: "1px", height: "28px", background: "#1E2D42" }} />
        <button onClick={() => setPromptPeekOpen(!promptPeekOpen)} style={{
          fontSize: "12px", fontWeight: "600", padding: "6px 14px", borderRadius: "6px", border: "0.5px solid " + (promptPeekOpen ? "#00B4D8" : "#1E2D42"),
          background: promptPeekOpen ? "#00B4D8" : "#111D2C", color: promptPeekOpen ? "#0A1120" : "#8BAFC9",
          cursor: "pointer"
        }}>
          Prompt Peek {promptPeekOpen ? "▲" : "▼"}
        </button>
      </div>

      {promptPeekOpen && (() => {
        const liveSystemPrompt = buildSystemPrompt(intensity, scriptMode, styleText, targetWords, writingGoal, audienceLevel, formality, emotionalTone, factLockEnabled, bypassTarget, imperfectionMode, persona, breathPause, voiceStyle, isClientVoice, writingStyle, behavioralProfile);
        const effectivePrompt = activePromptVersion ? activePromptVersion.text : liveSystemPrompt;
        const tinyBtn = { fontSize: "11px", fontWeight: "600", padding: "5px 12px", borderRadius: "5px", border: "0.5px solid #1E2D42", background: "#111D2C", color: "#8BAFC9", cursor: "pointer" };
        const tinyBtnPrimary = { ...tinyBtn, background: "#00B4D8", color: "#0A1120", border: "none" };
        return (
          <div style={{ borderBottom: "0.5px solid #1E2D42", padding: "16px 24px", background: "#0A1120" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#4A7FA5" }}>
                {activePromptVersion ? `Custom prompt — v${activePromptVersion.number} active (settings below are inactive)` : "Exact system prompt Ollama will receive for the current settings"}
              </div>
              {!promptEditMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => startEditingPrompt(liveSystemPrompt)} style={tinyBtn}>Edit</button>
                  {activePromptVersion && <button onClick={goLivePrompt} style={tinyBtn}>Back to Live</button>}
                </div>
              )}
            </div>

            {promptEditMode ? (
              <>
                <textarea value={promptDraft} onChange={e => setPromptDraft(e.target.value)} style={{
                  width: "100%", height: "220px", fontSize: "11px", lineHeight: "1.6", color: "#E8EDF5", background: "#111D2C",
                  border: "0.5px solid #00B4D8", borderRadius: "5px", padding: "14px", fontFamily: "monospace", resize: "vertical", outline: "none", boxSizing: "border-box"
                }} />
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button onClick={saveNewPromptVersion} disabled={!promptDraft.trim()} style={tinyBtnPrimary}>Save Version</button>
                  <button onClick={() => setPromptEditMode(false)} style={tinyBtn}>Cancel</button>
                </div>
              </>
            ) : (
              <pre style={{
                fontSize: "11px", lineHeight: "1.6", color: "#8BAFC9", background: "#111D2C", border: "0.5px solid #1E2D42",
                borderRadius: "5px", padding: "14px", maxHeight: "280px", overflow: "auto", whiteSpace: "pre-wrap", fontFamily: "monospace", margin: 0
              }}>
                {effectivePrompt}
              </pre>
            )}

            {promptVersions.length > 0 && (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "10px", alignItems: "center" }}>
                <span style={{ fontSize: "10px", color: "#4A7FA5", textTransform: "uppercase", letterSpacing: "0.1em" }}>Versions:</span>
                <button onClick={goLivePrompt} style={{
                  ...tinyBtn,
                  borderColor: !activePromptVersion ? "#00B4D8" : "#1E2D42",
                  background: !activePromptVersion ? "#00B4D8" : "#111D2C",
                  color: !activePromptVersion ? "#0A1120" : "#8BAFC9",
                }}>Live</button>
                {promptVersions.map(v => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <button onClick={() => { setActivePromptVersionId(v.id); setPromptEditMode(false); }} style={{
                      ...tinyBtn,
                      borderColor: activePromptVersionId === v.id ? "#00B4D8" : "#1E2D42",
                      background: activePromptVersionId === v.id ? "#00B4D8" : "#111D2C",
                      color: activePromptVersionId === v.id ? "#0A1120" : "#8BAFC9",
                    }}>v{v.number}</button>
                    <button onClick={() => deletePromptVersion(v.id)} style={{ background: "none", border: "none", color: "#4A7FA5", cursor: "pointer", fontSize: "13px", padding: "0 4px" }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Main */}
      <div style={s.main}>
        <div style={s.grid}>

          {/* Input */}
          <div>
            <div style={s.label}>
              <span>Original Text</span>
              <span style={s.wc}>{wordCount(input)} words</span>
            </div>
            <textarea value={input} onChange={e => setInput(e.target.value)}
              placeholder="Paste your text here..." style={s.textarea} />
            {cleanupNotice && (
              <div style={{ fontSize: "11px", color: "#4A7FA5", marginTop: "6px" }}>ℹ {cleanupNotice}</div>
            )}
          </div>

          {/* Output */}
          <div>
            <div style={s.tabBar}>
              {tabs.map(t => (
                <button key={t.id} style={s.tab(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>{t.label}</button>
              ))}
              {humanized && (
                <span style={{ ...s.wcDiff(wordCount(input), wordCount(humanized)), marginLeft: "auto", alignSelf: "center" }}>
                  {wordCount(humanized)} words
                  {wordCount(input) > 0 && (
                    <span style={{ marginLeft: "4px", fontSize: "10px" }}>
                      ({wordCount(humanized) >= wordCount(input) * 0.85
                        ? (wordCount(humanized) > wordCount(input) ? "+" : "") + (wordCount(humanized) - wordCount(input))
                        : "⚠ " + (wordCount(humanized) - wordCount(input))})
                    </span>
                  )}
                </span>
              )}
            </div>
            {versionHistory.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 0", flexWrap: "wrap" }}>
                <span style={{ fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#4A7FA5" }}>History:</span>
                {versionHistory.map((v, i) => (
                  <button key={i} onClick={() => restoreVersion(i)} title={new Date(v.time).toLocaleTimeString()} style={{
                    fontSize: "11px", fontWeight: "600", padding: "4px 10px", borderRadius: "5px", cursor: "pointer",
                    border: "0.5px solid " + (i === currentVersionIdx ? "#00B4D8" : "#1E2D42"),
                    background: i === currentVersionIdx ? "#00B4D8" : "#111D2C",
                    color: i === currentVersionIdx ? "#0A1120" : "#4A7FA5",
                  }}>
                    {v.source === "humanize" ? "H" : "E"}{i + 1}
                  </button>
                ))}
              </div>
            )}
            <div style={s.outputBox}>
              {busy ? (
                <div style={s.spinner}>
                  <div style={{ width: "26px", height: "26px", border: "2px solid #eee", borderTop: "2px solid #555", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <div style={{ fontSize: "11px", color: "#aaa", letterSpacing: "0.1em" }}>
                    {scoringOnly ? "Scoring..." : humanizeStage || "Working..."}{elapsedSeconds >= 3 ? ` — ${elapsedSeconds}s` : ""}
                  </div>
                  {!scoringOnly && elapsedSeconds >= 3 && (
                    <div style={{ fontSize: "11px", color: "#999", maxWidth: "280px", textAlign: "center", lineHeight: "1.5" }}>
                      This process can get fairly complex under the hood, so humanizing sometimes takes a bit longer than expected — sorry for the wait.
                    </div>
                  )}
                </div>
              ) : error ? (
                <div style={{ color: "#b05050", fontSize: "13px" }}>{error}</div>
              ) : activeTab === "output" ? (
                humanized
                  ? <>
                      {resultWasQuick && (
                        <div style={{ fontSize: "12px", color: "#8a6d1a", background: "#fdf6e3", border: "1px solid #f0e0a8", borderRadius: "6px", padding: "8px 12px", marginBottom: "12px" }}>
                          Generated in Quick mode - faster, but skips the extra passes that catch residual AI-detectable patterns. Result may score higher on AI detectors than Thorough mode. Switch to Thorough and re-run if this is going somewhere that matters.
                        </div>
                      )}
                      <HighlightedText text={humanized} onChange={handleHumanizedEdit} showHighlights={showHighlights} />
                    </>
                  : <div style={{ color: "#ccc", fontSize: "13px", fontStyle: "italic" }}>Your humanized text will appear here...</div>
              ) : activeTab === "diff" ? (
                <RedlineDiff oldText={input} newText={humanized} />
              ) : activeTab === "log" ? (
                changelog
                  ? <div style={{ fontSize: "13px", lineHeight: "1.7", color: "#555", whiteSpace: "pre-wrap" }}>{changelog}</div>
                  : <div style={{ color: "#ccc", fontSize: "13px", fontStyle: "italic" }}>No changes logged.</div>
              ) : (
                <div>
                  {(beforeAnalysis || afterAnalysis) ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                      <div>
                        <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#aaa", marginBottom: "12px" }}>Before</div>
                        <AnalysisPanel analysis={beforeAnalysis} label="Original text" />
                      </div>
                      <div>
                        <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#aaa", marginBottom: "12px" }}>After</div>
                        <AnalysisPanel analysis={afterAnalysis} label="Humanized text" loading={scoringAfter} />
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: "#ccc", fontSize: "13px", fontStyle: "italic" }}>Click "Check AI Score" to analyse, or run Humanize for a before/after comparison.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={s.actions}>
          <button className="btn-primary" onClick={handleHumanize} disabled={busy || !input.trim()} style={s.btnP(busy || !input.trim())}>
            {loading ? "Working..." : "Humanize"}
          </button>
          <button className="btn-secondary" onClick={handleCheckScore} disabled={busy || !input.trim()} style={s.btnCheckScore(busy || !input.trim())}>
            {scoringOnly ? "Scoring..." : "Check AI Score"}
          </button>
          {humanized && (
            <>
              <button onClick={copy} style={s.btnS(copied)}>{copied ? "Copied!" : "Copy"}</button>
              <div style={{ position: "relative" }}>
                <button onClick={() => setShowExport(!showExport)} style={s.btnG}>Export ▾</button>
                {showExport && (
                  <div style={{ position: "absolute", bottom: "44px", left: "0", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "4px", boxShadow: "0 4px 16px rgba(0,0,0,0.35)", zIndex: 10, minWidth: "140px" }}>
                    {[
                      { label: "Download .txt", fn: () => { exportTxt(humanized); setShowExport(false); } },
                      { label: "Download .md", fn: () => { exportMd(humanized); setShowExport(false); } },
                      { label: "Download .doc", fn: () => { exportDocx(humanized); setShowExport(false); } },
                    ].map(item => (
                      <button key={item.label} onClick={item.fn} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", fontSize: "12px", fontFamily: "Georgia, serif", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", color: "#333" }}
                        onMouseEnter={e => e.target.style.background = "#f8f8f8"}
                        onMouseLeave={e => e.target.style.background = "none"}
                      >{item.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {(input || humanized) && <button onClick={clear} style={s.btnG}>Clear</button>}
        </div>

        <div style={{ marginTop: "28px", borderTop: "0.5px solid #1E2D42", paddingTop: "14px" }}>
          <p style={{ fontSize: "11px", color: "#2A4A65", margin: 0 }}>
            HumanKind · AI Score is an approximation, not a certified detector result
          </p>
        </div>
      </div>
    </div>
  );
}

// Top-level: handles auth (once, shared across pages) and routes between the
// main app and the separate /admin page based on the URL path.
const seoWordCount = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const flattenSeoArticle = (article) => article.map(l => {
  if (l.type === "h1") return "# " + l.text;
  if (l.type === "h2") return "## " + l.text;
  if (l.type === "h3") return "### " + l.text;
  return l.text;
}).join("\n\n");

const SeoOptimizerPage = ({ onLogout }) => {
  const [primaryKeyword, setPrimaryKeyword] = useState("");
  const [secondaryKeywords, setSecondaryKeywords] = useState("");
  const [targetLength, setTargetLength] = useState("800");
  const [contentType, setContentType] = useState("Blog Post");
  const [audience, setAudience] = useState("General Public");
  const [tone, setTone] = useState("Conversational");
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [outputTab, setOutputTab] = useState("article");
  const [copiedField, setCopiedField] = useState("");
  const [showExport, setShowExport] = useState(false);

  const copyText = (text, field) => {
    // writeText() previously fired without awaiting or catching it, so the
    // "Copied!" UI showed unconditionally even if the write actually failed
    // (permissions, insecure context) - the user would be told something
    // was copied when it wasn't, and could paste stale content elsewhere.
    // Also left an unhandled promise rejection on failure.
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(f => (f === field ? "" : f)), 1500);
    }).catch(() => setError("Couldn't copy to clipboard - your browser may be blocking clipboard access."));
  };

  const handleOptimize = async () => {
    if (!primaryKeyword.trim()) { setError("Please enter a primary keyword before optimizing."); return; }
    if (!inputText.trim()) { setError("Please paste your text before optimizing."); return; }
    if (seoWordCount(inputText) < 100) { setError("Your text is too short to optimize. Paste at least 100 words."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const raw = await generateSeoOptimized(inputText, primaryKeyword.trim(), secondaryKeywords.trim(), contentType, targetLength, audience, tone);
      const parsed = parseSeoOptimizerResponse(raw, primaryKeyword.trim());
      if (!parsed) { setError("Something went wrong. Please try again."); return; }
      setResult(parsed);
      setOutputTab("article");
    } catch (err) {
      setError(err?.code === 402 ? (err.message || "You've used your monthly allowance. Top up to continue.") : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPrimaryKeyword(""); setSecondaryKeywords(""); setInputText(""); setResult(null); setError("");
  };

  const s = {
    app: { minHeight: "100vh", background: "#0F1724", color: "#E8EDF5", fontFamily: "Georgia, serif" },
    header: { background: "#0A1120", borderBottom: "0.5px solid #1E2D42", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    logoMark: { width: "28px", height: "28px", background: "#00B4D8", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    logo: { fontSize: "15px", color: "#E8EDF5", letterSpacing: "-0.3px", fontWeight: "600" },
    navLink: (active) => ({ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", textDecoration: "none", color: active ? "#0A1120" : "#8BAFC9", background: active ? "#00B4D8" : "transparent", fontWeight: active ? "700" : "400" }),
    ghostBtn: { background: "transparent", border: "0.5px solid #1E2D42", borderRadius: "6px", color: "#8BAFC9", fontSize: "13px", padding: "6px 14px", cursor: "pointer" },
    label: { fontSize: "10px", fontWeight: "600", letterSpacing: "0.8px", textTransform: "uppercase", color: "#4A7FA5", marginBottom: "6px" },
    input: { width: "100%", padding: "9px 12px", fontSize: "13px", border: "0.5px solid #1E2D42", borderRadius: "6px", background: "#111D2C", color: "#E8EDF5", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" },
    select: { width: "100%", padding: "9px 12px", fontSize: "13px", border: "0.5px solid #1E2D42", borderRadius: "6px", background: "#111D2C", color: "#E8EDF5", outline: "none", fontFamily: "Georgia, serif" },
    panel: { background: "#0A1120", border: "0.5px solid #1E2D42", borderRadius: "10px", padding: "20px" },
    btnP: (d) => ({ background: d ? "#0A6E85" : "#00B4D8", color: "#0A1120", border: "none", borderRadius: "7px", padding: "12px 24px", fontSize: "14px", fontWeight: "700", fontFamily: "Georgia, serif", cursor: d ? "not-allowed" : "pointer", opacity: d ? 0.6 : 1, width: "100%" }),
    btnS: { background: "transparent", color: "#8BAFC9", border: "0.5px solid #1E2D42", borderRadius: "7px", padding: "12px 24px", fontSize: "14px", fontFamily: "Georgia, serif", cursor: "pointer" },
    tab: (a) => ({ fontSize: "12px", fontWeight: "600", padding: "8px 14px", cursor: "pointer", background: "none", border: "none", color: a ? "#00B4D8" : "#4A7FA5", borderBottom: a ? "2px solid #00B4D8" : "2px solid transparent" }),
    copyBtn: { fontSize: "11px", padding: "5px 10px", borderRadius: "5px", border: "0.5px solid #1E2D42", background: "#111D2C", color: "#8BAFC9", cursor: "pointer" },
  };

  return (
    <div style={s.app}>
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={s.logoMark}><svg viewBox="0 0 16 16" width="16" height="16" fill="#0A1120"><path d="M8 1L14 5V11L8 15L2 11V5L8 1Z" /></svg></div>
            <span style={s.logo}>HumanKind</span>
          </div>
          <div style={{ display: "flex", gap: "4px" }}>
            <a href="/app" style={s.navLink(false)}>Humanizer</a>
            <a href="/seo" style={s.navLink(true)}>SEO Optimizer</a>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <a href="/app" style={s.ghostBtn}>← Back to Humanizer</a>
          {REQUIRE_AUTH && <button className="ghost-btn" onClick={onLogout} style={s.ghostBtn}>Sign out</button>}
        </div>
      </div>

      <div style={{ padding: "32px 24px", maxWidth: "1400px", margin: "0 auto" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "4px" }}>SEO Optimizer</h1>
        <p style={{ fontSize: "14px", color: "#8BAFC9", marginBottom: "28px" }}>Turn your humanized text into a publish-ready SEO article.</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "start" }}>
          {/* Left column - input & controls */}
          <div style={s.panel}>
            <div style={{ marginBottom: "16px" }}>
              <div style={s.label} title="This keyword will be placed in your title, introduction, at least one subheading, and naturally throughout the article.">PRIMARY KEYWORD</div>
              <input style={s.input} value={primaryKeyword} onChange={e => setPrimaryKeyword(e.target.value)} placeholder="Enter your main target keyword" />
            </div>
            <div style={{ marginBottom: "18px" }}>
              <div style={s.label} title="Related terms that support your primary keyword. These will be woven in naturally where they fit.">SECONDARY KEYWORDS</div>
              <input style={s.input} value={secondaryKeywords} onChange={e => setSecondaryKeywords(e.target.value)} placeholder="Enter 2-3 related keywords, comma separated" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "18px" }}>
              <div>
                <div style={s.label}>TARGET LENGTH</div>
                <select style={s.select} value={targetLength} onChange={e => setTargetLength(e.target.value)}>
                  {["500", "800", "1000", "1500", "2000"].map(v => <option key={v} value={v}>{v} words</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>CONTENT TYPE</div>
                <select style={s.select} value={contentType} onChange={e => setContentType(e.target.value)}>
                  {["Blog Post", "How-To Guide", "Listicle", "Product Review", "News Article", "Opinion Piece"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>AUDIENCE</div>
                <select style={s.select} value={audience} onChange={e => setAudience(e.target.value)}>
                  {["General Public", "Professional", "Expert", "Students"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>TONE</div>
                <select style={s.select} value={tone} onChange={e => setTone(e.target.value)}>
                  {["Neutral", "Conversational", "Authoritative", "Persuasive"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "8px" }}>
              <div style={{ ...s.label, display: "flex", justifyContent: "space-between" }}>
                <span>INPUT TEXT</span>
                <span>{seoWordCount(inputText)} WORDS</span>
              </div>
              <textarea value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Paste your humanized text here..."
                style={{ width: "100%", height: "260px", background: "#fff", border: "none", borderRadius: "8px", color: "#1a1a1a", fontFamily: "Georgia, serif", fontSize: "14px", lineHeight: "1.7", padding: "14px", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
            </div>

            {error && <div style={{ color: "#F87171", fontSize: "13px", margin: "10px 0" }}>{error}</div>}

            <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
              <button onClick={handleOptimize} disabled={loading || !primaryKeyword.trim() || !inputText.trim()} style={s.btnP(loading || !primaryKeyword.trim() || !inputText.trim())}>
                {loading ? "Optimizing..." : "Optimize for SEO"}
              </button>
              <button onClick={handleClear} style={s.btnS}>Clear</button>
            </div>
            <div style={{ fontSize: "11px", color: "#4A7FA5", marginTop: "10px" }}>SEO optimization uses words from your monthly allowance based on output length.</div>
          </div>

          {/* Right column - output */}
          <div style={s.panel}>
            {!result && !loading && (
              <div style={{ color: "#4A7FA5", fontSize: "13px", fontStyle: "italic" }}>Your optimized article, metadata, and SEO checklist will appear here.</div>
            )}
            {loading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "300px", gap: "12px" }}>
                <div style={{ width: "26px", height: "26px", border: "2px solid #1E2D42", borderTop: "2px solid #00B4D8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <div style={{ fontSize: "11px", color: "#4A7FA5", letterSpacing: "0.1em" }}>OPTIMIZING...</div>
              </div>
            )}
            {result && (
              <div>
                <div style={{ display: "flex", gap: "4px", marginBottom: "18px", borderBottom: "0.5px solid #1E2D42" }}>
                  <button style={s.tab(outputTab === "article")} onClick={() => setOutputTab("article")}>Optimized Article</button>
                  <button style={s.tab(outputTab === "metadata")} onClick={() => setOutputTab("metadata")}>SEO Metadata</button>
                  <button style={s.tab(outputTab === "checklist")} onClick={() => setOutputTab("checklist")}>SEO Checklist</button>
                </div>

                {outputTab === "article" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: "14px", position: "relative" }}>
                      <button style={s.copyBtn} onClick={() => copyText(flattenSeoArticle(result.article), "article")}>
                        {copiedField === "article" ? "Copied!" : "Copy Article"}
                      </button>
                      <button style={s.copyBtn} onClick={() => setShowExport(!showExport)}>Export ▾</button>
                      {showExport && (
                        <div style={{ position: "absolute", top: "32px", right: 0, background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.3)", zIndex: 20, minWidth: "120px", overflow: "hidden" }}>
                          {[
                            { label: "Download .txt", fn: () => exportTxt(flattenSeoArticle(result.article)) },
                            { label: "Download .md", fn: () => exportMd(flattenSeoArticle(result.article)) },
                            { label: "Download .doc", fn: () => exportDocx(flattenSeoArticle(result.article)) },
                          ].map(opt => (
                            <button key={opt.label} onClick={() => { opt.fn(); setShowExport(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: "12px", background: "none", border: "none", cursor: "pointer", color: "#333" }}>{opt.label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: "13px", lineHeight: "1.75" }}>
                      {result.article.map((line, i) => {
                        if (line.type === "h1") return <div key={i} style={{ marginTop: i ? "18px" : 0, marginBottom: "6px" }}><span style={{ fontSize: "9px", color: "#4A7FA5", marginRight: "6px" }}>H1</span><span style={{ fontSize: "22px", fontWeight: "700" }}>{line.text}</span></div>;
                        if (line.type === "h2") return <div key={i} style={{ marginTop: "18px", marginBottom: "6px", borderLeft: "3px solid #00B4D8", paddingLeft: "10px" }}><span style={{ fontSize: "9px", color: "#4A7FA5", marginRight: "6px" }}>H2</span><span style={{ fontSize: "17px", fontWeight: "700" }}>{line.text}</span></div>;
                        if (line.type === "h3") return <div key={i} style={{ marginTop: "14px", marginBottom: "6px" }}><span style={{ fontSize: "9px", color: "#4A7FA5", marginRight: "6px" }}>H3</span><span style={{ fontSize: "14px", fontWeight: "700" }}>{line.text}</span></div>;
                        return <p key={i} style={{ marginBottom: "12px", color: "#C8D6E5" }}>{line.text}</p>;
                      })}
                    </div>
                  </div>
                )}

                {outputTab === "metadata" && (() => {
                  const blocks = [
                    { key: "title", label: "Page Title (H1)", value: result.metadata.title, max: 60 },
                    { key: "metaDescription", label: "Meta Description", value: result.metadata.metaDescription, max: 160 },
                    { key: "keyword", label: "Target Keyword", value: result.metadata.keyword, max: null },
                    { key: "secondaryKeywords", label: "Secondary Keywords", value: result.metadata.secondaryKeywords || "None", max: null },
                    { key: "urlSlug", label: "Suggested URL Slug", value: result.metadata.urlSlug, max: null },
                  ];
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {blocks.map(b => (
                        <div key={b.key}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                            <div style={{ fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#4A7FA5" }}>{b.label}</div>
                            <button style={s.copyBtn} onClick={() => copyText(b.value, b.key)}>{copiedField === b.key ? "Copied!" : "Copy"}</button>
                          </div>
                          <div style={{ fontSize: "13px", color: "#E8EDF5", padding: "10px 12px", background: "#111D2C", borderRadius: "6px", border: "0.5px solid #1E2D42" }}>{b.value}</div>
                          {b.max && (
                            <div style={{ fontSize: "10px", marginTop: "4px", color: b.value.length <= b.max ? "#00C48C" : "#F87171" }}>{b.value.length} / {b.max} characters</div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {outputTab === "checklist" && (() => {
                  const passCount = result.checklist.filter(c => c.pass).length;
                  return (
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: "700", marginBottom: "16px", color: passCount === result.checklist.length ? "#00C48C" : "#E8EDF5" }}>
                        SEO Score: {passCount} / {result.checklist.length}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                        {result.checklist.map((c, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px" }}>
                            <span style={{ color: c.pass ? "#00C48C" : "#F87171", fontWeight: "700" }}>{c.pass ? "✓" : "✗"}</span>
                            <span style={{ color: "#C8D6E5" }}>{c.item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: "24px", borderTop: "0.5px solid #1E2D42", textAlign: "center" }}>
        <p style={{ fontSize: "11px", color: "#2A4A65" }}>HumanKind · SEO Optimizer suggestions are a guide, not a guarantee of search ranking.</p>
      </div>
    </div>
  );
};

export default function Humanizer() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(!REQUIRE_AUTH);

  useEffect(() => {
    if (!REQUIRE_AUTH) return;
    fetch(`${OLLAMA_URL}/api/auth/me.php`, { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(data => setCurrentUser(data?.user || null))
      .finally(() => setAuthChecked(true));
  }, []);

  const logout = async () => {
    await fetch(`${OLLAMA_URL}/api/auth/logout.php`, { method: "POST", credentials: "include" });
    setCurrentUser(null);
  };

  if (!authChecked) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px" }}>Loading...</div>;
  }

  const path = window.location.pathname;

  // Checked BEFORE the auth gate below - the one genuinely public path in
  // this bundle. Everything it does (analyzeFree) is pure client-side JS,
  // so this never needs a session, a backend call, or the rate-limiting a
  // real server endpoint would need - see FreeAnalyzerPage/analyzeFree for
  // why that's a deliberate simplification, not an oversight.
  if (path === "/free") {
    return <FreeAnalyzerPage />;
  }

  // The public landing page and credits page are static HTML, served
  // directly by nginx before this bundle ever loads (see landing.html /
  // credits.html) - anything reaching this point needs a session.
  if (REQUIRE_AUTH && !currentUser) {
    return <LoginScreen onLogin={setCurrentUser} />;
  }

  if (path === "/admin") {
    return <AdminPage currentUser={currentUser} onLogout={logout} />;
  }
  if (path === "/help") {
    return <HelpPage />;
  }
  if (path === "/seo") {
    return <SeoOptimizerPage currentUser={currentUser} onLogout={logout} />;
  }
  return <MainApp currentUser={currentUser} onLogout={logout} />;
}
